import * as React from "react";
import {
    makeStyles,
    mergeClasses,
    tokens,
    Button,
    Checkbox,
    Spinner,
    Menu,
    MenuTrigger,
    MenuPopover,
    MenuList,
    MenuItem,
    MenuDivider,
    MenuGroup,
    MenuGroupHeader,
    Tooltip,
} from "@fluentui/react-components";
import {
    Archive20Regular,
    TaskListSquareLtr20Regular,
    ChevronDown20Regular,
    Add20Regular,
    ArrowReply20Regular,
    ArrowReplyAll20Regular,
    ArrowForward20Regular,
    Checkmark20Regular,
    Dismiss20Regular,
    Mail20Regular,
    Document20Regular,
    CalendarLtr20Regular,
    ImageOff20Regular,
    Attach20Regular,
    Filter20Regular,
    MoreHorizontal20Regular,
    MailUnread20Regular,
    Delete20Regular,
    PersonAdd20Regular,
    Save20Regular,
    ArrowLeft20Regular,
    Print20Regular,
    ArrowDownload20Regular,
    FolderOpen20Regular,
    ArrowMaximize20Regular,
} from "@fluentui/react-icons";
import { useNavigate } from "react-router";
import { speichereMail } from "./mailHerunterladen";
import { SandboxedMailBody, type MailBild, type MailMarkierung } from "./SandboxedMailBody";
import { MarkierungsMenue } from "./MarkierungsMenue";
import { BildMenue } from "./BildMenue";
import {
    bildDateiname,
    bilderFuerVorgang,
    dataUriTyp,
    dataUriZuBlob,
    eingebetteteBilder,
    findeBildZurCid,
} from "./inlineBilder";
import { bilderAlsFlowAnhaenge, frageBilderAuswahl, uebernimmBilderInZiele } from "./mailBilderUebernahme";
import { speichereBlobUnter } from "./speichernUnter";
import { angebotFuer } from "./markierungsAngebot";
import { bilderLadenText, setzeStand, summeLadender, type BilderStand } from "./mailBilderLaden";
import { SenderAvatar, useInternalUserId } from "./SenderAvatar";
import { PresenceDot } from "../chat/PresenceDot";
import { usePresence } from "../chat/usePresence";
import {
    formatHeaderDate,
    formatRecipientTitle,
    normalizeAddress,
    recipientEntries,
    splitRecipients,
} from "./mailHeader";
import { Email, graphMailService, dedupeAttachments, isGraph404, isMailUnavailable, type MailAttachment, type MailMissingContactData, type MailRecognitionResponse, type MailSenderCandidate } from "../../services/graphMailService";
import { attachmentKey, toImportItems } from "../../services/mailAttachments";
import { isRemoteAllowed, allowRemoteSender } from "../../utils/mailRemotePrefs";
import { OpenWindowConfig, useWindowManager } from "../windowManager/WindowManagerContext";
// H5 — „Regel erstellen…" aus dem Lesefenster (Cluster REGELN).
import { openMailRegelWindow } from "./MailRegelWindow";
import { canPreviewInline } from "../documents/documentKind";
import { useFileViewer } from "../documents/useDocumentViewer";
import { openSchadenAblage, type SchadenAblageModus } from "./SchadenAblageFenster";
import { schadenOrdnerLink } from "./schadenOrdner";
import { openMailVorgangPicker, type MailVorgangZiel } from "./MailVorgangPickerWindow";
import { assignMailToZiele } from "./mailVorgangAssign";
import { openComposeWindow } from "./ComposeMailWindow";
import { TerminEinladungLeiste } from "./TerminEinladungLeiste";
import { loescheMailZeile } from "./mailRowActions";
import { openEntwurfWindow } from "./EntwurfFenster";
import { abonniereEntwurfAenderung } from "./entwurfBus";
import { baueAllenAntwortenEmpfaenger, baueAntwortZitat } from "./antwortVorlage";
import { openVorgangCreateFlow, type VorgangCreateResult } from "../vorgang/create/VorgangCreateFlowWindow";
import { openMailContactAssign } from "./MailContactAssignWindow";
import { bieteZuordnungAn, type AntwortZuordnungAngebot } from "./antwortZuordnung";
import { dekodiereVcf, leseVisitenkarten, type Visitenkarte } from "./vcard";
import { signaturHinweis, vorgangStartAus } from "./mailContactAssign";
import type { VorgangFuerVorgabe } from "../vorgang/create/vorgangFuer";
import { schliesseZuweisungAb } from "./mailZuweisungAbschluss";
import { openMailNummerZuordnen } from "./MailNummerZuordnenWindow";
import {
    ladeSpaeter,
    merkeSpaeter,
    offeneEintraege,
    type FehlenderEintrag,
} from "./mailNummerZuordnen";
import { mailVorgangPrefill } from "./mailVorgangPrefill";
import {
    mailLinkService,
    type CreateMailLinkInput,
    type MailLinkBatchItemInput,
    type MailLinkWithVorgangDto,
} from "../../services/mailLinkService";
import {
    buildConfirmLinkInputs,
    buildConfirmTargets,
    buildStateBItems,
    candidateKindLabel,
    chipKontaktId,
    deriveMailCommandState,
    effectiveSenderCandidates,
    effektiveZuweisung,
    istBetreffTreffer,
    pickDisplayContact,
    resolveCandidateAssignment,
    showAssignContactButton,
    showNeutralVorgangButton,
    showUnrecognizedWarning,
    showZuweisenMenu,
    kandidatHerkunft,
    teileStateB,
    type KandidatenChip,
} from "./mailCommandState";
import { rollupRecognition } from "./mailRecognitionRollup";
import { bodiesNachzuladen, neuesteZuerst } from "./convoBodyNachladen";
import { MailKopfAktionen } from "./mobil/MailKopfAktionen";
import BottomSheet from "../calendar/BottomSheet";
import { TerminKarte } from "./mobil/TerminKarte";
import { empfaengerKurz, kurzeZeit } from "./mobil/mobilAnzeige";
import "./mobil/mailMobil.css";

/** Höchstens so viele Nachrichtentexte einer Unterhaltung gleichzeitig laden. */
const MAX_BODY_LADUNGEN = 3;
import { bindeVerlaufAnZiel } from "./mailVerlaufBinden";
import { meldeZuordnenHinweis } from "./mailZuordnenHinweis";
import { zeigeGlobalenHinweis } from "../calendar/globalerHinweis";
import { posteingangsPruefung } from "./mailVerlaufItems";
import { MailContactSidebar } from "./MailContactSidebar";
import { MailAnhangLeiste } from "./MailAnhangLeiste";
import { MailZiehGriff } from "./MailZiehGriff";
import { MailDetailWindowShell } from "./MailDetailWindowShell";
import type { SidebarTabKey } from "./sidebar/sidebarTabs";
import {
    findeMailFenster,
    navKey,
    navPop,
    navPush,
    navTop,
    registriereMailFenster,
    type MailNavEntry,
} from "./mailDetailNav";
import { vorgangAus, vorgangsKundeAus, weitereKontaktZuweisungen, zugewiesenerKontakt, type KontaktRef } from "./mailAssignedContact";
import { abonniereZuweisung, betrifft, publiziereZuweisung } from "./mailAssignmentBus";
// P7 (PO 14.09.2026): „gehört zum Vorgang" im Kopf — dieselben Helfer wie die Liste.
import { vorgaengeAusLinks } from "./mailListMarkers";
import { MailVorgangsChips } from "./MailVorgangsMarker";
import { useMailContext } from "../../context/MailContext";
import { canReadMailbox, canSendMailbox } from "../../utils/mailboxAccess";
import { reportMailError } from "../../services/mailError";
import { hinweisen } from "../redesign/bestaetigen";
import { baueDruckDokument, druckeHtml, type DruckNachricht } from "./mailDruck";

// Create-Rework CR-SP5: „Zuweisen → Entwurf" (Sofort-Todo art='mail') ist
// entfallen — Kontakt/Vertrag/Risiko führen jetzt in den zweistufigen
// Erstell-Flow (Zuweisung = Schritt 1). Die frühere Entitätssuche +
// DraftEntityType wurden dafür entfernt.

/**
 * Fenstertitel. Nutzt seit Cluster ERKENNUNG (A2) den `recognizedContact` —
 * er ist extern-only, ein interner Kollege steht dort nie. Die Erkennungs-
 * antwort steht hier bewusst NICHT zur Verfügung: Der Titel entsteht beim
 * Öffnen des Fensters, lange bevor `GET /mail/recognize` geantwortet hat.
 */
export const customerName = (email: Email): string =>
    email.recognizedContact?.displayName || email.senderName || "Unbekannt";

/** Cluster ERKENNUNG (A6) — Beschriftung je Art des gefundenen Datums. */
const MISSING_LABEL: Record<"email" | "phone" | "mobile" | "fax", string> = {
    email: "Neue E-Mail-Adresse gefunden",
    phone: "Neue Telefonnummer gefunden",
    mobile: "Neue Mobilnummer gefunden",
    fax: "Neue Faxnummer gefunden",
};

const formatDate = (d: Date): string => {
    if (!(d instanceof Date) || isNaN(d.getTime())) return "";
    const dd = d.getDate().toString().padStart(2, "0");
    const mm = (d.getMonth() + 1).toString().padStart(2, "0");
    const y = d.getFullYear();
    const h = d.getHours().toString().padStart(2, "0");
    const min = d.getMinutes().toString().padStart(2, "0");
    return `${dd}.${mm}.${y}, ${h}:${min}`;
};

/**
 * Outlook-style quoted history for a reply, embedded into the compose body.
 *
 * KONSISTENZ P0-C: Das Zitat zieht den UNSANITISIERTEN Original-Mail-HTML
 * (`email.thread[].body` bzw. `email.body`) in den `contentEditable`-Editor —
 * der Editor-Inhalt geht beim Senden wieder als `innerHTML` raus, also ist ein
 * roher Quote ein Persistenz-/Re-Send-XSS-Vektor und schleppt Remote-Tracking-
 * Bilder mit. Deshalb wird der zusammengesetzte HTML-String durch DIESELBEN
 * geteilten Helfer gezogen wie der Lese-/Sende-Pfad — KEIN eigener Sanitizer:
 *   1. `blockRemoteContent(…, { block: true })` (SP4) → Remote-`src`/`srcset`/
 *      `background`/CSS-`url()` neutralisieren (kein Tracking-Beacon im Quote).
 *   2. `sanitizeComposeHtml(…)` (SP6) → `<script>`/`on*`/`<iframe>` etc. entfernen,
 *      `cid:`/`data:`-Bilder durchlassen.
 */
const buildReplyQuote = (email: Email): string =>
    baueAntwortZitat({
        datum: email.date,
        absenderName: email.senderName,
        absenderAdresse: email.senderEmail,
        html:
            email.thread && email.thread.length
                ? email.thread.map((m) => m.body).filter(Boolean).join("<hr/>")
                : email.body || "",
    });

/**
 * Reiner, testbarer Empfänger-Helfer für „Allen antworten" (Outlook-Standard):
 *   - `to` = Original-Absender (leer-gefiltert).
 *   - `cc` = Original-To + Original-Cc, case-insensitive dedupliziert, OHNE die
 *     eigene Mailbox-Adresse und OHNE den (in `to` stehenden) Absender.
 *   - Original-BCC ist strukturell nicht lesbar (`Email` trägt by-design kein
 *     `bcc`) → kann nie übernommen werden.
 *
 * Defensiv `?? []`: aus localStorage rehydrierte Alt-Objekte können `to`/`cc`
 * (noch) nicht tragen → Reply-All degradiert sauber zu „nur Absender in To".
 */
const buildReplyAllRecipients = (
    email: Email,
    ownMailbox: string,
): { to: string[]; cc: string[] } =>
    baueAllenAntwortenEmpfaenger({ von: email.senderEmail, an: email.to, cc: email.cc }, [ownMailbox]);

/**
 * Opens the mail detail as a window. Window chrome (minimize/maximize/close)
 * comes from FloatingWindow — closing via X simply closes the window and does
 * NOT mark the mail read. The mail is marked read only via an explicit action
 * (Archivieren / Zuweisen / Vorgang erstellen).
 */
export const openMailDetailWindow = (
    openWindow: (config: OpenWindowConfig) => void,
    params: {
        email: Email;
        mailbox: string;
        onArchived?: (id: string) => void;
        /** Phone: das Mail-Detail-Fenster gleich maximiert öffnen (Vollbild). */
        initialMaximized?: boolean;
        /**
         * A4: Als einzelne Nachricht öffnen — kein Konversations-Fan-out (z. B.
         * aus der Vorgang-Ansicht, wo genau die zugewiesene Mail gezeigt wird).
         */
        singleMessage?: boolean;
    },
): string => {
    const { email, mailbox, onArchived, initialMaximized, singleMessage } = params;
    // J1: Zeigt ein Fenster diese Mail bereits (womöglich unter der Id einer
    // ANDEREN Mail, weil der Nutzer darin weiternavigiert hat), wird genau
    // dieses Fenster angesprochen. `openWindow` auf eine vorhandene Id
    // fokussiert das Fenster und lässt seinen Inhalt unangetastet — es
    // entsteht also kein zweites Fenster mit identischem Inhalt.
    const id = findeMailFenster(navKey({ mailbox, email })) ?? `mail-detail-${email.id}`;
    openWindow({
        id,
        title: `${customerName(email)} — ${email.subject || "Kein Betreff"}`,
        icon: <Mail20Regular />,
        // Die rechte Leiste kostet 36 px Rail — das Fenster startet entsprechend
        // etwas größer. Auf Telefon/Tablet läuft es ohnehin im Vollbild.
        initialSize: { width: 960, height: 660 },
        initialMaximized,
        render: () => (
            <MailDetailWindowShell
                windowId={id}
                initial={{ email, mailbox }}
                onArchived={onArchived}
                singleMessage={singleMessage}
            />
        ),
    });
    return id;
};

const useStyles = makeStyles({
    /**
     * J1 — `root` ist jetzt eine Flex-ZEILE: links der Mailinhalt
     * (`contentCol`), rechts die Leiste. `position: relative` bleibt, weil das
     * Overlay der Leiste daran ankert.
     */
    root: {
        position: "relative",
        height: "100%",
        display: "flex",
        flexDirection: "row",
        backgroundColor: "#ffffff",
        fontFamily: "'Barlow', 'Segoe UI', sans-serif",
    },
    /** Der gesamte bisherige Inhalt — unverändert, nur eine Ebene tiefer. */
    contentCol: {
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        minWidth: 0,
        minHeight: 0,
    },
    toolbar: {
        display: "flex",
        alignItems: "center",
        gap: "6px",
        flexWrap: "wrap",
        padding: "10px 16px",
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        backgroundColor: "#faf6fb",
        flexShrink: 0,
    },
    /* ── Mail→Vorgang SP2: Command-Leisten-States ─────────────────────────
       State A („Gehört zu diesem Vorgang"), State B (Erkannt-Buttons +
       Typ-Badge + vertikaler Trenner), State C (orange Warnung). */
    toolbarSpacer: { marginLeft: "auto" },
    /* B3 — VERTIKALER Trenner zwischen Erkannt-Buttons und „Neuer Vorgang"
       (explizit kein „/"-Textknoten). */
    vDivider: {
        alignSelf: "stretch",
        width: "1px",
        minHeight: "22px",
        backgroundColor: "#d9cfe0",
        marginLeft: "4px",
        marginRight: "4px",
        flexShrink: 0,
    },
    candidateLead: { fontSize: "12.5px", fontWeight: 600, color: "#6b5a66", flexShrink: 0 },
    /* Erkannt-Button (B2): Auswahl per Klick, ausgewählt = Plum gefüllt. */
    candidateBtn: {
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding: "4px 10px",
        borderRadius: "6px",
        border: "1px solid #d9cfe0",
        backgroundColor: "#ffffff",
        color: "#2d142b",
        fontFamily: "inherit",
        fontSize: "12.5px",
        fontWeight: 600,
        cursor: "pointer",
        maxWidth: "260px",
        ":hover": { backgroundColor: "#f6f0f7" },
    },
    weitererEntfernen: {
        border: "none",
        background: "transparent",
        color: "#6b5a66",
        cursor: "pointer",
        fontSize: "14px",
        lineHeight: 1,
        padding: "0 2px",
        ":hover": { color: "#b3261e" },
    },
    candidateBtnSelected: {
        backgroundColor: "#4d2051",
        borderTopColor: "#4d2051",
        borderRightColor: "#4d2051",
        borderBottomColor: "#4d2051",
        borderLeftColor: "#4d2051",
        color: "#ffffff",
        ":hover": { backgroundColor: "#3d1941" },
    },
    candidateName: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    /* Typ-Badge (Firma/AP/Person) — B2, AP ausdrücklich gekennzeichnet. */
    candidateBadge: {
        flexShrink: 0,
        fontSize: "10px",
        fontWeight: 700,
        letterSpacing: "0.02em",
        padding: "1px 6px",
        borderRadius: "8px",
        backgroundColor: "#f1e8f3",
        color: "#4d2051",
    },
    candidateBadgeSelected: { backgroundColor: "rgba(255,255,255,0.22)", color: "#ffffff" },
    /* State A: „Gehört zu diesem Vorgang" + Titel-Chip(s). */
    belongsLabel: { fontSize: "13px", fontWeight: 600, color: "#2d142b", flexShrink: 0 },
    belongsChip: {
        display: "inline-flex",
        alignItems: "center",
        maxWidth: "280px",
        padding: "2px 8px",
        borderRadius: "6px",
        backgroundColor: "#f1e8f3",
        color: "#4d2051",
        fontSize: "12.5px",
        fontWeight: 600,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    confirmError: { flexBasis: "100%", color: "#b91c1c", fontSize: "12px" },
    /* State C (B5): orange/gelbe Warnung — bewusst die WARN-Palette des
       Remote-Banners (nicht rot). */
    warnBar: {
        display: "flex",
        alignItems: "center",
        gap: "10px",
        flexWrap: "wrap",
        padding: "8px 16px",
        backgroundColor: "#fff7ed",
        borderBottom: "1px solid #fed7aa",
        color: "#9a3412",
        fontSize: "13px",
        flexShrink: 0,
    },
    /**
     * Cluster ERKENNUNG (A4) — Leiste „unbekannte Adresse an bekannter
     * Firmendomäne". Bewusst RUHIG (kvp-rd-Aubergine auf hellem Grund), nicht
     * orange: Das hier ist ein Angebot, keine Warnung.
     */
    domainBar: {
        display: "flex",
        alignItems: "center",
        gap: "10px",
        flexWrap: "wrap",
        padding: "8px 16px",
        backgroundColor: "#faf5fb",
        borderBottom: "1px solid #e6d5ea",
        color: "#4d2051",
        fontSize: "13px",
        flexShrink: 0,
    },
    domainCompany: {
        fontWeight: 700,
    },
    /** Cluster ERKENNUNG (A6) — „Neue Telefonnummer gefunden: … [Übernehmen]". */
    hintBar: {
        display: "flex",
        alignItems: "center",
        gap: "10px",
        flexWrap: "wrap",
        padding: "6px 16px",
        backgroundColor: tokens.colorNeutralBackground2,
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        color: tokens.colorNeutralForeground2,
        fontSize: "12.5px",
        flexShrink: 0,
    },
    hintValue: {
        fontWeight: 600,
        color: tokens.colorNeutralForeground1,
    },
    hintUnsure: {
        color: tokens.colorNeutralForeground3,
        fontStyle: "italic",
    },
    header: {
        padding: "20px 24px 12px 24px",
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        flexShrink: 0,
        // Der Kopf gehoert zum Lesebereich, nicht zur weissen Mailkarte. Blieb er
        // weiss, bestand der ganze „neue Grund" aus dem schmalen Rahmen um die
        // Karte — sichtbar waeren ~6 % der Flaeche gewesen. Zusammen mit
        // `bodyScroll` steht jetzt Toolbar / Kopf / Grund im selben warmen Ton,
        // und die weisse Karte darin ist das, was heraussticht.
        backgroundColor: "#fbf6fb",
    },
    subject: {
        fontSize: "1.4rem",
        fontWeight: 700,
        color: "#2d142b",
        margin: 0,
        marginBottom: "12px",
    },
    /* SP3 (B3) — Outlook-artiger Kopf: „Von" + „An" links, Tag/Datum/Uhrzeit
       rechtsbündig; auf schmalen Breiten bricht die Datumszeile darunter um. */
    senderRow: { display: "flex", alignItems: "flex-start", gap: "12px", flexWrap: "wrap" },
    senderInfo: {
        display: "flex",
        flexDirection: "column",
        gap: "2px",
        minWidth: 0,
        flexGrow: 1,
        flexBasis: "200px",
    },
    senderLine: {
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        columnGap: "6px",
        rowGap: "2px",
        minWidth: 0,
    },
    /* PO 22.08.2026 („Von/An/Datum ist noch zu blass"): Im Kopf tragen die
       WERTE, nicht deren Marken. „Von:"/„An:" bleiben absichtlich zurückgenommen
       — aber im warmen INK2 der kvp-rd-Palette statt im kalten Fluent-Grau, das
       neben dem Aubergine-Betreff ausgewaschen wirkte. */
    /* 600, nicht 500: Barlow wird nur mit 400/600/700 geladen (index.css), ein
       angefordertes 500 faellt auf 400 zurueck — das Label saehe aus wie vorher.
       Der Farbschritt allein (Fluent-Grau → INK2) liegt unter der
       Wahrnehmungsschwelle; das Gewicht macht die Marke ueberhaupt erst sichtbar. */
    senderLabel: { fontSize: "0.85rem", fontWeight: 600, color: "#6b5a66" },
    senderName: { fontWeight: 600, fontSize: "0.95rem", color: "#2d142b" },
    /* Absender-Klick → KMVS-Kontakt (B5): als Link gestylter Button, Plum-Akzent. */
    senderLink: {
        backgroundColor: "transparent",
        border: "none",
        padding: 0,
        fontFamily: "inherit",
        fontWeight: 600,
        fontSize: "0.95rem",
        color: "#4d2051",
        cursor: "pointer",
        ":hover": { textDecorationLine: "underline" },
    },
    /* Die Adresse IST die Auskunft, an der man den Absender festmacht (zwei
       „Müller" unterscheiden sich nur daran) — deshalb INK, nur ohne das
       Gewicht des Namens. */
    senderAddress: { fontSize: "0.85rem", color: "#241522" },
    /* PO Runde 3: Die An-Zeile ist eine eigene Reihe aus Marke, Wert und
       (bei vielen Empfängern) Schalter. Nur so bleibt der Schalter sichtbar,
       wenn der Wert daneben mit „…" abschneidet. */
    recipientRow: {
        display: "flex",
        alignItems: "baseline",
        columnGap: "6px",
        minWidth: 0,
        maxWidth: "100%",
    },
    /* Die Empfängerzeile trägt jetzt den Wert (INK); ihr Label „An:" steht als
       eigenes `senderLabel`-Element im JSX und bleibt INK2. Die 0.82rem lasen
       sich neben der Von-Zeile wie Kleingedrucktes — jetzt auf deren 0.85rem. */
    recipientLine: {
        fontSize: "0.85rem",
        color: "#241522",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        // Als Flex-Kind muss die Zeile schrumpfen DÜRFEN, sonst schiebt eine
        // lange Adresse den Schalter aus dem Kopf.
        minWidth: 0,
        flexShrink: 1,
    },
    /* Aufgeklappt darf die Liste umbrechen — auch mitten in einer sehr langen
       Adresse, die sonst über den Rand des Kopfes hinausliefe. */
    recipientLineOffen: {
        whiteSpace: "normal",
        overflow: "visible",
        wordBreak: "break-word",
    },
    /* Wie in der Von-Zeile: der NAME trägt, die Adresse steht zurückgenommen
       daneben (`senderAddress`). Beide auf 0.85rem, der Unterschied kommt aus
       dem Gewicht — ein zweites 0.95rem neben „Von:" hätte den Kopf mit zwei
       gleich lauten Zeilen besetzt. */
    recipientName: { fontWeight: 600, color: "#2d142b" },
    /* Der Schalter darf NICHT mitschrumpfen — sonst frisst eine lange Adresse
       genau die Beschriftung auf, die das Aufklappen erklärt. `alignSelf`
       überschreibt das `flex-start` aus `ccToggle`, damit er auf der Grundlinie
       der Empfängerzeile sitzt statt an deren Oberkante. */
    recipientToggle: { flexShrink: 0, alignSelf: "baseline" },
    /** „Cc: n" als Schalter — die Liste steht erst nach einem Klick da. */
    ccToggle: {
        alignSelf: "flex-start",
        border: "none",
        background: "transparent",
        padding: 0,
        font: "inherit",
        fontSize: "0.82rem",
        color: "#6b5a66",
        cursor: "pointer",
        textAlign: "left",
        ":hover": { textDecorationLine: "underline" },
    },
    /* Cc bleibt bewusst EINE Stufe unter „An": Es kostet ohnehin einen Klick
       und soll den Kopf nicht mit einer zweiten gleichrangigen Adressliste
       fluten — daher INK2 statt INK. */
    ccExpanded: {
        fontSize: "0.82rem",
        color: "#6b5a66",
        whiteSpace: "normal",
        wordBreak: "break-word",
    },
    /* Wann kam die Mail? — im Posteingang die meistgesuchte Angabe des Kopfes.
       Sie stand als hellgraues 0.82rem am rechten Rand und war praktisch nicht
       zu sehen; jetzt INK mit Gewicht, aber unter dem Betreff. */
    headerDate: {
        marginLeft: "auto",
        flexShrink: 0,
        paddingTop: "2px",
        fontSize: "0.85rem",
        fontWeight: 600,
        color: "#241522",
        whiteSpace: "nowrap",
    },
    /* Handy (kompakt): die ganze Spalte scrollt — Kopf UND Text. */
    contentColScroll: {
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
    },
    bodyScrollMit: {
        flexGrow: 0,
        flexShrink: 0,
        overflowY: "visible",
        // Handy: randlos wie Outlook iOS — durchgehend weiß, kein Kasten.
        backgroundColor: "#ffffff",
        padding: "0",
        "@media (max-width: 768px)": { padding: "0" },
    },
    /* Handy: der Mailtext ohne Karte (kein Rand/Schatten), nur Seitenabstand. */
    bodyCardRandlos: {
        border: "none",
        boxShadow: "none",
        borderRadius: "0",
        padding: "4px 16px 24px",
        "@media (max-width: 768px)": { padding: "4px 16px 24px" },
    },
    bodyScroll: {
        flexGrow: 1,
        overflowY: "auto",
        // PO 22.08.2026: Der Grund war ein kaltes Blaugrau (#f8fafc) und stach
        // gegen die warme kvp-rd-Palette ab; #fbf6fb ist der Ton der Leiste
        // über dem Kopf (#faf6fb) und vom PO so vorgegeben.
        backgroundColor: "#fbf6fb",
        // Der Rahmen um die Mailkarte liegt VOR deren eigener Polsterung und
        // zaehlt doppelt. Er trennt hier nur noch Karte von Panelkante, deshalb
        // schmal (PO 22.08.2026: "der mail inhalt noch breiter").
        padding: "12px 12px",
        // Handy: jeder Pixel Rand fehlt dem Mailtext.
        "@media (max-width: 768px)": { padding: "6px" },
    },
    bodyCard: {
        backgroundColor: "#ffffff",
        // Dieselbe LINE-Kante wie an convoCard/den Chat-Karten: die Mailkarte
        // liest sich damit als dasselbe Blatt-Motiv wie die Abschnitte, die im
        // Konversationsfall an ihrer Stelle stehen — nicht als anderer Kartentyp.
        border: "1px solid #ece7f0",
        borderRadius: "10px",
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
        padding: "20px 24px",
        // Vorher gedeckelt (zuletzt 1040px) — der Mailtext blieb auf JEDEM Monitor
        // bei dieser Breite stehen, der Rest der Fläche blieb Grund.
        // Geschäftsmails sind Tabellenlayouts von 600-900px und wurden gestaucht.
        // PO 22.08.2026 ausdrücklich: kein Deckel mehr, die Karte füllt den
        // Lesebereich. PREIS: Eine reine Fließtext-Mail läuft damit auf einem
        // 2560px-Monitor über die volle Breite durch — deutlich mehr als die
        // ~90 Zeichen, ab denen das Auge die nächste Zeile schlechter findet.
        // Bewusst in Kauf genommen; ein Zahlenwert (z. B. 1280px) statt "none"
        // dreht es jederzeit zurück, ohne dass sonst etwas angefasst werden muss.
        maxWidth: "none",
        marginLeft: "auto",
        marginRight: "auto",
        fontSize: "15px",
        lineHeight: 1.55,
        color: "#1f2937",
        "@media (max-width: 768px)": { padding: "12px 10px" },
    },
    /* T-369 — Anhang-Leiste oben in der Mailkarte, Trennlinie zum Text darunter. */
    anhangOben: {
        marginBottom: "14px",
        paddingBottom: "12px",
        borderBottom: "1px solid rgba(0,0,0,0.08)",
    },
    /* Outlook Desktop: die Nachrichten einer Unterhaltung stehen auf einem
       Blatt, getrennt durch eine Linie (vorher je eine Karte, Outlook Web). */
    convoTrenner: {
        borderTop: "1px solid #ece7f0",
    },
    convoHead: {
        display: "flex",
        alignItems: "center",
        gap: "10px",
        padding: "10px 4px",
        cursor: "pointer",
        ":hover": { backgroundColor: "#fbf9fc" },
    },
    convoFrom: { fontWeight: 600, fontSize: "0.9rem", color: "#2d142b" },
    /* Im Konversationsfall zeigt der Kopf oben nur die Wurzelnachricht — das
       Von/An/Datum, das man tatsaechlich liest, steht hier je Karte. Es traegt
       deshalb dieselbe Rangfolge wie der Kopf (Werte INK), sonst waere P1 genau
       dort nicht erfuellt, wo Konversationen gelesen werden. Die Vorschau
       (`convoPreview`) bleibt bewusst leise — sie ist der Rang darunter. */
    convoMeta: {
        fontSize: "0.78rem",
        color: "#241522",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    convoPreview: {
        fontSize: "0.82rem",
        color: tokens.colorNeutralForeground3,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    convoDate: {
        fontSize: "0.78rem",
        // Wie `headerDate`: Wann kam die Nachricht? ist im Thread die meist-
        // gesuchte Angabe und stand als hellgraues Kleingedrucktes am Rand.
        color: "#241522",
        fontWeight: 600,
        whiteSpace: "nowrap",
        marginLeft: "6px",
    },
    convoChevron: {
        display: "inline-flex",
        color: tokens.colorNeutralForeground3,
        transitionProperty: "transform",
        transitionDuration: "120ms",
    },
    convoChevronOpen: {
        display: "inline-flex",
        color: tokens.colorNeutralForeground3,
        transform: "rotate(180deg)",
        transitionProperty: "transform",
        transitionDuration: "120ms",
    },
    convoBody: {
        padding: "4px 4px 16px 46px",
        fontSize: "15px",
        lineHeight: 1.55,
        color: "#1f2937",
        // Handy: die Einrückung unter das Bild kostet dort ein Achtel der Breite.
        "@media (max-width: 768px)": { padding: "4px 0 16px 0" },
    },
    threadMsg: {
        padding: "16px",
        marginBottom: "12px",
        borderRadius: "8px",
        backgroundColor: "#f3f2f1",
        border: "1px solid #e1dfdd",
        color: "#000000",
    },
    threadFrom: { fontWeight: 600, fontSize: "0.9rem", marginBottom: "2px" },
    /* Fallback-Thread (`view.thread`, wenn die Konversation nicht geladen werden
       konnte): dasselbe Datum, derselbe PO-Punkt — und hier auf grauem Grund
       (#f3f2f1) noch blasser als sonst. */
    threadDate: { fontSize: "0.8rem", color: "#241522", fontWeight: 600, marginBottom: "8px" },
    threadBody: { whiteSpace: "normal", fontSize: "14px", lineHeight: 1.5 },
    bodyText: { whiteSpace: "normal" },
    // SP4: Sofort sichtbare Vorschau, solange der Voll-Body noch lädt (statt
    // leerem Fenster + erst nach 400 ms ein Spinner-Hinweis). Dezent gehalten,
    // wird durch den echten Body ersetzt, sobald er da ist.
    previewLoading: {
        whiteSpace: "pre-wrap",
        color: tokens.colorNeutralForeground3,
    },
    // SP4/A2: Banner über dem Mail-Body, wenn externe Inhalte blockiert wurden.
    remoteBanner: {
        display: "flex",
        alignItems: "center",
        gap: "10px",
        flexWrap: "wrap",
        padding: "8px 12px",
        marginBottom: "12px",
        borderRadius: "8px",
        backgroundColor: "#fff7ed",
        border: "1px solid #fed7aa",
        color: "#9a3412",
        fontSize: "13px",
    },
    remoteBannerText: { flex: "1 1 auto", minWidth: 0 },
    // Lade-/Fehler-Zustände beim Nachladen (Body/Konversation/Anhänge) — warme
    // Palette wie die Chat-Karten (BG_SOFT/LINE aus chatTheme).
    loadingHint: {
        display: "flex",
        alignItems: "center",
        gap: "10px",
        padding: "8px 12px",
        marginBottom: "12px",
        borderRadius: "8px",
        backgroundColor: "#fbf9fc",
        border: "1px solid #ece7f0",
        color: tokens.colorNeutralForeground3,
        fontSize: "13px",
    },
    // Fehlerbox — spiegelt das loadError-Banner der MailView (Danger-Tokens),
    // als abgerundete Karte im Body-Bereich.
    loadErrorBox: {
        display: "flex",
        alignItems: "center",
        gap: "10px",
        flexWrap: "wrap",
        padding: "8px 12px",
        marginBottom: "12px",
        borderRadius: "8px",
        backgroundColor: tokens.colorStatusDangerBackground1,
        border: `1px solid ${tokens.colorStatusDangerBorder1}`,
        color: tokens.colorStatusDangerForeground1,
        fontSize: "13px",
    },
    loadErrorText: { flex: "1 1 auto", minWidth: 0 },
    // Sitzt bewusst ÜBER dem Kopfbereich: Vertrag und Risiko sind die
    // genaueste Aussage der Erkennung und standen vorher ganz unten, unter
    // Betreff, Von, An und Datum. Da die Zeile nicht mehr im Header liegt,
    // bringt sie dessen seitlichen Innenabstand jetzt selbst mit.
    recognitionChipRow: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        flexWrap: "wrap",
        padding: "10px 24px 0 24px",
        flexShrink: 0,
    },
    recognitionChip: {
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding: "4px 10px",
        borderRadius: "6px",
        border: "1px solid #d9cfe0",
        backgroundColor: "#fbf9fc",
        color: "#4d2051",
        fontFamily: "inherit",
        fontSize: "12px",
        fontWeight: 600,
        cursor: "pointer",
        maxWidth: "260px",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        ":hover": { backgroundColor: "#f1e8f3" },
    },
    recognitionLabel: {
        fontSize: "12px",
        color: tokens.colorNeutralForeground3,
        marginRight: "4px",
    },
    /* P7 (PO 14.09.2026) — „Gehört zum Vorgang: …" zwischen Betreff und
       Von-Zeile. Bis dahin sagte das Lesefenster NICHT, ob die Mail schon an
       einem Vorgang hängt: State A („Gehört zu diesem Vorgang") gilt nur für
       Vorgänge, an die diese Nachricht noch NICHT gebunden ist. Der Chip trägt
       die Optik der Erkennungs-Chips (Plum auf hellem Grund) und öffnet den
       Vorgang. */
    vorgangRow: {
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "6px",
        marginTop: "-6px",
        marginBottom: "10px",
        fontSize: "12px",
        color: "#6b5a66",
    },
    vorgangChip: {
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        padding: "2px 9px",
        borderRadius: "8px",
        border: "1px solid #d9cfe0",
        backgroundColor: "#fbf9fc",
        color: "#4d2051",
        fontFamily: "inherit",
        fontSize: "12px",
        fontWeight: 600,
        cursor: "pointer",
        maxWidth: "320px",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        ":hover": { backgroundColor: "#f1e8f3" },
    },
});

type MailDetailContentProps = {
    /** Gesetzt im Fenster-Modus (FloatingWindow) — Schließen geht dann über den WindowManager. */
    windowId?: string;
    email: Email;
    mailbox: string;
    onArchived?: (id: string) => void;
    /**
     * SP1 (Lese-Panel): Im Panel-Modus (rechte Vorschau in der MailView) ersetzt
     * dieser Callback das Fenster-Schließen — z. B. nach Archivieren/Antworten
     * wird das Panel geleert statt ein Fenster geschlossen.
     */
    onCloseRequest?: () => void;
    /**
     * A4: Nur diese eine Nachricht anzeigen — die Konversation NICHT nachladen
     * bzw. als Abschnitte auffächern. Genutzt aus der Vorgang-Ansicht, wo genau
     * die zugewiesene Mail (messageId + mailbox) gezeigt werden soll.
     */
    singleMessage?: boolean;
    /**
     * Cluster ERKENNUNG (A2) — Übergabepunkt an die rechte Kontakt-Leiste.
     *
     * Sie soll ihren Kontakt aus DERSELBEN Quelle bekommen wie der Mailkopf,
     * statt eine zweite Erkennung zu fahren (und dabei einen internen Kollegen
     * anzuzeigen). Dasselbe gilt für die Adressmenge: `addresses` enthält nur
     * externe Adressen — vorher stand dort ungefiltert `senderEmail`.
     */
    onRecognitionDisplayChange?: (v: {
        contact: { id: number; displayName: string } | null;
        addresses: string[];
    }) => void;
    /**
     * J1 — die rechte Leiste ausblenden. Escape-Luke für eingebettete
     * Vorschauen; kein heutiger Aufrufer setzt sie auf `false`.
     */
    sidebar?: boolean;
    /**
     * Handy-Vollansicht: die Leiste ohne Klickleiste am Rand (volle Breite für
     * die Mail). Die Reiter öffnet der Aufrufer über `aktiverReiter`.
     */
    leisteVerborgen?: boolean;
    /**
     * Handy-Vollansicht: kompakter, klar gegliederter Kopf (Outlook iOS) —
     * eine Aktionsreihe gleich großer Knöpfe, Absender in einer Zeile mit
     * Details per Tipp, Hinweise als schmale Zeile. Kopf und Text scrollen
     * gemeinsam; nur die Aktionsreihe bleibt oben stehen.
     */
    kompakt?: boolean;
    /** Handy: „Vollbild" — die Mail als eigenes Fenster öffnen. */
    onVollbild?: () => void;
    /**
     * J1 — Klick auf eine Mail in der Leiste. Fenster-Modus: In-Fenster-
     * Navigation (vom Fensterrahmen gesetzt). Panel-Modus: die MailView
     * entscheidet. Ohne Callback ist die Trefferliste nicht klickbar.
     */
    onOpenMailFromSidebar?: (mailbox: string, email: Email) => void;
    /**
     * J1 — „Zurück" der In-Fenster-Navigation. Gesetzt ⇒ die Kopfzeile der Mail
     * zeigt den Pfeil, und Schließen (X/ESC) geht erst eine Ebene zurück.
     */
    onNavBack?: () => void;
    /**
     * J1 — der geöffnete Reiter lebt im Fensterrahmen, damit der Remount des
     * Mailinhalts ihn nicht schließt. `undefined` ⇒ die Leiste führt ihn selbst.
     */
    aktiverReiter?: SidebarTabKey | null;
    onReiterWechsel?: (t: SidebarTabKey | null) => void;
};

/**
 * Wiederverwendbarer Lese-Inhalt (SP1): identisch im Detail-FENSTER und im
 * rechten Lese-PANEL der MailView. Der Unterschied ist nur, wie „schließen"
 * umgesetzt wird (WindowManager vs. onCloseRequest).
 */
export const MailDetailContent: React.FC<MailDetailContentProps> = ({
    windowId,
    email,
    mailbox,
    onArchived,
    onCloseRequest,
    singleMessage = false,
    onRecognitionDisplayChange,
    sidebar = true,
    leisteVerborgen = false,
    kompakt = false,
    onVollbild,
    onOpenMailFromSidebar,
    onNavBack,
    aktiverReiter,
    onReiterWechsel,
}) => {
    const styles = useStyles();
    const { closeWindow, openWindow, minimizeWindow } = useWindowManager();
    const openFile = useFileViewer();

    // Schließen abstrahiert: Panel-Modus → Callback, Fenster-Modus → WindowManager.
    // J1: Steht die Mail auf einer tieferen Ebene der In-Fenster-Navigation,
    // geht X/ESC erst EINE Ebene zurück — erst unten schließt es das Fenster.
    const requestClose = React.useCallback(() => {
        if (onNavBack) onNavBack();
        else if (onCloseRequest) onCloseRequest();
        else if (windowId) closeWindow(windowId);
    }, [onNavBack, onCloseRequest, windowId, closeWindow]);

    /**
     * J1 — die Leiste sucht den Schriftwechsel über ALLE lesbaren Postfächer.
     * Die Menge holt sich die Komponente selbst aus dem MailContext (der in
     * `App.tsx` auch die Fensterschicht umschließt), statt sie von sechs
     * Aufrufstellen durchreichen zu lassen.
     */
    const { mailboxes, folderTrees } = useMailContext();
    // ── T-212 (Teil B): Antwort-Entwurf zu dieser Mail? ─────────────────────
    // Nur mit Senderecht (ohne ließe er sich nicht fortsetzen). Der Index ist
    // kurz gecacht und wird mit dem Posteingang geteilt.
    const [antwortEntwurf, setAntwortEntwurf] = React.useState<{ draftId: string } | null>(null);
    const [entwurfTick, setEntwurfTick] = React.useState(0);
    const darfEntwurf = mailboxes.some(
        (m) => m.mailboxAddress.toLowerCase() === (mailbox ?? "").toLowerCase() && canSendMailbox(m),
    );
    React.useEffect(
        () =>
            abonniereEntwurfAenderung((mb) => {
                if (mb.toLowerCase() === (mailbox ?? "").toLowerCase()) setEntwurfTick((n) => n + 1);
            }),
        [mailbox],
    );
    React.useEffect(() => {
        const conv = email.conversationId;
        if (!darfEntwurf || !conv || !mailbox) {
            setAntwortEntwurf(null);
            return;
        }
        let aktiv = true;
        graphMailService
            .fetchDraftIndex(mailbox)
            .then((index) => {
                if (aktiv) setAntwortEntwurf(index[conv] ? { draftId: index[conv].draftId } : null);
            })
            .catch(() => {
                if (aktiv) setAntwortEntwurf(null);
            });
        return () => {
            aktiv = false;
        };
    }, [mailbox, email.conversationId, darfEntwurf, entwurfTick]);
    const readableMailboxes = React.useMemo(
        () => mailboxes.filter(canReadMailbox).map((m) => m.mailboxAddress),
        [mailboxes],
    );
    /**
     * Archivieren verschiebt die Mail im Postfach und verlangt deshalb ein
     * eigenes Recht darauf (`assertMessageWritable`). Wer eine Mail nur über
     * den Vorgang liest — ein Kollege hat sie aus seinem Postfach zugewiesen —,
     * bekommt den Knopf nicht: er scheiterte dort immer (Rücksprache
     * 06.10.2026, Prüfung der Outlook-Funktionen). Solange die Postfachliste
     * noch lädt, bleibt er stehen; der Fehlerhinweis fängt den Rest.
     */
    const darfArchivieren =
        mailboxes.length === 0 ||
        readableMailboxes.some((m) => m.toLowerCase() === (mailbox ?? "").toLowerCase());

    const [full, setFull] = React.useState<Email | null>(null);
    // Cluster VORGANG (D3): das frühere Inline-Overlay „Mail zuweisen — To-Do
    // wählen" ist entfallen. Es lud ALLE Todos des Mandanten und filterte im
    // Browser; To-dos sind jetzt IM Vorgangs-Picker mitdurchsuchbar und dort
    // als Unterzeile ihres Vorgangs anhakbar.
    // Cluster ERKENNUNG — die Erkennung ist ein ZUSTANDSTRIPLE, kein Wert.
    //
    //   loading — die Antwort ist unterwegs: die Leiste zeigt die neutrale
    //             Ansicht, insbesondere KEINE Warnung (kein Flackern).
    //   error   — 4xx/5xx/Timeout: die Leiste fällt SICHTBAR auf das bisherige
    //             Verhalten zurück (Listen-Annotation + Warnung wie bisher).
    //             Ein geschluckter Fehler hätte sie stumm geschaltet.
    //   ok      — der Server-Zustand gilt.
    type RecognitionState =
        | { status: "loading" }
        | { status: "error" }
        | { status: "ok"; data: MailRecognitionResponse };
    const [recognition, setRecognition] = React.useState<RecognitionState>({ status: "loading" });
    const recognitionSettled = recognition.status !== "loading";
    const recognized = recognition.status === "ok" ? recognition.data : null;

    React.useEffect(() => {
        setRecognition({ status: "loading" });
        let cancelled = false;
        graphMailService.recognizeMail(mailbox, email.id)
            .then((res) => {
                if (cancelled) return;
                setRecognition(res.status === "ok" ? { status: "ok", data: res.data } : { status: "error" });
            })
            .catch((err) => {
                // `recognizeMail` wirft nicht mehr — dieser Zweig ist die
                // Rückfallebene, falls doch (z. B. Netzabbruch im Transport).
                reportMailError({
                    area: "mail",
                    action: "recognize",
                    error: err,
                    fallbackMessage: "Die Absender-Erkennung ist nicht verfügbar.",
                });
                if (!cancelled) setRecognition({ status: "error" });
            });
        return () => { cancelled = true; };
    }, [mailbox, email.id]);

    /** Der Server-Zustand, aber NUR wenn er wirklich vorliegt (sonst Legacy). */
    const serverRecognition =
        recognition.status === "ok" ? recognition.data.contactRecognition ?? null : null;

    // ── Mail→Vorgang SP2: Command-Leisten-States (A/B/C) ────────────────────
    // Links der Konversation (inkl. aufgelöstem Vorgang) für die State-A-
    // Erkennung „Gehört zu diesem Vorgang". null = lädt noch → die Leiste
    // zeigt solange die neutrale Standard-Ansicht (kein C-Warn-Flackern).
    const [convLinks, setConvLinks] = React.useState<MailLinkWithVorgangDto[] | null>(null);
    // Initialabruf, manuelles Refresh und Bus-Reload dürfen sich überlappen.
    // Nur die jüngste Generation darf `convLinks` verändern.
    const convLinksGenerationRef = React.useRef(0);
    // [X] am Vorschlag: State A verwerfen → Fallback B/C (nur für diese Ansicht).
    const [suggestionDismissed, setSuggestionDismissed] = React.useState(false);
    // State A, mehrere Vorgänge: angehakte Ziele (null = noch nicht angefasst
    // → ALLE vorausgewählt; „einem, mehreren oder allen" zuordenbar).
    const [checkedVorgangIds, setCheckedVorgangIds] = React.useState<number[] | null>(null);
    const [confirming, setConfirming] = React.useState(false);
    const [confirmError, setConfirmError] = React.useState<string | null>(null);
    // State B: per Klick gewählter Erkennungs-Kandidat (null = Default Firma).
    const [selectedCandidateId, setSelectedCandidateId] = React.useState<number | null>(null);
    /**
     * Cluster ZUWEISEN (B5) — die EXPLIZITE Zuweisung aus dem Zuweisen-Popup.
     * Sie schlägt die Kandidaten-Vorauswahl; ausgewertet wird beides über
     * `effektiveZuweisung`.
     *
     * Ehrlich zur Wirkung (Annahme A9): Nach einem ERFOLGREICHEN „Zuweisen"
     * archiviert `finishRead()` und schließt das Fenster — dieser State lebt
     * dann nicht mehr. Er wirkt genau dort, wo das Fenster offen BLEIBT:
     *   (a) Ausgang „Vorgang" — das Mail-Fenster lebt weiter, während der
     *       Erstell-Flow läuft;
     *   (b) Verknüpfen oder Archivieren scheitert (kein Postfachrecht);
     *   (c) die Leiste „fehlende Kontaktdaten" und die rechte Leiste lesen die
     *       Zuweisung im selben Fensterleben.
     */
    /**
     * J2 — drei Werte statt zwei: `undefined` = in dieser Ansicht wurde nichts
     * entschieden (dann zählen Links, Vorgangskunde, Erkennung), `null` =
     * bewusst „ohne Kontakt" (bindend), sonst der gewählte Kontakt.
     */
    const [expliziteZuweisung, setExpliziteZuweisung] = React.useState<KontaktRef | null | undefined>(
        undefined,
    );
    /** Die bisherige Sicht auf denselben Zustand (Cluster ZUWEISEN, B5). */
    const zuweisung = expliziteZuweisung ?? null;
    /** J2 — nach einer Zuweisung die Konversations-Links frisch holen. */
    const [convLinksTick, setConvLinksTick] = React.useState(0);
    /**
     * C9 — in DIESER Browser-Sitzung zurückgestellte Werte („Später"). Bewusst
     * `sessionStorage` und kein Backend-Feld (Annahme A4): ein „nie wieder" ohne
     * Verwaltungsoberfläche wäre schlechter als gar keins.
     */
    const [nummerSpaeter, setNummerSpaeter] = React.useState<string[]>([]);

    React.useEffect(() => {
        let cancelled = false;
        const generation = ++convLinksGenerationRef.current;
        setConvLinks(null);
        setSuggestionDismissed(false);
        setCheckedVorgangIds(null);
        setConfirmError(null);
        if (!email.conversationId) {
            setConvLinks([]);
            return;
        }
        mailLinkService
            .listByConversation(email.conversationId, mailbox)
            .then((links) => {
                if (!cancelled && convLinksGenerationRef.current === generation) {
                    setConvLinks(links);
                }
            })
            .catch((err) => {
                // Lookup-Fehler degradiert still auf B/C — die Leiste bleibt nutzbar.
                reportMailError({
                    area: "mail",
                    action: "load-linked-mails",
                    error: err,
                    fallbackMessage: "Verknüpfungen der Konversation konnten nicht geladen werden.",
                });
                if (!cancelled && convLinksGenerationRef.current === generation) {
                    setConvLinks([]);
                }
            });
        return () => { cancelled = true; };
    }, [mailbox, email.conversationId, email.id, convLinksTick]);

    // Nachrichten der Konversation. MUSS vor `senderCandidates` stehen: die
    // Memo unten liest `convo`, und eine spätere Deklaration ergäbe zur
    // Laufzeit einen ReferenceError („Cannot access before initialization").
    // TypeScript fängt das NICHT — der Zugriff liegt in einer Closure, deren
    // Ausführungszeitpunkt der Compiler nicht kennt.
    const [convo, setConvo] = React.useState<Email[] | null>(null);

    // Cluster ERKENNUNG: Die Kandidaten kommen vom SERVER — er sucht sie über
    // die ganze Konversation (Von, An, Cc aller Nachrichten, interne Adressen
    // nie). Der frühere Frontend-Fallback über den geladenen Verlauf
    // (`candidatesFromThread`) ist damit entfallen; zwei konkurrierende
    // Definitionen von „welcher Kontakt gehört zum Thread" wären genau die Art
    // Doppel-Wahrheit, die später auseinanderläuft.
    //
    // Ohne Server-Antwort (lädt noch, gescheitert, altes Backend) gilt die
    // Listen-Annotation — zweites Argument ist der `recognizedContact`, NICHT
    // der `matchedContact`: sonst käme über alt-gecachte Listenobjekte ohne
    // `senderCandidates` weiterhin ein interner Kollege als Chip durch.
    const senderCandidates = React.useMemo(
        () =>
            serverRecognition
                ? serverRecognition.candidates
                : effectiveSenderCandidates(
                      email.senderCandidates ?? full?.senderCandidates,
                      email.recognizedContact ?? full?.recognizedContact,
                  ),
        [serverRecognition, email.senderCandidates, email.recognizedContact, full],
    );

    /**
     * Cluster ERKENNUNG (A2) — die EINZIGE Anzeigequelle für „der Kontakt
     * dieser Mail". Alle Anzeige- und Vorbelegungsstellen hängen daran; auf
     * `matchedContact` wird nirgends mehr zurückgefallen, sonst käme der
     * interne Kollege durch die Hintertür zurück.
     */
    const displayContact = React.useMemo(
        () => pickDisplayContact(recognition, email, full),
        [recognition, email, full],
    );

    /**
     * Die Adressen, auf die sich die Erkennung stützt — nie eine interne.
     * Ohne Server-Antwort bleibt der heutige Wert (die Absenderadresse), damit
     * die rechte Leiste ohne Erkennung nicht schlechter ist als vorher.
     */
    const displayAddresses = React.useMemo(
        () =>
            serverRecognition?.displayAddresses?.length
                ? serverRecognition.displayAddresses
                : email.senderEmail
                  ? [email.senderEmail]
                  : [],
        [serverRecognition, email.senderEmail],
    );

    React.useEffect(() => {
        onRecognitionDisplayChange?.({ contact: displayContact, addresses: displayAddresses });
    }, [onRecognitionDisplayChange, displayContact, displayAddresses]);

    // „Vertrag → Risiko → Kontakt": Ist ein Vertrag erkannt, trägt er die
    // beiden anderen Ebenen implizit — dann steht nur er da. Gehört er
    // allerdings zu einem ANDEREN Kontakt als dem Absender, bleibt alles
    // stehen; sonst verschwände genau die Information, dass hier zwei Parteien
    // im Spiel sind.
    const rollup = React.useMemo(
        () =>
            rollupRecognition(
                recognized?.contracts ?? [],
                recognized?.risks ?? [],
                senderCandidates,
            ),
        [recognized, senderCandidates],
    );

    const commandState = React.useMemo(
        () =>
            deriveMailCommandState({
                messageId: email.id,
                internetMessageId: email.internetMessageId ?? full?.internetMessageId ?? null,
                links: convLinks ?? [],
                candidates: senderCandidates,
                dismissed: suggestionDismissed,
                // Nur den ECHTEN Server-Zustand durchreichen. Fehlt er, greift
                // im Zustandsautomaten der Legacy-Pfad — heutiges Verhalten
                // inklusive Warnleiste bei leerer Kandidatenliste.
                recognition: serverRecognition,
                // T-265: Kunden aus dem Betreff, ohne Vorauswahl.
                subjectCandidates: serverRecognition?.subjectCandidates,
            }),
        [email.id, email.internetMessageId, full, convLinks, senderCandidates, suggestionDismissed, serverRecognition],
    );

    // ── Cluster ERKENNUNG (A6): fehlende Kontaktdaten ───────────────────────
    // Der Haupt-Endpoint rechnet den Abgleich gegen den SERVERSEITIG erkannten
    // Kontakt. Wählt der Nutzer einen anderen Chip (oder steht im Zustand D
    // eine eindeutige Firma zur Debatte), wird NICHT der Haupt-Endpoint erneut
    // gerufen — der fährt u. a. einen Full-Scan über die Immobilien-Merkmale
    // des Mandanten. Stattdessen der schlanke Zweit-Endpoint.
    // `undefined` = kein Override, es gilt die Antwort des Haupt-Endpoints.
    const [missingOverride, setMissingOverride] =
        React.useState<MailMissingContactData | null | undefined>(undefined);

    const missingTargetContactId =
        selectedCandidateId ??
        (commandState.kind === "D" && !commandState.ambiguous
            ? commandState.companies[0]?.contactId ?? null
            : null);

    React.useEffect(() => {
        const serverContactId = recognized?.missingContactData?.contactId ?? null;
        if (missingTargetContactId === null || missingTargetContactId === serverContactId) {
            setMissingOverride(undefined);
            return;
        }
        let cancelled = false;
        graphMailService
            .fetchMissingContactData(mailbox, email.id, missingTargetContactId)
            .then((res) => { if (!cancelled) setMissingOverride(res); });
        return () => { cancelled = true; };
    }, [mailbox, email.id, missingTargetContactId, recognized]);

    const missingContactData =
        missingOverride !== undefined ? missingOverride : recognized?.missingContactData ?? null;

    // C9: beim Wechsel der Nachricht die zurückgestellten Werte neu einlesen.
    React.useEffect(() => {
        setNummerSpaeter(ladeSpaeter(mailbox, email.id));
    }, [mailbox, email.id]);

    /** Die noch offenen Fundstellen — ohne die in dieser Sitzung vertagten. */
    const offeneKontaktdaten: FehlenderEintrag[] = React.useMemo(
        () => offeneEintraege((missingContactData?.entries ?? []) as FehlenderEintrag[], nummerSpaeter),
        [missingContactData, nummerSpaeter],
    );

    // ── SP4/A2: Externe-Inhalte-Steuerung ───────────────────────────────────
    // Wir starten IMMER im Block-Zustand (showRemote=false). Externe Bilder etc.
    // werden erst geladen, wenn entweder
    //   (a) der Nutzer „Anzeigen" klickt, ODER
    //   (b) der full-Load ein VERIFIZIERTES Intern-Flag liefert (Auto-Load).
    // Das Listen-Flag (isInternalSender, ohne DMARC) ist spoofbar und darf NICHT
    // auto-laden — Konsistenz-Plan P1-A.
    const [showRemote, setShowRemote] = React.useState(false);
    const [hasRemote, setHasRemote] = React.useState(false);

    // Sichtbarer Lade-/Fehler-Zustand für den Voll-Body (vorher nur
    // console.error — der User sah bei Fehlschlag dauerhaft die Preview,
    // ohne Hinweis). `retryTick` löst den Fetch über „Neu laden" erneut aus.
    const [bodyError, setBodyError] = React.useState(false);
    // A4: unterscheidet echten Graph-404 (Nachricht existiert nicht mehr) von
    // transienten Fehlern (Timeout/5xx) — steuert den Fehlertext.
    const [bodyError404, setBodyError404] = React.useState(false);
    // A1/A2: dauerhaft „nicht verfügbar" (403 kein Zugriff ODER 404 endgültig weg)
    // → graziöse Meldung statt „Neu laden"-Retry.
    const [bodyUnavailable, setBodyUnavailable] = React.useState(false);
    // Self-Heal-Rekey: Graph vergibt beim Move eine neue Id; das Backend heilt
    // beim Body-Laden den MailLink auf die neue Id um. Anhänge/Notizen müssen
    // danach dieselbe (geheilte) Id nutzen — sonst scheitert für Beteiligte
    // (ohne Postfach-Zugriff) die Autorisierung, weil der Link nicht mehr auf die
    // alte Id zeigt. `resolvedId` = die vom Body zurückgelieferte aktuelle Id.
    const [resolvedId, setResolvedId] = React.useState(email.id);
    // SP4-Nachtrag: Body-Fehler JE Konversations-Nachricht (vorher Endlos-
    // Spinner). `requestedBodyIds` verhindert doppelte Fetches beim mehrfachen
    // Auf-/Zuklappen (in-flight oder bereits geladen); bei Fehler wird die id
    // wieder freigegeben, damit „Neu laden" greift.
    const [failedBodyIds, setFailedBodyIds] = React.useState<Set<string>>(new Set());
    const requestedBodyIds = React.useRef<Set<string>>(new Set());
    const [retryTick, setRetryTick] = React.useState(0);
    // Lade-Hinweis erst nach ~400 ms zeigen — kein Flackern bei schnellen Loads.
    const [bodyLoadingSlow, setBodyLoadingSlow] = React.useState(false);

    // Load the full body (list items carry only a preview).
    React.useEffect(() => {
        let cancelled = false;
        setBodyError(false);
        setBodyError404(false);
        setBodyUnavailable(false);
        setResolvedId(email.id);
        // Die Haupt-Mail läuft über DIESEN Effect — für die Konversations-Karte
        // derselben Nachricht keinen zweiten Fetch anstoßen (s. loadBody).
        requestedBodyIds.current.add(email.id);
        setFailedBodyIds((prev) => {
            if (!prev.has(email.id)) return prev;
            const next = new Set(prev);
            next.delete(email.id);
            return next;
        });
        graphMailService
            .fetchMessageBody(mailbox, email.id)
            .then((f) => { if (!cancelled) { setFull(f); setBodyError(false); if (f.id) setResolvedId(f.id); } })
            .catch((err) => {
                reportMailError({
                    area: "mail",
                    action: "load-detail",
                    error: err,
                    fallbackMessage: "Der Mail-Inhalt konnte nicht geladen werden.",
                });
                if (!cancelled) {
                    setBodyError(true);
                    setBodyError404(isGraph404(err));
                    setBodyUnavailable(isMailUnavailable(err));
                    // Auch die Konversations-Karte der Haupt-Mail zeigt dann die
                    // Fehlerzeile statt eines Endlos-Spinners.
                    setFailedBodyIds((prev) => new Set(prev).add(email.id));
                }
            });
        return () => { cancelled = true; };
    }, [mailbox, email.id, retryTick]);

    // Verzögerter Lade-Hinweis: Timer sauber aufräumen, damit ein schneller
    // Load (oder ein Fehler) nie kurz den Hinweis aufblitzen lässt.
    React.useEffect(() => {
        if (full || bodyError) {
            setBodyLoadingSlow(false);
            return;
        }
        const timer = window.setTimeout(() => setBodyLoadingSlow(true), 400);
        return () => window.clearTimeout(timer);
    }, [full, bodyError, retryTick]);

    const view = full ?? email;

    // ── SP3 (B3–B5): Outlook-artiger Mail-Kopf ──────────────────────────────
    // B5: Klick auf den Absender öffnet den KMVS-Kontakt — nur wenn ein
    // matchedContact existiert (bewusst KEINE Outlook-Visitenkarte sonst).
    const navigate = useNavigate();
    // Cluster ERKENNUNG (A2): der Mailkopf zeigt denselben Kontakt wie die
    // rechte Leiste und die Command-Leiste — aus EINER Quelle.
    const senderContact = displayContact;
    // B4 (User-Entscheidung 18): Präsenz = KMVS-Chat-Status — nur interne
    // Absender (E-Mail ↔ App-User via useInternalUserId); extern bleibt ohne
    // Punkt (kein Fake-Status, kein neuer MS-Graph-Consent).
    const senderUserId = useInternalUserId(view.senderEmail);
    const senderPresence = usePresence(senderUserId ? [senderUserId] : []);

    // ── SP4: Konversation als Outlook-Abschnitte ────────────────────────────
    // Alle Nachrichten des Threads laden (postfachweit, älteste zuerst). Bei
    // >1 Nachricht rendert der Body Abschnitts-Karten; sonst klassisch. Fehler
    // → null (Fallback auf Einzel-/Preview-Ansicht, nichts geht verloren) —
    // aber NICHT mehr still: ist die Mail als Konversation gruppiert
    // (email.type === "conversation"), zeigt ein Hinweis oberhalb des Bodys
    // die Degradation an, mit „Neu laden" (convoRetryTick).
    const [convoError, setConvoError] = React.useState(false);
    const [convoRetryTick, setConvoRetryTick] = React.useState(0);
    const [expandedIds, setExpandedIds] = React.useState<Set<string>>(new Set());
    // Voll-Bodies je Nachricht (lazy beim Aufklappen; Haupt-Mail wird aus `full` gespeist).
    const [bodies, setBodies] = React.useState<Record<string, Email>>({});

    React.useEffect(() => {
        // A4: Im Einzel-Modus die Konversation NICHT auffächern — nur diese
        // Nachricht wird gezeigt (kein Fan-out in der Vorgang-Ansicht).
        if (singleMessage || !email.conversationId) {
            setConvo(null);
            return;
        }
        let cancelled = false;
        setConvoError(false);
        graphMailService
            .fetchConversation(mailbox, email.conversationId)
            .then((list) => {
                if (cancelled) return;
                if (list.length > 1) {
                    setConvo(list);
                    // Die ganze Unterhaltung lesen (User 03.11.2026): ALLE
                    // Nachrichten aufgeklappt, neueste oben. Die Texte lädt der
                    // Nachlade-Effekt unten — gedrosselt, s. MAX_BODY_LADUNGEN.
                    setExpandedIds(new Set(list.map((m) => m.id)));
                } else {
                    setConvo(null);
                }
            })
            .catch((err) => {
                reportMailError({
                    area: "mail",
                    action: "load-conversation",
                    error: err,
                    fallbackMessage: "Die Konversation konnte nicht vollständig geladen werden.",
                });
                if (!cancelled) {
                    setConvo(null);
                    setConvoError(true);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [mailbox, email.conversationId, convoRetryTick, singleMessage]);

    // Haupt-Mail-Body aus dem ohnehin laufenden full-Load übernehmen. Vergleich
    // per Referenz (nicht nur „vorhanden?"), damit die Mail-SP7-gepatchte Fassung
    // (cid:→data:) auch in die Konversations-Karte derselben Nachricht durchschlägt.
    React.useEffect(() => {
        if (!full) return;
        // Self-Heal-Rekey: heilt das Backend die Id beim Body-Laden um, steht
        // dieselbe Nachricht in der (bis zu 30 s gecachten) Konversationsliste
        // womöglich noch unter der ALTEN Id. Der Body wird deshalb unter beiden
        // Ids abgelegt — sonst bliebe deren Karte im Spinner, weil `loadBody`
        // die alte Id bereits als angefordert führt und nicht erneut fetcht.
        const ids = full.id === email.id ? [full.id] : [full.id, email.id];
        setBodies((prev) => {
            if (ids.every((id) => prev[id] === full)) return prev;
            const next = { ...prev };
            for (const id of ids) next[id] = full;
            return next;
        });
    }, [full, email.id]);

    // Mail-SP7 — progressives Nachladen der Inline-Bilder der HAUPT-Mail: sobald
    // `full` mit `pendingInlineCids` da ist, Bilder holen und den Body patchen
    // (Body wurde bereits mit `cid:` gerendert). Nach dem Versuch ist
    // `pendingInlineCids` leer → kein erneuter Lauf (kein Loop bei Teil-Fehlern).
    React.useEffect(() => {
        if (!full || !(full.pendingInlineCids && full.pendingInlineCids.length > 0)) return;
        let cancelled = false;
        graphMailService
            .patchInlineImages(mailbox, email.id, full)
            .then((patched) => { if (!cancelled && patched !== full) setFull(patched); })
            .catch((err) => reportMailError({
                area: "mail",
                action: "load-inline-images",
                error: err,
                fallbackMessage: "Eingebettete Bilder konnten nicht vollständig geladen werden.",
            }));
        return () => { cancelled = true; };
    }, [full, mailbox, email.id]);

    // Body einer Konversations-Nachricht laden (lazy beim Aufklappen bzw. via
    // „Neu laden"). `force` überspringt den Dedupe-Schutz für den Retry-Fall.
    // Seit alle Nachrichten offen stehen, wollen bei langen Threads viele Texte
    // gleichzeitig laden — Exchange begrenzt die gleichzeitigen Verbindungen je
    // Postfach. Der Nachlade-Effekt hält deshalb höchstens so viele in Arbeit.
    const laufendeBodies = React.useRef(0);
    const loadBody = React.useCallback(
        (id: string, force = false) => {
            if (!force && requestedBodyIds.current.has(id)) return;
            requestedBodyIds.current.add(id);
            laufendeBodies.current += 1;
            setFailedBodyIds((prev) => {
                if (!prev.has(id)) return prev;
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
            graphMailService
                .fetchMessageBody(mailbox, id)
                .then((f) => {
                    // Vor dem State-Update: der Effekt, den es auslöst, darf schon nachlegen.
                    laufendeBodies.current = Math.max(0, laufendeBodies.current - 1);
                    setBodies((cur) => (cur[id] ? cur : { ...cur, [id]: f }));
                    // Mail-SP7: Inline-Bilder dieser Thread-Nachricht progressiv
                    // nachladen und `cid:`→`data:` patchen (graceful je Bild).
                    if (f.pendingInlineCids && f.pendingInlineCids.length > 0) {
                        graphMailService
                            .patchInlineImages(mailbox, id, f)
                            .then((patched) => { if (patched !== f) setBodies((cur) => ({ ...cur, [id]: patched })); })
                            .catch((e) => reportMailError({
                                area: "mail",
                                action: "load-inline-images",
                                error: e,
                                fallbackMessage: "Eingebettete Bilder konnten nicht vollständig geladen werden.",
                            }));
                    }
                })
                .catch((err) => {
                    laufendeBodies.current = Math.max(0, laufendeBodies.current - 1);
                    reportMailError({
                        area: "mail",
                        action: "load-detail",
                        error: err,
                        fallbackMessage: "Ein Mail-Inhalt der Konversation konnte nicht geladen werden.",
                    });
                    requestedBodyIds.current.delete(id); // erneuter Versuch erlaubt
                    setFailedBodyIds((prev) => new Set(prev).add(id));
                });
        },
        [mailbox],
    );

    // „Neu laden" je Abschnitt: die Haupt-Mail läuft über den full-Load-Effect
    // (setzt auch bodyError/`full` zurück), alle anderen direkt über loadBody.
    const retryBody = React.useCallback(
        (id: string) => {
            if (id === email.id) setRetryTick((t) => t + 1);
            else loadBody(id, true);
        },
        [email.id, loadBody],
    );

    const toggleExpand = React.useCallback(
        (id: string) => {
            setExpandedIds((prev) => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                return next;
            });
            // Body lazy nachladen (einmalig je Nachricht — dedupet in loadBody).
            loadBody(id);
        },
        [loadBody],
    );

    // Jede OFFENE Karte bekommt ihren Body — auch die, die niemand angeklickt hat.
    //
    // Der Fehler davor: Beim Laden der Konversation wird die neueste Nachricht
    // automatisch aufgeklappt (`setExpandedIds` oben), aber ohne Fetch. Da
    // `fetchConversation` die Nachrichten ohne Body liefert, stand die Karte
    // dauerhaft im Spinner — es sei denn, es war zufällig die geöffnete Mail,
    // deren Body der full-Load ohnehin holt. Genau das war meist NICHT der Fall:
    // die neueste Nachricht eines Threads ist häufig die eigene gesendete
    // Antwort, die in der Liste gar nicht auftaucht. Erst manuelles Zu- und
    // Wiederaufklappen half (`toggleExpand` lädt).
    //
    // Der Effect zieht das nach, statt es an einer einzelnen Stelle nachzureichen:
    // offen + kein Body + kein gemeldeter Fehler ⇒ laden. `loadBody` dedupet
    // selbst, die Haupt-Mail bleibt damit beim full-Load (dort schon als
    // angefordert vermerkt) und Fehlgeschlagene behalten ihre „Neu laden"-Zeile.
    React.useEffect(() => {
        if (!convo || convo.length <= 1) return;
        // Neueste zuerst — in dieser Reihenfolge stehen sie auch untereinander.
        for (const id of bodiesNachzuladen(neuesteZuerst(convo).map((m) => m.id), expandedIds, bodies, failedBodyIds)) {
            if (laufendeBodies.current >= MAX_BODY_LADUNGEN) break;
            loadBody(id);
        }
    }, [convo, expandedIds, bodies, failedBodyIds, loadBody]);

    // Auto-Load NUR aus dem verifizierten Detail-Flag, also erst NACH full-Load.
    React.useEffect(() => {
        if (full?.isInternalSenderVerified === true) setShowRemote(true);
    }, [full]);

    // T1: hat der Nutzer den Absender/seine Domäne dauerhaft freigegeben, sofort
    // Remote-Inhalte laden (Default bleibt sonst blockiert — Tracking-Schutz).
    React.useEffect(() => {
        if (isRemoteAllowed(email.senderEmail)) setShowRemote(true);
    }, [email.senderEmail]);

    // T2: Datei-Anhänge nachladen (nur wenn die Mail welche hat). Inline-Bilder
    // (cid:) werden im Body angezeigt → hier nur die echten Datei-Anhänge listen.
    const [attachments, setAttachments] = React.useState<MailAttachment[]>([]);
    // Fehlgeschlagenes Anhänge-Laden sichtbar machen (statt still leer) —
    // „Neu laden" über attRetryTick.
    const [attachmentsError, setAttachmentsError] = React.useState(false);
    const [attRetryTick, setAttRetryTick] = React.useState(0);
    const [downloadingAtt, setDownloadingAtt] = React.useState<string | null>(null);
    // Anhänge über ALLE Nachrichten der Konversation laden, die welche tragen
    // (attachmentMessageIds) — nicht nur die neueste. Fallback auf email.id für
    // Alt-/Nicht-gruppierte Objekte. Merge + Dedup, Inline-Bilder ausblenden.
    const attachmentSourceKey = (email.attachmentMessageIds ?? []).join(",");
    React.useEffect(() => {
        const ids = email.attachmentMessageIds?.length
            // Self-Heal-Rekey: die (ggf. geheilte) aktuelle Id nutzen, nicht die
            // evtl. stale email.id — sonst 403 für Beteiligte nach dem Body-Heal.
            // Im Thread-Fall die Haupt-Nachricht-Id auf die geheilte Id abbilden.
            ? email.attachmentMessageIds.map((mid) => (mid === email.id ? resolvedId : mid))
            : email.hasAttachments
                ? [resolvedId]
                : [];
        setAttachmentsError(false);
        if (ids.length === 0) { setAttachments([]); return; }
        let cancelled = false;
        Promise.all(ids.map((mid) => graphMailService.fetchAttachments(mailbox, mid)))
            .then((lists) => {
                if (cancelled) return;
                setAttachments(dedupeAttachments(lists.flat()).filter((a) => !a.isInline));
            })
            .catch((err) => {
                reportMailError({
                    area: "mail",
                    action: "load-attachments",
                    error: err,
                    fallbackMessage: "Anhänge konnten nicht geladen werden.",
                });
                if (!cancelled) setAttachmentsError(true);
            });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mailbox, email.id, resolvedId, email.hasAttachments, attachmentSourceKey, attRetryTick]);

    const handleDownloadAttachment = React.useCallback(async (att: MailAttachment) => {
        const key = attachmentKey(att);
        setDownloadingAtt(key);
        try {
            // att.messageId (Quell-Nachricht), NICHT email.id — der Anhang kann an
            // einer früheren Thread-Nachricht hängen.
            await graphMailService.downloadAttachment(mailbox, att.messageId, att.id, att.name);
        } catch (err) {
            setAttSaveMsg(reportMailError({
                area: "mail",
                action: "download-attachment",
                error: err,
                fallbackMessage: "Der Anhang konnte nicht heruntergeladen werden.",
            }));
        } finally {
            setDownloadingAtt(null);
        }
    }, [mailbox]);

    // PO-Notiz 26.07.2026 (Punkt 12): Klick auf den Anhangsnamen öffnet PDFs und
    // Bilder im KMVS-Viewer statt sie herunterzuladen; der Download bleibt als
    // eigener Button daneben. Nicht darstellbare Typen (docx, zip …) laden
    // weiterhin direkt herunter.
    const handleOpenAttachment = React.useCallback((att: MailAttachment) => {
        if (!canPreviewInline(att.name, att.contentType)) {
            void handleDownloadAttachment(att);
            return;
        }
        openFile({
            key: `mail-${att.messageId}-${att.id}`,
            fileName: att.name,
            mimeType: att.contentType,
            load: () => graphMailService.fetchAttachmentBlob(mailbox, att.messageId, att.id),
            onDownload: () => void handleDownloadAttachment(att),
        });
    }, [mailbox, openFile, handleDownloadAttachment]);

    // SP5 (Plan 1): „Beim Kontakt speichern" — Anhang als Kundendokument ablegen
    // (konsolidierter Endpoint `mail/attachments/import`). Nur aktiv, wenn die
    // Mail einem KMVS-Kontakt zugeordnet ist (matchedContact). Idempotent: das
    // Backend meldet Duplikate, hier zeigen wir „gespeichert".
    const [savingAttKey, setSavingAttKey] = React.useState<string | null>(null);
    const [savedAttKeys, setSavedAttKeys] = React.useState<Set<string>>(() => new Set());
    const [attSaveMsg, setAttSaveMsg] = React.useState<string | null>(null);

    /**
     * Wie viele Bilder gerade noch geladen werden — je Body-Instanz, weil ein
     * aufgefächerter Verlauf mehrere gleichzeitig zeigt (`mailBilderLaden.ts`).
     */
    const [bilderStand, setBilderStand] = React.useState<BilderStand>({});
    const meldeBilderLaden = React.useCallback(
        (quelle: string, anzahl: number) => setBilderStand((s) => setzeStand(s, quelle, anzahl)),
        [],
    );
    const bilderHinweis = bilderLadenText(summeLadender(bilderStand));

    // ── Visitenkarte im Anhang (.vcf) → Kontakt anlegen ─────────────────────
    const [vcfLaedt, setVcfLaedt] = React.useState<string | null>(null);

    /**
     * Öffnet die Anlage-Maske mit den Werten der Visitenkarte.
     *
     * Bewusst `modus: "nur-anlegen"`: Eine angehängte Visitenkarte gehört fast
     * nie dem Absender — jemand LEITET sie weiter. Würde dieser Weg die Mail
     * zuweisen, landete sie beim Falschen und wäre archiviert dazu.
     *
     * Die Werte reisen als `erkennung.signature`, weil die Maske genau daraus
     * schon ihre Vorbelegung zieht (`vorbelegungAusMail`) — die Felder einer
     * Visitenkarte und die einer erkannten Signatur sind dieselben.
     */
    const oeffneVisitenkarte = React.useCallback((karte: Visitenkarte) => {
        openMailContactAssign(openWindow, {
            senderEmail: karte.email || null,
            senderName: [karte.vorname, karte.nachname].filter(Boolean).join(" ") || null,
            modus: "nur-anlegen",
            erkennung: {
                signature: {
                    name: { firstName: karte.vorname, lastName: karte.nachname },
                    company: karte.firma ? { value: karte.firma } : null,
                    phone: karte.telefon ? { value: karte.telefon } : null,
                    mobile: karte.mobil ? { value: karte.mobil } : null,
                },
            },
            onResolved: (ergebnis) => {
                if (ergebnis.kontakt) {
                    setAttSaveMsg(`Kontakt „${ergebnis.kontakt.displayName}" ist angelegt.`);
                }
            },
        });
    }, [openWindow]);

    // ── T-36: Kontakt aus einer Markierung im Mailtext ─────────────────────
    // Markieren → kleiner Knopf „Kontakt anlegen" an der Markierung; Rechtsklick
    // auf die Markierung → Menü „Als neuen Kontakt anlegen | Kopieren". Der Text
    // geht an den Signatur-Parser (POST /mail/recognize/signature-text), die
    // Anlage läuft wie bei der Visitenkarte im Modus „nur anlegen": wer eine
    // fremde Signatur markiert, will die Mail nicht zwingend zuweisen.
    const [markierung, setMarkierung] = React.useState<{
        text: string;
        x: number;
        y: number;
        modus: "knopf" | "menue";
    } | null>(null);
    const beiMarkierung = React.useCallback((m: MailMarkierung | null) => {
        const angebot = m ? angebotFuer(m.text) : null;
        if (!m || !angebot) {
            setMarkierung(null);
            return;
        }
        setMarkierung({
            text: angebot.text,
            x: m.rect?.left ?? 120,
            y: (m.rect?.bottom ?? 120) + 6,
            modus: m.kontextmenue ? "menue" : "knopf",
        });
    }, []);
    const schliesseMarkierung = React.useCallback(() => setMarkierung(null), []);
    const oeffneMarkierungAlsKontakt = React.useCallback(async (text: string) => {
        setMarkierung(null);
        const sig = await graphMailService.parseSignatureText(text);
        const erkannt = sig && sig.found ? sig : null;
        openMailContactAssign(openWindow, {
            senderEmail: erkannt?.emails?.[0] ?? null,
            senderName: null,
            modus: "nur-anlegen",
            markierterText: text,
            erkennung: erkannt ? { signature: erkannt } : null,
            onResolved: (ergebnis) => {
                if (ergebnis.kontakt) {
                    setAttSaveMsg(`Kontakt „${ergebnis.kontakt.displayName}" ist angelegt.`);
                }
            },
        });
    }, [openWindow]);

    /** Lädt die .vcf-Datei und öffnet damit die Anlage-Maske. */
    const handleVisitenkarte = React.useCallback(async (att: MailAttachment) => {
        const key = attachmentKey(att);
        setVcfLaedt(key);
        setAttSaveMsg(null);
        try {
            const blob = await graphMailService.fetchAttachmentBlob(mailbox, att.messageId, att.id);
            const karten = leseVisitenkarten(dekodiereVcf(await blob.arrayBuffer()));
            if (karten.length === 0) {
                setAttSaveMsg("Diese Visitenkarte enthält keine lesbaren Kontaktdaten.");
                return;
            }
            if (karten.length > 1) {
                // Ein exportiertes Adressbuch. Still die erste zu nehmen wäre ein
                // unsichtbarer Verlust — also wird gesagt, was passiert.
                const name = [karten[0].vorname, karten[0].nachname].filter(Boolean).join(" ");
                setAttSaveMsg(
                    `Die Datei enthält ${karten.length} Visitenkarten. Geöffnet wird die erste${name ? ` (${name})` : ""}.`,
                );
            }
            oeffneVisitenkarte(karten[0]);
        } catch (err) {
            setAttSaveMsg(reportMailError({
                area: "mail",
                action: "download-attachment",
                error: err,
                fallbackMessage: "Die Visitenkarte konnte nicht gelesen werden.",
            }));
        } finally {
            setVcfLaedt(null);
        }
    }, [mailbox, oeffneVisitenkarte]);
    /**
     * Fehlgeschlagenes Archivieren (2026-07-29). Steht bewusst im Fenster statt
     * nur in der Konsole: Archivieren verlangt Postfach-Recht, Lesen dagegen
     * genügt Beteiligung am Vorgang — die Aktion scheitert also regelmäßig für
     * Leute, die die Mail sehen dürfen.
     */
    const [archiveError, setArchiveError] = React.useState<string | null>(null);
    /**
     * Cluster ABLAGE (E11) — Hinweis zur DAUERHAFTEN Ablage, getrennt von
     * `archiveError` (dem Fehlschlag des Graph-Moves selbst). Er entsteht, wenn
     * die Mail zwar archiviert, aber nicht gesichert wurde. Bewusst ein eigener
     * Zustand: „archiviert" und „dauerhaft gesichert" sind zwei Aussagen.
     */
    const [archiveNote, setArchiveNote] = React.useState<string | null>(null);
    const [attachmentsOpen, setAttachmentsOpen] = React.useState(true);
    /** Cc-Empfänger sind eingeklappt, bis der User sie anfordert (PO 26.07.2026). */
    const [ccOpen, setCcOpen] = React.useState(false);
    // Handy (kompakt): die Absender-Details klappen auf.
    const [kopfDetails, setKopfDetails] = React.useState(false);
    // Handy: „…" einer einzelnen Nachricht der Unterhaltung.
    const [nachrichtMenue, setNachrichtMenue] = React.useState<Email | null>(null);
    /** Dasselbe für „An:", sobald es mehr Empfänger sind als die Zeile zeigt. */
    const [toOpen, setToOpen] = React.useState(false);

    // J1 — Der frühere Aufklapp-Block „Besprechen" unter dem Mailtext ist
    // entfallen: Kommentare zu DIESER Mail stehen jetzt im Reiter „Kommentare"
    // der rechten Leiste, die es seit diesem Schritt an JEDER geöffneten Mail
    // gibt (auch im Fenster). Zwei Eingabefelder auf dieselbe MailNote-Tabelle,
    // zwei Live-Abonnements auf denselben Socket-Raum und zwei Ladepfade
    // nebeneinander waren eine Fehlerquelle ohne Nutzen. Der Datenbestand
    // bleibt vollständig — dieselbe Tabelle, kein Eintrag geht verloren.

    /**
     * J2 — der zugewiesene Kontakt dieser Mail, aus EINER Auflösung:
     * explizite Entscheidung → Link dieser Nachricht → Link der Konversation →
     * Kunde des verknüpften Vorgangs (E01) → Erkennung. Alles, was „der
     * Kontakt dieser Mail" braucht (rechte Leiste, „Beim Kontakt speichern"),
     * hängt daran — nicht mehr an der Adress-Annotation des Listenobjekts.
     */
    const zuweisungJ2 = React.useMemo(
        () =>
            zugewiesenerKontakt({
                messageId: email.id,
                explizit: expliziteZuweisung,
                links: convLinks ?? [],
                erkannt: displayContact,
            }),
        [email.id, expliziteZuweisung, convLinks, displayContact],
    );

    // Beim Wechsel der Mail ist nichts mehr entschieden.
    React.useEffect(() => {
        setExpliziteZuweisung(undefined);
    }, [mailbox, email.id]);

    /**
     * Ein anderes Fenster (oder die Liste) hat die Zuweisung geändert: gleiche
     * Nachricht ODER gleicher Verlauf ⇒ hier mitziehen.
     *
     * P7 (PO 14.09.2026): meldet der Bus eine VORGANGS-Verknüpfung, werden die
     * Konversations-Links frisch geholt, damit „Gehört zum Vorgang" im Kopf
     * steht. Bewusst NICHT über `convLinksTick`: der setzt `convLinks` auf
     * null und ließe die Command-Leiste kurz in die neutrale Ansicht kippen.
     * Hier bleibt der alte Stand stehen, bis der neue da ist.
     */
    React.useEffect(() => {
        let aktiv = true;
        const ab = abonniereZuweisung((z) => {
            if (!betrifft(z, { mailbox, messageId: email.id, conversationId: email.conversationId ?? null })) return;
            if (z.kontakt !== undefined) setExpliziteZuweisung(z.kontakt);
            if (z.vorgang !== undefined && email.conversationId) {
                const generation = ++convLinksGenerationRef.current;
                mailLinkService
                    .listByConversation(email.conversationId, mailbox)
                    .then((links) => {
                        if (aktiv && convLinksGenerationRef.current === generation) {
                            setConvLinks(links);
                        }
                    })
                    .catch(() => { /* alter Stand bleibt — kein Banner für Beiwerk */ });
            }
        });
        return () => {
            aktiv = false;
            ab();
        };
    }, [mailbox, email.id, email.conversationId]);

    /** P7 — die Vorgänge, an denen diese Konversation hängt (dedupliziert). */
    const zugeordneteVorgaenge = React.useMemo(() => vorgaengeAusLinks(convLinks ?? []), [convLinks]);
    const weitereKontakte = React.useMemo(
        () => weitereKontaktZuweisungen(convLinks, email.id, zuweisungJ2.kontakt?.id ?? null),
        [convLinks, email.id, zuweisungJ2.kontakt],
    );

    const saveTargetContact = zuweisungJ2.kontakt;

    // T-369: In einer Konversation stehen die Anhänge aller Nachrichten
    // gesammelt oben — je Anhang, aus welcher Nachricht er stammt.
    const anhangHerkunft = React.useMemo(() => {
        const m = new Map<string, string>();
        for (const msg of convo ?? []) {
            const label = `von ${msg.senderName || msg.senderEmail || "Unbekannt"}, ${formatDate(msg.date)}`;
            m.set(msg.id, label);
            // Self-Heal-Rekey: Anhänge der Haupt-Nachricht tragen ggf. die geheilte Id.
            if (msg.id === email.id && resolvedId !== email.id) m.set(resolvedId, label);
        }
        return m;
    }, [convo, email.id, resolvedId]);

    const handleSaveAttachment = React.useCallback(async (att: MailAttachment) => {
        if (!saveTargetContact) return;
        const key = attachmentKey(att);
        setSavingAttKey(key);
        setAttSaveMsg(null);
        try {
            const res = await graphMailService.importAttachments({
                mailbox,
                contactId: saveTargetContact.id,
                attachments: [{ messageId: att.messageId, attachmentId: att.id }],
            });
            const r = res.results[0];
            // Das Backend fängt Anhang-Fehler pro Item ab und antwortet dennoch
            // mit HTTP 200 + status:'error' (kein Kundendokument angelegt, z. B.
            // bei Graph-Throttling/Timeout). Solche Items NICHT als gespeichert
            // markieren — sonst gäbe es eine falsche Erfolgsmeldung und der Anhang
            // ließe sich wegen Häkchen/disabled nicht erneut speichern.
            if (!r || r.status === "error") {
                reportMailError({
                    area: "mail",
                    action: "save-attachment",
                    error: new Error(r?.error || "attachment import failed"),
                    fallbackMessage: "Der Anhang konnte nicht beim Kontakt gespeichert werden.",
                });
                setAttSaveMsg(
                    `„${att.name}" konnte nicht beim Kontakt gespeichert werden.`,
                );
                return;
            }
            setSavedAttKeys((prev) => new Set(prev).add(key));
            setAttSaveMsg(
                r.status === "duplicate" || r.status === "linked"
                    ? `„${att.name}" war bereits beim Kontakt gespeichert.`
                    : `„${att.name}" beim Kontakt gespeichert.`,
            );
        } catch (err: unknown) {
            setAttSaveMsg(reportMailError({
                area: "mail",
                action: "save-attachment",
                error: err,
                fallbackMessage: "Der Anhang konnte nicht beim Kontakt gespeichert werden.",
            }));
        } finally {
            setSavingAttKey(null);
        }
    }, [mailbox, saveTargetContact]);

    const handleSaveAllAttachments = React.useCallback(async () => {
        if (!saveTargetContact || attachments.length === 0) return;
        setSavingAttKey("__all__");
        setAttSaveMsg(null);
        try {
            const res = await graphMailService.importAttachments({
                mailbox,
                contactId: saveTargetContact.id,
                attachments: toImportItems(attachments),
            });
            // NUR tatsächlich angelegte/bereits vorhandene Anhänge als gespeichert
            // markieren. Fehler-Items (HTTP 200 + status:'error', kein Dokument
            // angelegt) bleiben ohne Häkchen und erneut speicherbar — sonst würde
            // ein Fehlschlag (z. B. Graph-Throttling) fälschlich als gespeichert
            // angezeigt.
            setSavedAttKeys((prev) => {
                const next = new Set(prev);
                for (const r of res.results) {
                    if (r.status === "error") continue;
                    next.add(attachmentKey({ messageId: r.messageId, id: r.attachmentId }));
                }
                return next;
            });
            const failed = res.results.filter((r) => r.status === "error").length;
            if (failed > 0) {
                reportMailError({
                    area: "mail",
                    action: "save-attachment",
                    error: new Error("one or more attachment imports failed"),
                    fallbackMessage: "Einige Anhänge konnten nicht gespeichert werden.",
                });
            }
            setAttSaveMsg(
                `${res.imported} beim Kontakt gespeichert` +
                    (res.skipped ? `, ${res.skipped} bereits vorhanden` : "") +
                    (failed ? `, ${failed} fehlgeschlagen` : "") +
                    ".",
            );
        } catch (err: unknown) {
            setAttSaveMsg(reportMailError({
                area: "mail",
                action: "save-attachment",
                error: err,
                fallbackMessage: "Die Anhänge konnten nicht beim Kontakt gespeichert werden.",
            }));
        } finally {
            setSavingAttKey(null);
        }
    }, [mailbox, saveTargetContact, attachments]);

    // ── T-236: Bilder im Mailtext als Datei ────────────────────────────────
    // Klick aufs Bild → Vorschau im KMVS-Viewer; Rechtsklick → Menü „Vorschau |
    // Speichern unter … | Beim Kontakt speichern". Die Bilder bleiben getrennt
    // von `attachments`: „Alle beim Kontakt speichern", der Reiter Anhänge und
    // das Weiterleiten nehmen sie nie mit (Signatur-Logos).
    //
    // Geladen wird erst bei Bedarf: eine reine Screenshot-Mail meldet
    // `hasAttachments: false`, ihre Anhangliste fragt sonst niemand ab.
    const anhangListenRef = React.useRef(new Map<string, Promise<MailAttachment[]>>());
    const ladeAnhaengeVon = React.useCallback((messageId: string): Promise<MailAttachment[]> => {
        const key = `${mailbox}|${messageId}`;
        let liste = anhangListenRef.current.get(key);
        if (!liste) {
            liste = graphMailService.fetchAttachments(mailbox, messageId).catch((err) => {
                // Ein Fehlschlag darf nicht im Cache kleben bleiben.
                anhangListenRef.current.delete(key);
                throw err;
            });
            anhangListenRef.current.set(key, liste);
        }
        return liste;
    }, [mailbox]);
    /** Eingebettete Bilder der ganzen Konversation — leer statt Fehler. */
    const ladeInlineBilder = React.useCallback(async (): Promise<MailAttachment[]> => {
        const ids = Array.from(new Set([
            resolvedId,
            ...(email.attachmentMessageIds ?? []).map((mid) => (mid === email.id ? resolvedId : mid)),
        ]));
        const listen = await Promise.all(ids.map((mid) => ladeAnhaengeVon(mid).catch(() => [] as MailAttachment[])));
        return eingebetteteBilder(listen.flat());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ladeAnhaengeVon, resolvedId, email.id, attachmentSourceKey]);
    const findeBild = React.useCallback(async (bild: MailBild, messageId: string): Promise<MailAttachment | null> => {
        if (!bild.cid) return null;
        const eigene = await ladeAnhaengeVon(messageId).catch(() => [] as MailAttachment[]);
        return findeBildZurCid(eigene, bild.cid, messageId)
            ?? findeBildZurCid(await ladeInlineBilder(), bild.cid, messageId);
    }, [ladeAnhaengeVon, ladeInlineBilder]);

    // Bilder, die für die Übernahme in einen Vorgang in Frage kommen (ohne
    // Logos). Vorab geladen, sobald der Mailtext eingebettete Bilder zeigt —
    // „Vorgang erstellen" öffnet sein Fenster sofort und kann nicht warten.
    const [vorgangsBilder, setVorgangsBilder] = React.useState<MailAttachment[]>([]);
    const hatBilderImText = /data-kmvs-cid=|["'(]cid:/i.test(view.body ?? "");
    React.useEffect(() => {
        if (!hatBilderImText) {
            setVorgangsBilder([]);
            return;
        }
        let abgebrochen = false;
        void ladeInlineBilder().then((bilder) => {
            if (!abgebrochen) setVorgangsBilder(bilderFuerVorgang(bilder));
        });
        return () => {
            abgebrochen = true;
        };
    }, [hatBilderImText, ladeInlineBilder]);
    const bildDatum = view.date instanceof Date ? view.date : new Date();
    /**
     * Anhänge für „Vorgang erstellen": die Dateien (vorausgewählt) und dahinter
     * die Bilder aus dem Mailtext als eigene, nicht vorausgewählte Gruppe.
     */
    const flowAnhaenge = () =>
        attachments.length + vorgangsBilder.length > 0
            ? {
                  mailbox,
                  items: [
                      ...attachments.map((a) => ({
                          messageId: a.messageId,
                          attachmentId: a.id,
                          name: a.name,
                          size: a.size,
                      })),
                      ...bilderAlsFlowAnhaenge(vorgangsBilder, bildDatum),
                  ],
              }
            : undefined;

    const [bildMenue, setBildMenue] = React.useState<{
        bild: MailBild;
        messageId: string;
        /** `undefined` = wird noch gesucht, `null` = kein Anhang zu diesem Bild. */
        anhang: MailAttachment | null | undefined;
    } | null>(null);
    const schliesseBildMenue = React.useCallback(() => setBildMenue(null), []);

    const bildName = (bild: MailBild, anhang: MailAttachment | null): string =>
        bildDateiname(
            anhang?.name,
            anhang?.contentType ?? dataUriTyp(bild.dataUri),
            view.date instanceof Date ? view.date : new Date(),
        );
    const speichereBildUnter = async (bild: MailBild, anhang: MailAttachment | null) => {
        try {
            await speichereBlobUnter(await dataUriZuBlob(bild.dataUri), bildName(bild, anhang));
        } catch (err) {
            zeigeGlobalenHinweis(
                reportMailError({
                    area: "mail",
                    action: "download-attachment",
                    error: err,
                    fallbackMessage: "Das Bild konnte nicht gespeichert werden.",
                }),
                { art: "fehler" },
            );
        }
    };
    const oeffneBild = (bild: MailBild, messageId: string, anhang: MailAttachment | null) => {
        const typ = anhang?.contentType ?? dataUriTyp(bild.dataUri) ?? "image/png";
        openFile({
            key: anhang
                ? `mail-inline-${anhang.messageId}-${anhang.id}`
                : `mail-inline-${messageId}-${bild.dataUri.length}-${bild.dataUri.slice(-16)}`,
            fileName: bildName(bild, anhang),
            mimeType: typ,
            // Das Bild steht schon im Mailtext — kein zweiter Abruf.
            load: () => dataUriZuBlob(bild.dataUri),
            onDownload: () => void speichereBildUnter(bild, anhang),
        });
    };
    const speichereBildBeimKontakt = async (anhang: MailAttachment) => {
        if (!saveTargetContact) return;
        try {
            const res = await graphMailService.importAttachments({
                mailbox,
                contactId: saveTargetContact.id,
                attachments: [{ messageId: anhang.messageId, attachmentId: anhang.id }],
            });
            const r = res.results[0];
            if (!r || r.status === "error") throw new Error(r?.error || "attachment import failed");
            zeigeGlobalenHinweis(
                r.status === "duplicate" || r.status === "linked"
                    ? "Das Bild war bereits beim Kontakt gespeichert."
                    : "Bild beim Kontakt gespeichert.",
                { art: "info" },
            );
        } catch (err) {
            zeigeGlobalenHinweis(
                reportMailError({
                    area: "mail",
                    action: "save-attachment",
                    error: err,
                    fallbackMessage: "Das Bild konnte nicht beim Kontakt gespeichert werden.",
                }),
                { art: "fehler" },
            );
        }
    };
    const beiBild = (bild: MailBild, messageId: string) => {
        if (!bild.kontextmenue) {
            // Sofort öffnen; der Dateiname braucht den Anhang, der meist schon im Cache liegt.
            void findeBild(bild, messageId).then((anhang) => oeffneBild(bild, messageId, anhang));
            return;
        }
        setMarkierung(null);
        setBildMenue({ bild, messageId, anhang: undefined });
        void findeBild(bild, messageId).then((anhang) =>
            setBildMenue((offen) => (offen && offen.bild === bild ? { ...offen, anhang } : offen)),
        );
    };

    // NACHTRAG 2026-07-05: „Archivieren" = ECHTER Graph-Move in den Archiv-
    // Ordner (vorher nur gelesen-markieren). Nur aus expliziter Aktion, nie
    // beim bloßen Schließen des Fensters.
    const finishRead = React.useCallback(async () => {
        try {
            // Cluster ABLAGE: der erkannte Kontakt reist mit, damit die Ablage
            // ihn nicht erneut auflösen muss.
            const res = await graphMailService.archiveMessage(mailbox, email.id, {
                contactId: saveTargetContact?.id ?? null,
            });
            const ablage = res?.archive ?? null;
            // E11 — der Fall „zu groß" darf NICHT still verschluckt werden.
            // Hier bleibt das Fenster deshalb bewusst offen und verlangt eine
            // Kenntnisnahme: die Mail liegt zwar im Archivordner, ist aber als
            // einzige NICHT dauerhaft gesichert. Alle anderen Zustände schließen
            // wie bisher. T-240: eine VERKNÜPFTE Mail verschiebt der Server erst
            // nach ihrer Sicherung — `archiveMessage` wartet ein 409
            // MAIL_CAPTURE_PENDING ab; ein unverknüpftes `pending` holt der Cron.
            if (ablage?.status === "skipped" || ablage?.status === "failed") {
                if (ablage.status === "failed") {
                    reportMailError({
                        area: "mail",
                        action: "archive",
                        error: new Error(ablage.error || "permanent archive failed"),
                        fallbackMessage: "Die dauerhafte Mail-Ablage ist fehlgeschlagen.",
                    });
                }
                setArchiveNote(
                    ablage.status === "skipped"
                        ? "Diese Mail ist zu groß für die dauerhafte Ablage und wurde NICHT gesichert."
                        : "Die Mail wurde archiviert, die dauerhafte Ablage ist aber fehlgeschlagen.",
                );
                setArchiveError(null);
                return true;
            }
        } catch (err: unknown) {
            // 2026-07-29: Fehler NICHT mehr verschlucken. Archivieren hängt
            // strikt am Postfach-Recht — anders als das Lesen, das über den
            // zugeordneten Vorgang möglich ist. Ein Beteiligter ohne Zugriff auf
            // `mailbox` bekam vorher nur einen Konsolen-Eintrag: das Fenster
            // schloss sich, `onArchived` lief, und die Aktion GALT als erledigt,
            // obwohl die Mail unverändert im Postfach lag. Jetzt bleibt das
            // Fenster offen und benennt das Problem.
            setArchiveError(reportMailError({
                area: "mail",
                action: "archive",
                error: err,
                fallbackMessage: "Die Mail konnte nicht archiviert werden.",
            }));
            return false;
        }
        setArchiveError(null);
        onArchived?.(email.id);
        requestClose();
        return true;
    }, [email.id, mailbox, onArchived, requestClose, saveTargetContact]);

    // T-229-Nachtrag (02.11.2026, wie Outlook): Nach dem Zusagen / mit Vorbehalt
    // Zusagen / Absagen wandert die Einladung in „Gelöschte Elemente" — derselbe
    // Weg wie „Löschen" in der Liste (Verschieben, MailLinks ziehen mit). Ein
    // 404 heißt „schon weg". Fehler wirft weiter: die Leiste sagt es dann.
    /**
     * Handy: eine EINZELNE Nachricht der Unterhaltung löschen (Papierkorb). Ist
     * es die geöffnete Mail, geht die Ansicht zu; sonst fällt nur ihr Abschnitt weg.
     */
    const loescheNachricht = async (msg: Email) => {
        try {
            await loescheMailZeile(
                { mailbox, id: msg.id, entwurf: false },
                { moveMessage: graphMailService.moveMessage, deleteDraft: graphMailService.deleteDraft },
            );
        } catch (err: unknown) {
            setArchiveError(
                reportMailError({
                    area: "mail",
                    action: "delete",
                    error: err,
                    fallbackMessage: "Die Nachricht konnte nicht gelöscht werden.",
                }),
            );
            return;
        }
        onArchived?.(msg.id);
        if (msg.id === email.id) requestClose();
        else setConvo((c) => (c ? c.filter((m) => m.id !== msg.id) : c));
    };

    const einladungBeantwortet = React.useCallback(async () => {
        await loescheMailZeile(
            { mailbox, id: email.id, entwurf: false },
            { moveMessage: graphMailService.moveMessage, deleteDraft: graphMailService.deleteDraft },
        );
        onArchived?.(email.id);
        requestClose();
    }, [email.id, mailbox, onArchived, requestClose]);

    // Create-Rework CR-SP5: Mail-Aktionen führen IMMER durch den zweistufigen
    // Erstell-Flow (Zuweisung zuerst) — es gibt keine Mail-Entwürfe mehr
    // (User-Entscheidung). Nach dem Anlegen wird die Mail best-effort an das
    // Ergebnis gebunden (Vorgang ODER Todo im bestehenden Vorgang) und die
    // Mail als verarbeitet markiert. Snapshot VORHER kopieren (das Detail-
    // Fenster kann zwischenzeitlich geschlossen sein).
    // SP2 (B4): `contactOverride` = der in der Command-Leiste ausgewählte
    // Erkennungs-Kandidat — die getroffene Auswahl IST bereits die Zuweisung;
    // ohne Override bleibt der bisherige matchedContact-Vorschlag.
    // SP3 (C3): Betreff/Inhalt der Mail werden NICHT mehr in den Vorgang
    // übernommen — die Vorbelegung ist NUR der Kontakt (mailVorgangPrefill).
    // `opts.explicit` (SP4): das Ergebnis des Zuweisungs-Flows ist maßgeblich —
    // ein explizit übergebenes `null` heißt „Vorgang OHNE Kunden" (Entscheidung
    // 28) und fällt NICHT auf den matchedContact zurück.
    const openCreateFlowFromMail = (
        contactOverride?: { id: number; displayName: string } | null,
        opts?: { explicit?: boolean; vorgangFuer?: VorgangFuerVorgabe },
    ) => {
        const matched = opts?.explicit
            ? (contactOverride ?? null)
            : (contactOverride ?? displayContact ?? undefined);
        const snap = mailSnapshot();
        openVorgangCreateFlow(openWindow, {
            // E1/E5: Der Mail-Weg ist einschrittig, und der Mail-Betreff reist als
            // Angebot für den Stift-Button mit (er wird NICHT vorbelegt, C3).
            // Runde 3: Die führende Kunden-Klammer „(Acme GmbH / " schneidet
            // `mailVorgangPrefill` selbst weg — dort, wo ALLE Mail-Einstiege
            // durchlaufen. Hier NICHT noch einmal: zweimal geschnitten fräst
            // eine verschachtelte Klammer zu weit.
            ...mailVorgangPrefill(matched, view.subject ?? email.subject ?? null),
            // PO 01.10.2026: die in „Kontakt zuweisen" getroffene Wahl Person/Firma.
            vorgangFuer: opts?.vorgangFuer,
            // PO 14.09.2026: „in Mails neuer Vorgang, wenn ohne Kontakt
            // weitermachen — dann auch die Möglichkeit, persönlichen Vorgang zu
            // erstellen." Die Wahl steht deshalb GENAU DANN, wenn kein Kunde
            // feststeht (ausdrücklich „Ohne Kontakt fortfahren" oder
            // unerkannter Absender). Mit erkanntem Kunden bleibt es beim
            // Kontakt-Vorgang — ein privater Vorgang wäre dort ein
            // Versehen-Kandidat, und der Schriftverkehr verschwände für alle
            // anderen. Explizit gesetzt, weil die Ableitung im Flow an den
            // Mail-Anhängen hängt.
            allowPersoenlich: matched == null,
            // SP5 (Entscheidung 31): Datei-Anhänge der Mail zur Übernahme in den
            // Vorgang (vorausgewählt, abwählbar) — der Import läuft im Flow nach
            // dem Anlegen (contactId + vorgangId).
            mailAttachments: flowAnhaenge(),
            onCreated: async (result: VorgangCreateResult) => {
                try {
                    if (result.vorgangId) {
                        meldeVerlauf(await bindVerlauf({ vorgangId: result.vorgangId }, snap));
                    } else if (result.todoId) {
                        meldeVerlauf(await bindVerlauf({ todoId: result.todoId }, snap));
                    }
                } catch (e) {
                    reportMailError({
                        area: "mail",
                        action: "link",
                        error: e,
                        fallbackMessage: "Die Mail konnte nicht mit dem Vorgang verknüpft werden.",
                    });
                }
                // E01/J2: Der Kunde des neuen Vorgangs ist ab jetzt der Kontakt
                // dieser Mail — melden, damit Liste und Leiste mitziehen.
                if (matched) uebernimmZuweisung(matched, { persistieren: false });
                // AWAITED, nicht fire-and-forget: Das Archivieren ist ein
                // Graph-Move, der eine NEUE messageId vergibt; das Backend
                // remapt den eben angelegten MailLink (old→new). Navigierte
                // der Flow schon vorher zur Vorgang-Detailseite, holte deren
                // Mail-Tab die Links noch MIT der alten Id — erstes Öffnen
                // scheiterte dann mit „Mail nicht verfügbar" (bis zum Reload).
                await finishRead();
            },
        });
    };

    const handleRecognizedClick = (item: any) => {
        const matched = item.contactId ? { id: item.contactId } : null;
        const snap = mailSnapshot();
        const flowParams: any = {
            // E1/E5: Der Mail-Weg ist einschrittig, und der Mail-Betreff reist als
            // Angebot für den Stift-Button mit (er wird NICHT vorbelegt, C3).
            // Runde 3: Die führende Kunden-Klammer „(Acme GmbH / " schneidet
            // `mailVorgangPrefill` selbst weg — dort, wo ALLE Mail-Einstiege
            // durchlaufen. Hier NICHT noch einmal: zweimal geschnitten fräst
            // eine verschachtelte Klammer zu weit.
            ...mailVorgangPrefill(matched, view.subject ?? email.subject ?? null),
            // Siehe oben: die Wahl steht nur ohne erkannten Kunden. Hier kommt
            // der Kontakt aus dem angeklickten Erkennungs-Treffer — hat der
            // keinen, ist der Weg derselbe wie „Ohne Kontakt fortfahren".
            allowPersoenlich: matched == null,
            mailAttachments: flowAnhaenge(),
            onCreated: async (result: VorgangCreateResult) => {
                try {
                    if (result.vorgangId) {
                        meldeVerlauf(await bindVerlauf({ vorgangId: result.vorgangId }, snap));
                    } else if (result.todoId) {
                        meldeVerlauf(await bindVerlauf({ todoId: result.todoId }, snap));
                    }
                } catch (e) {
                    reportMailError({
                        area: "mail",
                        action: "link",
                        error: e,
                        fallbackMessage: "Die Mail konnte nicht mit dem Vorgang verknüpft werden.",
                    });
                }
                // Awaited aus demselben Grund wie in openCreateFlowFromMail:
                // erst Archiv-Move + MailLink-Remap, dann Navigation.
                await finishRead();
            },
        };
        if (item.type === "vertrag") {
            flowParams.initialVertrag = { id: item.id, contractNbr: item.contractNbr, contactId: item.contactId };
        } else {
            flowParams.initialRisiko = { id: item.id, name: item.name, contactId: item.contactId };
        }
        openVorgangCreateFlow(openWindow, flowParams);
    };

    // SP4 (State C / D1–D4): nicht erkannter Absender → Zuweisungs-Flow öffnen
    // (Suche + Minimal-Anlage Firma/AP/Person + „ohne Kontakt"). Das Ergebnis ist
    // maßgeblich (explicit) und geht direkt ins Light-Erstell-Popup (#803).
    const openAssignFlowFromMail = (
        standardAbschluss: "zuweisen" | "vorgang" = "zuweisen",
        aktuelleZuweisung: { id: number; displayName: string } | null = null,
    ) => {
        const snap = mailSnapshot();
        openMailContactAssign(openWindow, {
            senderEmail: view.senderEmail ?? email.senderEmail ?? null,
            senderName: view.senderName ?? email.senderName ?? null,
            // B3/B6: der Schnappschuss ist die Grundlage der MailLink-
            // Verknüpfung — getrennt davon, ob die Absenderadresse übernommen
            // wird (bei eigenen Domänen: nie).
            mail: snap,
            aktuelleZuweisung,
            // Cluster ERKENNUNG speist Signatur, Domänen-Firma und den
            // Intern-Zustand ein. Alles optional: fehlt die Erkennung, bleibt
            // die Maske schlicht leer vorbelegt.
            erkennung: {
                signature: recognized?.signature ?? null,
                companyDomain: serverRecognition?.companyDomain ?? null,
                intern: serverRecognition?.status === "internal-only",
            },
            standardAbschluss,
            onResolved: async (e) => {
                // Der Popup-Weg schreibt den Link unten selbst (schliesseZuweisungAb)
                // bzw. der Vorgangsweg bindet ihn an den Vorgang — deshalb hier
                // ohne zweites Persistieren.
                uebernimmZuweisung(e.kontakt, { persistieren: false });
                if (e.weiter === "vorgang") {
                    // Der Flow startet mit der gewählten Person; lief die Wahl
                    // auf „geschäftlich", legt er den Vorgang auf die Firma und
                    // behält die Person als Kontakt (PO 01.10.2026, vorgangStartAus).
                    const start = vorgangStartAus(e);
                    openCreateFlowFromMail(start.kontakt, { explicit: true, vorgangFuer: start.vorgangFuer });
                    return;
                }
                // B5/E03: erst verknüpfen, DANN archivieren. Scheitert das
                // Verknüpfen, wird nicht archiviert und das Fenster bleibt offen.
                const r = await schliesseZuweisungAb(e, {
                    verknuepfeMailMitKontakt: (contactId) =>
                        mailLinkService.createForContact({ ...snap, contactId }),
                    finishRead,
                });
                if (r.fehler) setArchiveError(r.fehler);
            },
        });
    };

    // ── Schadenpostfach (PO 05.10.2026) ─────────────────────────────────────
    // In schaden@ wird eine Mail nicht einem Vorgang zugeordnet, sondern in den
    // Schaden-Ordner der Kundenakte gelegt. Die Leiste bleibt dieselbe; nur die
    // Beschriftung und das Ziel der Vorgangs-Aktionen wechseln.
    const istSchadenPostfach = mailboxes.some(
        (m) => m.schadenAblage === true && m.mailboxAddress.toLowerCase() === mailbox.toLowerCase(),
    );
    const oeffneSchadenAblage = (modus: SchadenAblageModus) => {
        const kontakt =
            selectedCandidate && commandState.kind === "B"
                ? { id: selectedCandidate.contactId, displayName: selectedCandidate.displayName }
                : displayContact
                  ? { id: displayContact.id, displayName: displayContact.displayName }
                  : null;
        openSchadenAblage(openWindow, {
            modus,
            mailbox,
            messageId: email.id,
            betreff: view.subject ?? email.subject ?? null,
            kontakt,
            // Wie beim Zuweisen an einen Vorgang: danach raus aus dem Posteingang
            // und gleich dorthin, wo die Mail jetzt liegt — den Schaden-Ordner
            // der Kundenakte (Kontakt → „Dokumente"). Erst NACH dem Archivieren:
            // das schließt Fenster/Lesepanel; scheitert es, bleibt die Mail mit
            // der Fehlermeldung offen statt wegnavigiert zu werden.
            onAbgelegt: ({ contactId, ordner }) =>
                void finishRead().then((ok) => {
                    if (ok) navigate(schadenOrdnerLink(contactId, ordner));
                }),
        });
    };

    const handleZuweisen = (target: string) => {
        if (istSchadenPostfach) {
            oeffneSchadenAblage(target === "Vorgang" || target === "Todo" ? "zuordnen" : "neu");
            return;
        }
        // An Bestehendes anhängen (D15b) — nur MailLink, kein neuer Entwurf.
        // Vorgänge UND To-dos laufen seit Cluster VORGANG (D3) durch DASSELBE
        // Fenster: der Picker sucht serverseitig über Betreff, Nummer, Kunde
        // und Todo-Titel und lässt beides als Ziel anhaken.
        if (target === "Vorgang" || target === "Todo") {
            oeffneVorgangPicker();
            return;
        }
        // „Neuer Vorgang" aus dem Zuweisen-Menü (Zustand C/D — es gibt keinen
        // erkannten Kontakt): Cluster ZUWEISEN (B5/E8) schickt den Weg zuerst
        // durch das Zuweisen-Popup, dort aber mit dem Vorgang als VORAUSGEWÄHLTEM
        // Ausgang. So lässt sich der Kunde noch bestimmen, ohne dass der
        // Vorgangsweg verschwindet — und „Ohne Kontakt fortfahren" steht daneben.
        openAssignFlowFromMail("vorgang", effektiveZuweisungJetzt());
    };

    const mailSnapshot = () => ({
        mailbox,
        messageId: email.id,
        conversationId: view.conversationId ?? null,
        // Ohne sie entdoppelt der Server zwei Kopien DERSELBEN Mail nicht:
        // `messageId` ist die Graph-`id` und je Ordner verschieden.
        internetMessageId: view.internetMessageId ?? email.internetMessageId ?? null,
        subject: view.subject ?? null,
        senderName: view.senderName ?? null,
        senderEmail: view.senderEmail ?? null,
        receivedAt:
            view.date instanceof Date && !Number.isNaN(view.date.getTime())
                ? view.date.toISOString()
                : null,
        preview: view.preview ?? null,
    });

    /**
     * J2 — EINE Stelle, an der eine getroffene Zuweisung wirksam wird:
     *  1. sie gilt sofort in dieser Ansicht,
     *  2. sie wird gemeldet (Liste + Fenster derselben Konversation),
     *  3. sie wird persistiert (`POST /mail/links/kontakt` ist idempotent —
     *     wo ein anderer Weg den Link schon geschrieben hat, ist der Aufruf
     *     folgenlos), damit sie ein Neuladen überlebt,
     *  4. die Konversations-Links werden neu geholt.
     * Fehler beim Persistieren werden geloggt, aber nicht angezeigt: die Sicht
     * ist trotzdem richtig, nur der Neustart-Fall fiele auf die Erkennung
     * zurück.
     */
    const uebernimmZuweisung = (kontakt: KontaktRef | null, opts?: { persistieren?: boolean }) => {
        setExpliziteZuweisung(kontakt);
        publiziereZuweisung({
            mailbox,
            messageId: email.id,
            conversationId: view.conversationId ?? null,
            kontakt,
        });
        if (kontakt && opts?.persistieren !== false) {
            mailLinkService
                .createForContact({ ...mailSnapshot(), contactId: kontakt.id })
                .catch((e) => reportMailError({
                    area: "mail",
                    action: "link",
                    error: e,
                    fallbackMessage: "Die Kontakt-Verknüpfung konnte nicht gespeichert werden.",
                }));
        }
        setConvLinksTick((t) => t + 1);
    };

    /**
     * „zuweisen mehrere Firmen/Kontakte bei Mails" (PO 28.09.2026): dieselbe
     * Nachricht zusätzlich einem WEITEREN Kontakt zuweisen — etwa der Firma UND
     * ihrem Ansprechpartner oder zwei beteiligten Kunden. Der Hauptkontakt
     * bleibt, die Mail bleibt, wo sie ist (kein Archivieren, kein Schließen).
     * Das Backend kennt das längst: je Kontakt ein MailLink.
     */
    const oeffneWeiterenKontakt = () => {
        const snap = mailSnapshot();
        openMailContactAssign(openWindow, {
            senderEmail: view.senderEmail ?? email.senderEmail ?? null,
            senderName: view.senderName ?? email.senderName ?? null,
            mail: snap,
            aktuelleZuweisung: zuweisungJ2.kontakt,
            erkennung: {
                signature: recognized?.signature ?? null,
                companyDomain: serverRecognition?.companyDomain ?? null,
                intern: serverRecognition?.status === "internal-only",
            },
            modus: "zusaetzlich",
            onResolved: async (e) => {
                const ziele = [e.kontakt, e.firma].filter(
                    (k, i, alle): k is KontaktRef => !!k && alle.findIndex((x) => x?.id === k.id) === i,
                );
                try {
                    // Neu angelegte Firma samt Ansprechpartner: beide bekommen die Mail.
                    for (const k of e.angelegt.includes("firma") ? ziele : ziele.slice(0, 1)) {
                        await mailLinkService.createForContact({ ...snap, contactId: k.id });
                    }
                } catch (err) {
                    setArchiveError(reportMailError({
                        area: "mail",
                        action: "link",
                        error: err,
                        fallbackMessage: "Die Mail konnte dem weiteren Kontakt nicht zugewiesen werden.",
                    }));
                }
                setConvLinksTick((t) => t + 1);
            },
        });
    };

    /** Eine zusätzliche Kontakt-Zuweisung wieder lösen (nur die Verknüpfung). */
    const entferneKontaktZuweisung = (linkId: number) => {
        void mailLinkService
            .remove(linkId)
            .then(() => setConvLinksTick((t) => t + 1))
            .catch((err: unknown) => setArchiveError(reportMailError({
                area: "mail",
                action: "link",
                error: err,
                fallbackMessage: "Die Zuweisung konnte nicht entfernt werden.",
            })));
    };

    // ── DER EINE MailLink-Schreibpfad (P1-B) ────────────────────────────────
    // „An Bestehendes anhängen" (assignZiele), der Bind nach frischer Anlage
    // und [Bestätigen] laufen über diesen Helper → identische Snapshot-Logik,
    // kein zweiter Schreibpfad mit driftenden Feldern.
    //
    // Seit 09/2026 hängt dabei der GANZE VERLAUF am Ziel, nicht nur die
    // geöffnete Nachricht: Eine Zuweisung gilt dem Schriftwechsel. Endpoint ist
    // deshalb `POST /mail/links/batch` (mailLinkService.createBatch) — er
    // dedupt serverseitig, archiviert die Posteingangs-Nachrichten des Threads
    // und zieht die dabei neu vergebenen Message-Ids nach.
    //
    // Die geöffnete Mail selbst archiviert weiterhin `finishRead` (siehe
    // `mailVerlaufItems`), damit Fenster-Schließen und Listen-Auffrischung
    // unverändert an EINER Stelle hängen.
    //
    // `offen` ist überschreibbar, weil die Create-Flows ihren Snapshot schon
    // beim Öffnen ziehen — die Mail kann bis zum `onCreated` längst archiviert
    // und umgeschlüsselt sein.
    const bindVerlauf = (
        target: { vorgangId?: number; todoId?: number },
        offen: MailLinkBatchItemInput = mailSnapshot(),
        // T-29: Ohne aufgefächerten Verlauf sieht niemand, dass der ganze
        // Schriftwechsel mitgeht — dann ausdrücklich sagen, als Hinweis in
        // KMVS (globaler Kanal, überlebt das Schließen des Fensters) statt
        // als Browser-Meldung. Der Rückgabewert trägt nur noch Probleme.
        meldeMitgenommene: ((text: string) => void) | undefined = singleMessage
            ? (text) => zeigeGlobalenHinweis(text)
            : undefined,
    ): Promise<string | null> =>
        bindeVerlaufAnZiel({
            mailbox,
            offen,
            conversationId: view.conversationId ?? email.conversationId ?? null,
            ziel: target,
            verlauf: convo,
            istImPosteingang: posteingangsPruefung(folderTrees, mailbox),
            meldeMitgenommene,
        });

    /** Sagt dem Nutzer, was am Verlauf nicht glattging — sonst nichts. */
    const meldeVerlauf = (text: string | null): void => {
        // Bewusst ein Hinweis-Popup (app-weit, nicht im Fenster): `finishRead`
        // schließt dieses Fenster unmittelbar danach, eine Notiz darin läse
        // niemand mehr. Der Fall ist selten
        // (Teilfehler im Verlauf oder eine gekappte Überlänge) und zu wichtig,
        // um ihn still zu verschlucken — dieselbe Wahl wie im
        // Mail-hinzufügen-Dialog des Vorgangs.
        // Gilt nur noch für Neuanlage und [Bestätigen]; „Bestehendem Vorgang
        // zuordnen" meldet seit T-29 per Hinweis (`mailZuordnenHinweis`).
        if (text) void hinweisen({ titel: "Hinweis zum Mailverlauf", text });
    };

    /**
     * Eine Mail an MEHRERE Ziele hängen (PO 26.07.2026, Punkt 11; seit
     * Cluster VORGANG D3 auch an einzelne To-dos).
     *
     * Reihenfolge ist hier tragend: ERST alle Links schreiben, DANN archivieren.
     * `finishRead()` verschiebt die Nachricht, und dabei vergibt Graph eine neue
     * messageId — ein Archivieren mitten in der Schleife ließe die restlichen
     * Links auf die alte Id zeigen (vgl. das messageId-Remapping in
     * mail-link.service).
     */
    const assignZiele = async (
        ziele: MailVorgangZiel[],
        navigationVorgangId: number | null,
    ): Promise<void> => {
        // T-29: Dieser Weg meldet nicht mehr per `window.alert`, sondern mit
        // EINEM Hinweis in KMVS — mitgenommene Mails (Einzelansicht) und
        // etwaige Probleme zusammen (`mailZuordnenHinweis`).
        const mitgenommen: string[] = [];
        let probleme: string | null = null;
        // T-236: Die Datei-Anhänge wandern von selbst mit (Mail-Ablage), die
        // Bilder aus dem Mailtext nicht — danach wird gefragt. Und zwar VOR dem
        // Zuordnen: beim Archivieren vergibt Graph eine neue messageId.
        // T-287: mit Vorschau und Einzelauswahl.
        const bilder = await frageBilderAuswahl(
            vorgangsBilder,
            attachments.length,
            "zuordnen",
            (b) => graphMailService.fetchAttachmentBlob(mailbox, b.messageId, b.id),
            (b) => bildDateiname(b.name, b.contentType, bildDatum),
            { groesse: (b) => b.size },
        );
        const mitBildern = bilder.length > 0;
        const uebernimmBilder = async (gebunden: MailVorgangZiel[]) => {
            try {
                await uebernimmBilderInZiele({ mailbox, bilder, datum: bildDatum, ziele: gebunden });
            } catch (err) {
                probleme = [
                    probleme,
                    reportMailError({
                        area: "mail",
                        action: "save-attachment",
                        error: err,
                        fallbackMessage: "Die Bilder aus dem Mailtext konnten nicht in den Vorgang übernommen werden.",
                    }),
                ]
                    .filter(Boolean)
                    .join(" ");
            }
        };
        try {
            await assignMailToZiele(ziele, {
                vorArchivieren: mitBildern ? uebernimmBilder : undefined,
                bindVerlauf: (target) =>
                    bindVerlauf(
                        target,
                        mailSnapshot(),
                        singleMessage ? (text) => mitgenommen.push(text) : undefined,
                    ),
                finishRead,
                melde: (text) => {
                    probleme = text;
                },
            });
        } finally {
            meldeZuordnenHinweis(mitgenommen, probleme);
        }
        // E01/J2: Die Mail hängt jetzt an einem Vorgang und gehört damit auch
        // zu dessen Kunden. Der Link ist geschrieben — hier wird nur gemeldet,
        // damit Liste und offene Fenster derselben Konversation mitziehen.
        if (zuweisungJ2.kontakt) uebernimmZuweisung(zuweisungJ2.kontakt, { persistieren: false });
        if (navigationVorgangId != null) {
            // Das Ziel soll sichtbar werden: ein schwebendes Mailfenster wird
            // minimiert, das eingebettete Lesepanel geleert.
            if (windowId) minimizeWindow(windowId);
            else onCloseRequest?.();
            navigate(`/vorgaenge/${navigationVorgangId}`);
        }
    };

    /**
     * D1: Der Picker braucht den erkannten Kontakt — er bestimmt die erste
     * Rangstufe („Vorgänge dieses Kontakts"). Ohne Kontakt beginnt die Liste
     * bei Stufe 2, ohne Fehlermeldung.
     */
    const oeffneVorgangPicker = () =>
        istSchadenPostfach
            ? oeffneSchadenAblage("zuordnen")
            : openMailVorgangPicker(openWindow, {
                  contactId: displayContact?.id ?? null,
                  onAssign: assignZiele,
                  // T-34: per Knopf als Betreff eines neuen To-dos übernehmbar.
                  mailBetreff: view.subject ?? email.subject ?? null,
              });

    // ── SP2: Aktionen der Command-Leisten-States ────────────────────────────
    // State B: effektiv ausgewählter Kandidat — ohne Klick gilt der Default
    // (erste Firma, B2); ein gewählter Kandidat, der nach einem Reload nicht
    // mehr existiert, fällt ebenfalls auf den Default zurück.
    const selectedCandidate: MailSenderCandidate | null = React.useMemo(() => {
        if (commandState.kind !== "B") return null;
        const effectiveId =
            selectedCandidateId != null &&
            commandState.candidates.some((c) => c.contactId === selectedCandidateId)
                ? selectedCandidateId
                : commandState.defaultContactId;
        return commandState.candidates.find((c) => c.contactId === effectiveId) ?? null;
    }, [commandState, selectedCandidateId]);

    /**
     * Cluster ZUWEISEN (B5) — die wirksame Zuweisung im Moment des Klicks.
     * Eine Quelle für Popup-Vorbelegung, C9-Leiste und (Cluster SEITENLEISTE)
     * die rechte Leiste: die explizite Zuweisung schlägt die Kandidatenauswahl.
     */
    const effektiveZuweisungJetzt = () =>
        effektiveZuweisung(
            zuweisung,
            selectedCandidate,
            commandState.kind === "B" ? commandState.candidates : [],
        );

    /**
     * C9 — eine gefundene Nummer/Adresse gezielt zuordnen. Der Bezug kommt aus
     * DERSELBEN Quelle wie die Anzeige (`effektiveZuweisungJetzt`); ohne Kontakt
     * gibt es nichts zu tun, dann erscheint die Leiste gar nicht erst.
     */
    const oeffneNummerZuordnen = (entry: FehlenderEintrag) => {
        const kontakt = effektiveZuweisungJetzt() ?? displayContact;
        if (!kontakt) return;
        // Firmenbezug: Ist der Kontakt selbst die Firma, ist er sein eigener
        // Firmenbezug; ist er ein AP, steht die Firma im Kandidaten (viaCompanyId).
        const kandidat = senderCandidates.find((c) => c.contactId === kontakt.id);
        const apFirma =
            kandidat?.kind === "ap" && kandidat.viaCompanyId != null
                ? senderCandidates.find(
                      (c) => c.kind === "firma" && c.contactId === kandidat.viaCompanyId,
                  ) ?? null
                : null;
        const firma =
            kandidat?.kind === "firma"
                ? { id: kandidat.contactId, displayName: kandidat.displayName }
                : apFirma
                  ? { id: apFirma.contactId, displayName: apFirma.displayName }
                  : kandidat?.kind === "ap" && kandidat.viaCompanyId != null
                    ? { id: kandidat.viaCompanyId, displayName: kandidat.viaCompanyName ?? "Firma" }
                    : null;
        openMailNummerZuordnen(openWindow, {
            mailbox,
            messageId: email.id,
            eintrag: entry,
            bezug: { kontakt, firma },
            onErledigt: ({ uebernommen }) => {
                // In beiden Fällen ist die Fundstelle für diese Sitzung erledigt:
                // übernommen wurde sie oder bewusst vertagt.
                merkeSpaeter(mailbox, email.id, [entry.value]);
                setNummerSpaeter(ladeSpaeter(mailbox, email.id));
                if (uebernommen) setArchiveError(null);
            },
        });
    };

    /** „Später" für ALLE offenen Fundstellen dieser Mail. */
    const stelleKontaktdatenZurueck = () => {
        merkeSpaeter(mailbox, email.id, offeneKontaktdaten.map((e) => e.value));
        setNummerSpaeter(ladeSpaeter(mailbox, email.id));
    };

    // Cluster ZUWEISEN (B4): Ein Klick auf einen Erkennungs-Chip öffnet IMMER
    // das Zuweisen-Popup — vorbelegt mit genau diesem Kandidaten als „Aktuell
    // zugewiesen".
    //
    // Vorher wechselte der erste Klick nur die Auswahl und erst ein ZWEITER
    // öffnete den Erstell-Flow; die Zuweisung ändern ging allein über den
    // Zusatz-Button „anders zuweisen", und den gab es nur an einem AP mit
    // eigenem Status. Bei jedem Chip geht es aber um dieselbe Frage: haben wir
    // den richtigen Kontakt?
    //
    // B1 (Review 2026-07-06): ist der Kandidat der AP einer Firma, ist der
    // Vorgang-Kontakt die FIRMA (resolveCandidateAssignment) — nur eine reine
    // Person bleibt Person. Das AP-Badge bleibt zur Info sichtbar.
    const handleCandidateClick = (candidate: MailSenderCandidate) => {
        if (commandState.kind !== "B") return;
        // Die Auswahl BLEIBT — „Abbrechen" im Popup lässt sie stehen, verknüpft
        // und archiviert aber nichts.
        setSelectedCandidateId(candidate.contactId);
        openAssignFlowFromMail(
            "zuweisen",
            resolveCandidateAssignment(candidate, commandState.candidates),
        );
    };

    // State A: Ziel-Vorgänge des [Bestätigen] — angehakte ids (Default: alle),
    // client-dedupet gegen bereits bestehende Links (L6, messageId+vorgangId).
    const confirmTargets = React.useMemo(() => {
        if (commandState.kind !== "A") return [] as number[];
        const checked = checkedVorgangIds ?? commandState.vorgaenge.map((v) => v.id);
        return buildConfirmTargets(
            checked,
            convLinks ?? [],
            email.id,
            email.internetMessageId ?? full?.internetMessageId ?? null,
        );
    }, [commandState, checkedVorgangIds, convLinks, email.id, email.internetMessageId, full]);

    // [Bestätigen] (User-Entscheidung #25): ordnet die Mail dem/den
    // Konversations-Vorgängen zu (je Ziel EIN MailLink über denselben
    // Schreibpfad wie „An Bestehendes anhängen") UND archiviert sie.
    const handleConfirmSuggestion = async () => {
        if (confirmTargets.length === 0) return;
        setConfirming(true);
        setConfirmError(null);
        try {
            const meldungen: string[] = [];
            for (const input of buildConfirmLinkInputs(mailSnapshot(), confirmTargets)) {
                const { vorgangId, ...offen } = input;
                const meldung = await bindVerlauf({ vorgangId }, offen);
                if (meldung && !meldungen.includes(meldung)) meldungen.push(meldung);
            }
            meldeVerlauf(meldungen.length ? meldungen.join(" ") : null);
            // E01/J2: gehört jetzt zum Vorgang — und damit zu dessen Kunden.
            if (zuweisungJ2.kontakt) uebernimmZuweisung(zuweisungJ2.kontakt, { persistieren: false });
            await finishRead();
        } catch (e: unknown) {
            setConfirmError(reportMailError({
                area: "mail",
                action: "assign",
                error: e,
                fallbackMessage: "Die Zuordnung konnte nicht abgeschlossen werden.",
            }));
        } finally {
            setConfirming(false);
        }
    };

    // State A, mehrere Vorgänge: Checkbox-Toggle (Default: alle angehakt).
    const toggleVorgangChecked = (vorgangId: number, checked: boolean) => {
        if (commandState.kind !== "A") return;
        const base = checkedVorgangIds ?? commandState.vorgaenge.map((v) => v.id);
        setCheckedVorgangIds(
            checked ? Array.from(new Set([...base, vorgangId])) : base.filter((id) => id !== vorgangId),
        );
    };

    /**
     * T-195 — Drucken: druckt, was das Fenster zeigt. Einzelmail → ihr Text;
     * Verlauf → je Nachricht Kopf + Text (offene, geladene Karte) bzw. Kopf +
     * Vorschauzeile (zugeklappt/noch nicht geladen). Externe Inhalte folgen der
     * Anzeige (`showRemote`), Anhänge stehen nur als Liste darunter.
     */
    const handleDrucken = () => {
        const anhaenge = attachments.map((a) => ({ name: a.name, groesse: a.size }));
        const vonEmail = (e: Email) => ({ name: e.senderName, address: e.senderEmail });
        let nachrichten: DruckNachricht[];
        if (convo && convo.length > 1) {
            nachrichten = convo.map((msg) => {
                const voll = expandedIds.has(msg.id) ? bodies[msg.id] : undefined;
                const quelle = voll ?? msg;
                return {
                    betreff: quelle.subject || view.subject,
                    von: vonEmail(quelle),
                    an: quelle.to ?? [],
                    cc: quelle.cc ?? [],
                    datum: quelle.date,
                    bodyHtml: voll?.body || null,
                    vorschau: msg.preview,
                };
            });
            if (nachrichten.length > 0) nachrichten[nachrichten.length - 1].anhaenge = anhaenge;
        } else if (view.thread && view.thread.length > 0) {
            nachrichten = view.thread.map((msg, i) => ({
                betreff: view.subject,
                von: { name: msg.from },
                an: i === 0 ? view.to ?? [] : [],
                cc: i === 0 ? view.cc ?? [] : [],
                datum: msg.date,
                bodyHtml: msg.body,
            }));
            nachrichten[nachrichten.length - 1].anhaenge = anhaenge;
        } else {
            nachrichten = [
                {
                    betreff: view.subject,
                    von: vonEmail(view),
                    an: view.to ?? [],
                    cc: view.cc ?? [],
                    datum: view.date,
                    bodyHtml: view.body || null,
                    vorschau: email.preview,
                    anhaenge,
                },
            ];
        }
        druckeHtml(baueDruckDokument(nachrichten, { blockRemote: !showRemote })).catch((err) => {
            const text = reportMailError({
                area: "mail",
                action: "print",
                error: err,
                fallbackMessage: "Die Mail konnte nicht gedruckt werden.",
            });
            void hinweisen({ titel: "Drucken nicht möglich", text });
        });
    };

    /**
     * Herunterladen (User 26.10.2026): die geöffnete Mail als `.eml`, wie Outlook
     * sie speichert — mit allen Anhängen. Neben „Drucken", weil beides „die Mail
     * mitnehmen" ist.
     */
    const [laedtHerunter, setLaedtHerunter] = React.useState(false);
    const handleHerunterladen = () => {
        setLaedtHerunter(true);
        graphMailService
            .fetchEmlBlob(mailbox, email.id)
            .then((blob) => speichereMail(blob, view.subject))
            .catch((err) => {
                const text = reportMailError({
                    area: "mail",
                    action: "download-mail",
                    error: err,
                    fallbackMessage: "Die Mail konnte nicht heruntergeladen werden.",
                });
                void hinweisen({ titel: "Herunterladen nicht möglich", text });
            })
            .finally(() => setLaedtHerunter(false));
    };

    /**
     * H5 — „Regel erstellen…" aus dem Lesefenster (Cluster REGELN).
     *
     * Der Eintrag steht in BEIDEN Zweigen der Command-Leiste, nicht im
     * „Vorgang"-Menü: das hängt an `showZuweisenMenu(commandState.kind)`, und
     * das ist `kind !== 'B'` — also unsichtbar genau im Normalfall „Absender
     * erkannt", in dem man eine Regel bauen will.
     */
    const regelMenuItem = (
        <MenuItem
            icon={<Filter20Regular />}
            onClick={() =>
                openMailRegelWindow(openWindow, {
                    vorbelegung: {
                        senderEmail: view.senderEmail,
                        subject: view.subject,
                        mailbox,
                    },
                })
            }
        >
            Regel erstellen …
        </MenuItem>
    );

    /**
     * T-180 (28.10.2026): „Einem Schaden zuordnen…" in JEDEM Postfach — die
     * Mail samt Anhängen in einen Schaden-Ordner der Kundenakte, wie in
     * schaden@. Dort entfällt der Eintrag: die Vorgangszeilen sind schon die
     * Schaden-Wege. Ohne eigenes Recht am Postfach (Mail nur über den Vorgang
     * lesbar) auch: die Ablage prüft das Leserecht am Postfach und scheiterte.
     */
    const schadenZuordnenItem = istSchadenPostfach || !darfArchivieren ? null : (
        <MenuItem icon={<FolderOpen20Regular />} onClick={() => oeffneSchadenAblage("zuordnen")}>
            Einem Schaden zuordnen…
        </MenuItem>
    );

    /**
     * T-22: Wessen Adressen das Verfassen-Fenster beim Klick in An/Cc/Bcc
     * vorschlägt. „In einem Vorgang" zählt dessen Kunde — nicht der erkannte
     * Absender (bei cbb@-Mails meist leer). Ohne Vorgang der zugewiesene
     * Kontakt, sonst keiner.
     */
    const empfaengerKontextFuer = (messageId: string): number | null =>
        vorgangsKundeAus(convLinks, messageId)?.id ?? zuweisungJ2.kontakt?.id ?? null;

    /**
     * Zuweisen beim Antworten (User 02.11.2026): Hängt die Konversation an
     * keinem Vorgang, bietet das Antwort-Fenster das Zuordnen an — Kontakt
     * (falls noch keiner zugewiesen ist) und/oder Vorgang/To-do. Nicht im
     * Schadenpostfach: dort wird abgelegt, nicht zugeordnet.
     */
    const zuordnungsAngebotFuer = (target: Email): AntwortZuordnungAngebot | undefined => {
        if (istSchadenPostfach || !bieteZuordnungAn(convLinks)) return undefined;
        const zugewiesen =
            zuweisungJ2.quelle === "link" || zuweisungJ2.quelle === "explizit" || zuweisungJ2.quelle === "vorgang"
                ? zuweisungJ2.kontakt
                : null;
        return {
            ursprung: {
                mailbox,
                messageId: target.id,
                conversationId: target.conversationId ?? view.conversationId ?? null,
                internetMessageId: target.internetMessageId ?? null,
                subject: target.subject ?? null,
                senderName: target.senderName ?? null,
                senderEmail: target.senderEmail ?? null,
                receivedAt:
                    target.date instanceof Date && !Number.isNaN(target.date.getTime())
                        ? target.date.toISOString()
                        : null,
                preview: target.preview ?? null,
            },
            senderEmail: target.senderEmail ?? null,
            senderName: target.senderName ?? null,
            erkannterKontakt: displayContact ? { id: displayContact.id, displayName: displayContact.displayName } : null,
            kontaktZugewiesen: zugewiesen,
        };
    };

    // A3 — „Antworten": echtes Threading via `replyToMessageId` (→ Graph
    // `/messages/{id}/reply`). Vorher landete die Antwort als NEUE Mail (P0-1).
    // SP4: `target` erlaubt Antworten auf EINE bestimmte Nachricht der
    // Konversation (Abschnitts-Aktionen); Default bleibt die Haupt-/neueste Mail.
    /**
     * T-212 (Teil B) — liegt zu dieser Mail schon ein Antwort-Entwurf (im
     * Ordner „Entwürfe" dieses Postfachs), setzt „Antworten" ihn fort, statt
     * einen zweiten anzulegen — wie in Outlook.
     */
    const entwurfFortsetzen = () => {
        if (!antwortEntwurf) return;
        requestClose();
        openEntwurfWindow(openWindow, { mailbox, draftId: antwortEntwurf.draftId, titel: view.subject });
    };

    const handleReply = (target: Email = view) => {
        if (antwortEntwurf) {
            entwurfFortsetzen();
            return;
        }
        const quote = buildReplyQuote(target);
        requestClose();
        openComposeWindow(openWindow, {
            mailbox,
            to: target.senderEmail ? [target.senderEmail] : [],
            subject: `Re: ${target.subject ?? ""}`,
            body: quote,
            mode: "reply",
            contactId: displayContact?.id ?? null,
            // T-22: Empfänger-Vorschläge zum Kunden des Vorgangs; ohne Vorgang
            // zum zugewiesenen Kontakt.
            empfaengerKontextId: empfaengerKontextFuer(target.id),
            // T-225/T-256: Vorgang der Nachricht — nur Kontext (Dokumente in
            // der Auswahl, Kontakte für die Vorschläge), verknüpft nichts.
            bezugsVorgangId: vorgangAus(convLinks, target.id)?.id ?? null,
            replyToMessageId: target.id,
            // PO 26.07.2026 (Punkt 14): die Antwort erbt den Vorgangsbezug der
            // Ursprungsnachricht — automatisch, ohne Rückfrage.
            inheritLinksFromMessageId: target.id,
            // Fundort der Ursprungsnachricht (2026-07-29). Getrennt vom Absender:
            // Wer die Mail nur über den Vorgang lesen darf, sendet aus dem
            // EIGENEN Postfach — das Original liegt aber weiter hier.
            replySourceMailbox: mailbox,
            antwortZuordnung: zuordnungsAngebotFuer(target),
        });
    };

    // A3 — „Allen antworten": Absender → To, Original-To+Cc → Cc (eigene Adresse
    // und Absender-Dublette raus, Original-BCC nie). Echtes Threading wie Reply.
    const handleReplyAll = (target: Email = view) => {
        if (antwortEntwurf) {
            entwurfFortsetzen();
            return;
        }
        const { to, cc } = buildReplyAllRecipients(target, mailbox);
        const quote = buildReplyQuote(target);
        requestClose();
        openComposeWindow(openWindow, {
            mailbox,
            to,
            cc,
            subject: `Re: ${target.subject ?? ""}`,
            body: quote,
            mode: "replyAll",
            contactId: displayContact?.id ?? null,
            // T-22: Empfänger-Vorschläge zum Kunden des Vorgangs; ohne Vorgang
            // zum zugewiesenen Kontakt.
            empfaengerKontextId: empfaengerKontextFuer(target.id),
            // T-225/T-256: Vorgang der Nachricht — nur Kontext (Dokumente in
            // der Auswahl, Kontakte für die Vorschläge), verknüpft nichts.
            bezugsVorgangId: vorgangAus(convLinks, target.id)?.id ?? null,
            replyToMessageId: target.id,
            inheritLinksFromMessageId: target.id,
            replySourceMailbox: mailbox,
            antwortZuordnung: zuordnungsAngebotFuer(target),
        });
    };

    // A3 — „Weiterleiten": leere Empfänger (User adressiert frei), „Fwd: …",
    // gequoteter Body. BEWUSST KEIN `replyToMessageId` → neue Mail via `/sendMail`
    // (kein Thread-Reply an den Original-Absender) und volle RBAC-Prüfung der frei
    // eingegebenen Empfänger (keine Allow-List).
    const handleForward = (target: Email = view) => {
        const quote = buildReplyQuote(target);
        // Datei-Anhänge der Ursprungsnachricht übernehmen (nur Metadaten — die
        // Bytes holt der Server beim Senden). Eingebettete Bilder bleiben
        // bewusst draußen: Die stecken bereits als data:-URI im zitierten Text
        // und hingen sonst ein zweites Mal an der Mail.
        //
        // In einer Konversation trägt jeder Anhang die Id SEINER Nachricht
        // (von `fetchAttachments` gestempelt). Wer eine einzelne Nachricht aus
        // dem Verlauf weiterleitet, bekommt deshalb nur deren eigene Anhänge —
        // nicht die des ganzen Threads.
        const eigeneAnhaenge = attachments.filter(
            (a) => !a.isInline && (!a.messageId || a.messageId === target.id),
        );
        requestClose();
        openComposeWindow(openWindow, {
            mailbox,
            to: [],
            subject: `Fwd: ${target.subject ?? ""}`,
            body: quote,
            mode: "forward",
            inheritedAttachments: eigeneAnhaenge,
            // Absicherung gegen das Wettrennen mit dem Laden der Anhangsliste:
            // Wer sofort nach dem Öffnen auf „Weiterleiten" klickt, hat sie
            // vielleicht noch nicht — dann holt das Verfassen-Fenster sie selbst
            // nach. Ohne das gingen genau in diesem Moment die Anhänge still
            // verloren, und das fällt erst beim Empfänger auf.
            inheritAttachmentsFrom:
                target.hasAttachments && eigeneAnhaenge.length === 0
                    ? { mailbox, messageId: target.id }
                    : undefined,
            contactId: displayContact?.id ?? null,
            // T-22: Empfänger-Vorschläge zum Kunden des Vorgangs; ohne Vorgang
            // zum zugewiesenen Kontakt.
            empfaengerKontextId: empfaengerKontextFuer(target.id),
            // T-225/T-256: Vorgang der Nachricht — nur Kontext (Dokumente in
            // der Auswahl, Kontakte für die Vorschläge), verknüpft nichts.
            bezugsVorgangId: vorgangAus(convLinks, target.id)?.id ?? null,
            // Kein `replyToMessageId` (siehe oben), aber der Vorgangsbezug soll
            // trotzdem mitkommen — dafür ist dieses Feld bewusst getrennt.
            inheritLinksFromMessageId: target.id,
            // Auch beim Weiterleiten aus fremdem Postfach: die Konversation der
            // Ursprungsnachricht wird dort aufgelöst, nicht im Absender-Postfach
            // (2026-07-29 — vorher ging der Vorgangsbezug dabei verloren).
            replySourceMailbox: mailbox,
            antwortZuordnung: zuordnungsAngebotFuer(target),
        });
    };

    // „Neuer Vorgang" (CR-SP5): führt durch den zweistufigen Flow — zuerst die
    // Zuweisung (Schritt 1), dann das Erstellen; der MailLink-Bind läuft im
    // onCreated des Flows. SP2 (B3/B4): im State B ist der ausgewählte
    // Erkennungs-Kandidat die vorbelegte Zuweisung — und zwar GENAU er
    // (PO 28.09.2026: „für eine Person, die zu einer Firma gehört, steht bei
    // Kontakt die Firma, obwohl ich ja die Person ausgewählt habe"). Bis dahin
    // bildete B1 einen AP-Kandidaten hier auf seine Firma ab. Die Zuweisung der
    // MAIL (resolveCandidateAssignment) bleibt davon unberührt.
    const handleNeuerVorgang = () =>
        istSchadenPostfach
            ? oeffneSchadenAblage("neu")
            : openCreateFlowFromMail(
            selectedCandidate && commandState.kind === "B"
                ? { id: selectedCandidate.contactId, displayName: selectedCandidate.displayName }
                : undefined,
        );

    // Die Leiste wird erst „scharf", wenn BEIDE Quellen durch sind: der
    // Link-Lookup (sonst flackert die Warnung, weil deriveMailCommandState
    // solange mit links=[] läuft) UND die Erkennung (sonst flackert der
    // Zustandswechsel). Das ist ein ZEITPUNKT, kein Zustand — deshalb steht er
    // hier im Bauteil und nicht in der State-Machine.
    const barReady = convLinks !== null && recognitionSettled;

    // PO Runde 3: Die „An:"-Zeile zeigt Name UND Adresse — wie „Von:". Damit
    // eine Rundmail den Kopf nicht flutet, stehen eingeklappt nur die ersten
    // Empfänger da; der Rest kommt über denselben Schalter-Weg wie Cc.
    const toEmpfaenger = recipientEntries(view.to);
    const toKompakt = splitRecipients(toEmpfaenger);
    const toSichtbar = toOpen ? toEmpfaenger : toKompakt.shown;

    // Handy: die erkannte Signatur eines (sonst) unerkannten Absenders — der
    // Kontakt-Knopf und „Mehr" bieten sie an („Als Kontakt anlegen").
    const signaturAngebot =
        kompakt && barReady && showUnrecognizedWarning(commandState)
            ? signaturHinweis(recognized?.signature ?? null, false)?.replace(/^Signatur erkannt:\s*/, "") ?? null
            : null;

    return (
        <div className={styles.root}>
            {bildMenue && (
                <BildMenue
                    position={{ x: bildMenue.bild.x, y: bildMenue.bild.y }}
                    onVorschau={() => oeffneBild(bildMenue.bild, bildMenue.messageId, bildMenue.anhang ?? null)}
                    onSpeichernUnter={() => void speichereBildUnter(bildMenue.bild, bildMenue.anhang ?? null)}
                    onBeimKontaktSpeichern={
                        saveTargetContact && bildMenue.anhang
                            ? () => void speichereBildBeimKontakt(bildMenue.anhang as MailAttachment)
                            : undefined
                    }
                    kontaktName={saveTargetContact?.displayName ?? null}
                    onSchliessen={schliesseBildMenue}
                />
            )}
            {markierung && (
                <MarkierungsMenue
                    text={markierung.text}
                    position={{ x: markierung.x, y: markierung.y }}
                    modus={markierung.modus}
                    onAnlegen={(t) => void oeffneMarkierungAlsKontakt(t)}
                    onSchliessen={schliesseMarkierung}
                />
            )}
            <div className={mergeClasses(styles.contentCol, kompakt && styles.contentColScroll)}>
            {/* Archivieren fehlgeschlagen — sichtbar statt nur in der Konsole
                (2026-07-29). Das Fenster bleibt in diesem Fall offen. */}
            {archiveError && (
                <div
                    role="alert"
                    style={{
                        padding: "8px 12px",
                        fontSize: 12,
                        color: "#a4262c",
                        background: "#fdf3f4",
                        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                    }}
                >
                    <span style={{ flexGrow: 1 }}>⚠ {archiveError}</span>
                    <Button appearance="transparent" size="small" onClick={() => setArchiveError(null)}>
                        Schließen
                    </Button>
                </div>
            )}
            {/* Cluster ABLAGE (E11): Die Mail IST archiviert, aber nicht
                dauerhaft gesichert. Der Hinweis verlangt eine Kenntnisnahme —
                „Verstanden" schließt das Fenster wie ein normales Archivieren. */}
            {archiveNote && (
                <div
                    role="alert"
                    style={{
                        padding: "8px 12px",
                        fontSize: 12,
                        color: "#8a5b12",
                        background: "#fbf1e3",
                        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                    }}
                >
                    <span style={{ flexGrow: 1 }}>⚠ {archiveNote}</span>
                    <Button
                        appearance="transparent"
                        size="small"
                        onClick={() => {
                            setArchiveNote(null);
                            onArchived?.(email.id);
                            requestClose();
                        }}
                    >
                        Verstanden
                    </Button>
                </div>
            )}
            {/* Handy: EINE Reihe gleich großer Knöpfe statt der Leiste darunter. */}
            {kompakt && (
                <MailKopfAktionen
                    onAntworten={() => handleReply()}
                    onAllenAntworten={() => handleReplyAll()}
                    onWeiterleiten={() => handleForward()}
                    onArchivieren={darfArchivieren ? () => void finishRead() : undefined}
                    vorgangLabel={istSchadenPostfach ? "Schaden" : "Vorgang"}
                    vorgang={[
                        { label: istSchadenPostfach ? "Neuer Schaden" : "Neuer Vorgang", icon: <Add20Regular />, onClick: handleNeuerVorgang },
                        {
                            label: istSchadenPostfach ? "Einem bestehenden Schaden zuordnen" : "Bestehendem Vorgang zuordnen",
                            icon: <TaskListSquareLtr20Regular />,
                            onClick: () => (commandState.kind === "B" ? oeffneVorgangPicker() : handleZuweisen("Vorgang")),
                        },
                        ...(istSchadenPostfach ? [] : [{ label: "An To-Do anhängen", onClick: () => handleZuweisen("Todo") }]),
                        ...(schadenZuordnenItem
                            ? [{ label: "Einem Schaden zuordnen…", icon: <FolderOpen20Regular />, onClick: () => oeffneSchadenAblage("zuordnen") }]
                            : []),
                    ]}
                    onKontaktZuweisen={() =>
                        // Mit erkannter Signatur (und ohne Erkennung) wie früher „Als
                        // Kontakt anlegen": der Zuweisen-Ablauf ist damit vorbelegt.
                        signaturAngebot ? openAssignFlowFromMail("zuweisen") : openAssignFlowFromMail("zuweisen", effektiveZuweisungJetzt())
                    }
                    weitere={[
                        [
                            ...(signaturAngebot
                                ? [{ label: `Als Kontakt anlegen – ${signaturAngebot}`, icon: <PersonAdd20Regular />, onClick: () => openAssignFlowFromMail("zuweisen") }]
                                : []),
                            ...(zuweisungJ2.kontakt && zuweisungJ2.quelle !== "erkannt"
                                ? [{ label: "Weiterer Kontakt …", icon: <PersonAdd20Regular />, onClick: oeffneWeiterenKontakt }]
                                : []),
                            {
                                label: "Regel erstellen …",
                                icon: <Filter20Regular />,
                                onClick: () =>
                                    openMailRegelWindow(openWindow, {
                                        vorbelegung: { senderEmail: view.senderEmail, subject: view.subject, mailbox },
                                    }),
                            },
                        ],
                        // Die Bereiche der Leiste (auf dem Handy ohne Klickleiste am Rand).
                        onReiterWechsel
                            ? [
                                  { label: "Kontakt & Schriftwechsel", icon: <Mail20Regular />, onClick: () => onReiterWechsel("schriftwechsel") },
                                  { label: "Vorgänge", icon: <TaskListSquareLtr20Regular />, onClick: () => onReiterWechsel("vorgaenge") },
                                  { label: "Dokumente, Anhänge & Ablage", icon: <Document20Regular />, onClick: () => onReiterWechsel("dokumente") },
                                  { label: "Termine", icon: <CalendarLtr20Regular />, onClick: () => onReiterWechsel("termine") },
                              ]
                            : [],
                        [
                            { label: "Drucken", icon: <Print20Regular />, onClick: handleDrucken },
                            { label: "Herunterladen (.eml)", icon: <ArrowDownload20Regular />, onClick: handleHerunterladen, disabled: laedtHerunter },
                            ...(onVollbild ? [{ label: "Als Vollbild öffnen", icon: <ArrowMaximize20Regular />, onClick: onVollbild }] : []),
                        ],
                    ]}
                />
            )}
            {/* ── Command-Leiste mit drei States (Mail→Vorgang SP2) ─────────── */}
            {commandState.kind === "A" ? (
                /* State A — „Gehört zu diesem Vorgang": Bestätigen ordnet zu UND
                   archiviert (Entscheidung #25), [X] verwirft den Vorschlag
                   (Fallback B/C). Übrige Aktionen im Überlauf-Menü (⋯, #26). */
                <div className={styles.toolbar}>
                    <span className={styles.belongsLabel}>
                        {commandState.vorgaenge.length === 1
                            ? "Gehört zu diesem Vorgang:"
                            : "Gehört zu diesen Vorgängen:"}
                    </span>
                    {commandState.vorgaenge.length === 1 ? (
                        <span className={styles.belongsChip} title={commandState.vorgaenge[0].titel}>
                            {commandState.vorgaenge[0].titel}
                        </span>
                    ) : (
                        /* Mehrere Vorgänge: Zuordnung zu einem, mehreren oder
                           allen — alle vorausgewählt. */
                        commandState.vorgaenge.map((v) => (
                            <Checkbox
                                key={v.id}
                                checked={(checkedVorgangIds ?? commandState.vorgaenge.map((x) => x.id)).includes(v.id)}
                                label={v.titel}
                                disabled={confirming}
                                onChange={(_, data) => toggleVorgangChecked(v.id, data.checked === true)}
                            />
                        ))
                    )}
                    <Button
                        icon={<Checkmark20Regular />}
                        size="small"
                        disabled={confirming || confirmTargets.length === 0}
                        onClick={() => void handleConfirmSuggestion()}
                        style={{ backgroundColor: "#4d2051", color: "#ffffff", border: "1px solid #4d2051" }}
                    >
                        {confirming ? "Wird zugeordnet …" : "Bestätigen"}
                    </Button>
                    <Button
                        appearance="subtle"
                        size="small"
                        icon={<Dismiss20Regular />}
                        aria-label="Vorschlag verwerfen"
                        title="Vorschlag verwerfen"
                        disabled={confirming}
                        onClick={() => setSuggestionDismissed(true)}
                    />
                    <div className={styles.toolbarSpacer} />
                    {!kompakt && (
                    <Menu>
                        <MenuTrigger disableButtonEnhancement>
                            <Tooltip content="Weitere Aktionen" relationship="label">
                                <Button
                                    appearance="subtle"
                                    size="small"
                                    icon={<MoreHorizontal20Regular />}
                                    aria-label="Weitere Aktionen"
                                />
                            </Tooltip>
                        </MenuTrigger>
                        <MenuPopover>
                            <MenuList>
                                <MenuItem icon={<ArrowReply20Regular />} onClick={() => handleReply()}>
                                    Antworten
                                </MenuItem>
                                <MenuItem icon={<ArrowReplyAll20Regular />} onClick={() => handleReplyAll()}>
                                    Allen antworten
                                </MenuItem>
                                <MenuItem icon={<ArrowForward20Regular />} onClick={() => handleForward()}>
                                    Weiterleiten
                                </MenuItem>
                                {darfArchivieren && (
                                    <>
                                        <MenuDivider />
                                        <MenuItem icon={<Archive20Regular />} onClick={() => void finishRead()}>
                                            Archivieren
                                        </MenuItem>
                                    </>
                                )}
                                <MenuDivider />
                                <MenuItem icon={<Add20Regular />} onClick={() => (istSchadenPostfach ? oeffneSchadenAblage("neu") : openCreateFlowFromMail())}>
                                    {istSchadenPostfach ? "Neuer Schaden" : "Neuer Vorgang"}
                                </MenuItem>
                                <MenuItem onClick={() => handleZuweisen("Vorgang")}>
                                    {istSchadenPostfach ? "Einem bestehenden Schaden zuordnen" : "An Vorgang anhängen"}
                                </MenuItem>
                                {!istSchadenPostfach && (
                                    <MenuItem onClick={() => handleZuweisen("Todo")}>An To-Do anhängen</MenuItem>
                                )}
                                {schadenZuordnenItem}
                                <MenuDivider />
                                {regelMenuItem}
                            </MenuList>
                        </MenuPopover>
                    </Menu>
                    )}
                    {confirmError && (
                        <span className={styles.confirmError} role="alert">
                            {confirmError}
                        </span>
                    )}
                </div>
            ) : !kompakt ||
              commandState.kind === "B" ||
              !!zuweisung ||
              weitereKontakte.length > 0 ||
              (!!zuweisungJ2.kontakt && zuweisungJ2.quelle !== "erkannt") ? (
                /* States B/C (+ neutrale Ansicht, solange der Link-Lookup lädt):
                   bestehende Aktionen bleiben erreichbar (Leitplanke aus §3.2).
                   Handy: nur die Erkennungs-/Zuweisungs-Chips, als EINE
                   waagerecht scrollende Zeile — die Aktionen stehen in der Reihe. */
                <div className={styles.toolbar + (kompakt ? " mm-chips" : "")}>
                    {/* B4/B5: Eine EXPLIZITE Zuweisung, die nicht unter den
                        Kandidaten steht, bekommt einen eigenen Chip vor der
                        Erkennung — sonst verschwände die getroffene Entscheidung
                        hinter den (unveränderten) Vorschlägen. Ein Klick öffnet
                        dasselbe Popup. */}
                    {zuweisung &&
                        !(commandState.kind === "B" &&
                            commandState.candidates.some((c) => c.contactId === zuweisung.id)) && (
                            <button
                                type="button"
                                className={mergeClasses(styles.candidateBtn, styles.candidateBtnSelected)}
                                title="Zuweisung prüfen oder ändern"
                                onClick={() => openAssignFlowFromMail("zuweisen", zuweisung)}
                            >
                                <span className={styles.candidateName}>
                                    Zugewiesen: {zuweisung.displayName}
                                </span>
                                <span
                                    className={mergeClasses(
                                        styles.candidateBadge,
                                        styles.candidateBadgeSelected,
                                    )}
                                >
                                    gewählt
                                </span>
                            </button>
                        )}
                    {/* PO 28.09.2026 — „zuweisen mehrere Firmen/Kontakte bei Mails":
                        weitere Kontakte dieser Nachricht, je mit ×, plus der Weg,
                        einen hinzuzufügen. */}
                    {weitereKontakte.map((w) => (
                        <span key={`weiterer-${w.linkId}`} className={styles.candidateBtn} title="Ebenfalls zugewiesen">
                            <span className={styles.candidateName}>Auch: {w.kontakt.displayName}</span>
                            <button
                                type="button"
                                className={styles.weitererEntfernen}
                                aria-label={`Zuweisung an ${w.kontakt.displayName} entfernen`}
                                title="Zuweisung entfernen"
                                onClick={() => entferneKontaktZuweisung(w.linkId)}
                            >
                                ×
                            </button>
                        </span>
                    ))}
                    {/* Nur neben einer GETROFFENEN Zuweisung — eine bloße Erkennung
                        ist noch keine; dort weist der Chip der Erkennung zu. */}
                    {zuweisungJ2.kontakt && zuweisungJ2.quelle !== "erkannt" && (
                        <Button
                            appearance="subtle"
                            size="small"
                            icon={<PersonAdd20Regular />}
                            title="Diese Mail zusätzlich einem weiteren Kontakt oder einer Firma zuweisen"
                            onClick={oeffneWeiterenKontakt}
                        >
                            Weiterer Kontakt
                        </Button>
                    )}
                    {commandState.kind === "B" && (() => {
                        /* 01.11.2026 — „nicht zweimal ‚erkannt' zeigen": EIN Chip,
                           der erkannte (Rangfolge Betreff → Adresse → Domäne, vom
                           Server) bzw. der gewählte Kontakt; alle übrigen Kandidaten
                           stehen unter „Andere". Ohne eindeutige Erkennung stehen
                           wie bisher alle Chips zur Wahl. */
                        const kandidaten = commandState.candidates;
                        const { haupt, weitere } = teileStateB(
                            kandidaten,
                            selectedCandidate?.contactId ?? null,
                        );
                        const renderChip = (chip: KandidatenChip) => {
                            if (chip.type === "combined") {
                                /* F1a: Firma + AP als EIN Chip (der AP ist der
                                   Ansprechpartner DER Firma). B4: Klick öffnet
                                   die Zuweisung zum Prüfen/Ändern. */
                                const ap = chip.ap;
                                const isSelected = selectedCandidate?.contactId === ap.contactId;
                                const assignmentName = resolveCandidateAssignment(ap, kandidaten).displayName;
                                return (
                                    <button
                                        key={`combined-${ap.contactId}`}
                                        type="button"
                                        className={mergeClasses(
                                            styles.candidateBtn,
                                            isSelected && styles.candidateBtnSelected,
                                        )}
                                        aria-pressed={isSelected}
                                        title={`Zuweisung prüfen oder ändern — aktuell ${assignmentName}`}
                                        onClick={() => handleCandidateClick(ap)}
                                    >
                                        <span className={styles.candidateName}>
                                            {chip.firmaName} – {ap.displayName}
                                        </span>
                                        <span
                                            className={mergeClasses(
                                                styles.candidateBadge,
                                                isSelected && styles.candidateBadgeSelected,
                                            )}
                                        >
                                            AP
                                        </span>
                                    </button>
                                );
                            }
                            const c = chip.candidate;
                            const isSelected = selectedCandidate?.contactId === c.contactId;
                            // B1: der als Vorgang-Kontakt wirksame Ziel-Name — bei
                            // einem AP die Firma, sonst der Kandidat selbst.
                            const assignmentName = resolveCandidateAssignment(c, kandidaten).displayName;
                            return (
                                <button
                                    key={c.contactId}
                                    type="button"
                                    className={mergeClasses(
                                        styles.candidateBtn,
                                        isSelected && styles.candidateBtnSelected,
                                    )}
                                    aria-pressed={isSelected}
                                    title={
                                        istBetreffTreffer(c)
                                            ? `Steht im Betreff — zuweisen an ${assignmentName}`
                                            : `Zuweisung prüfen oder ändern — aktuell ${assignmentName}`
                                    }
                                    onClick={() => handleCandidateClick(c)}
                                >
                                    <span className={styles.candidateName}>{c.displayName}</span>
                                    <span
                                        className={mergeClasses(
                                            styles.candidateBadge,
                                            isSelected && styles.candidateBadgeSelected,
                                        )}
                                    >
                                        {candidateKindLabel(c.kind)}
                                    </span>
                                </button>
                            );
                        };
                        const neuerVorgang = (
                            <>
                                {/* B3 — vertikaler Trenner (eigenes Element, kein „/"). */}
                                <span className={styles.vDivider} aria-hidden="true" />
                                <Button
                                    icon={<Add20Regular />}
                                    onClick={handleNeuerVorgang}
                                    size="small"
                                    style={{ backgroundColor: "#4d2051", color: "#ffffff", border: "1px solid #4d2051" }}
                                >
                                    {istSchadenPostfach ? "Neuer Schaden" : "Neuer Vorgang"}
                                </Button>
                            </>
                        );
                        if (haupt) {
                            const istErkannt = chipKontaktId(haupt) === commandState.defaultContactId;
                            return (
                                <>
                                    <span className={styles.candidateLead}>
                                        {istErkannt ? "Erkannt:" : "Gewählt:"}
                                    </span>
                                    {renderChip(haupt)}
                                    {weitere.length > 0 && (
                                        <Menu>
                                            <MenuTrigger disableButtonEnhancement>
                                                <Button
                                                    appearance="subtle"
                                                    size="small"
                                                    icon={<ChevronDown20Regular />}
                                                    iconPosition="after"
                                                    title="Diese Mail einem anderen Kontakt aus Adressen oder Betreff zuweisen"
                                                >
                                                    {`Andere (${weitere.length})`}
                                                </Button>
                                            </MenuTrigger>
                                            <MenuPopover>
                                                <MenuList>
                                                    {weitere.map((chip) => {
                                                        const k = chip.type === "combined" ? chip.ap : chip.candidate;
                                                        const name =
                                                            chip.type === "combined"
                                                                ? `${chip.firmaName} – ${k.displayName}`
                                                                : k.displayName;
                                                        const art = chip.type === "combined" ? "AP" : candidateKindLabel(k.kind);
                                                        return (
                                                            <MenuItem
                                                                key={`andere-${chipKontaktId(chip)}`}
                                                                onClick={() => handleCandidateClick(k)}
                                                            >
                                                                {`${name} · ${art} · ${kandidatHerkunft(k)}`}
                                                            </MenuItem>
                                                        );
                                                    })}
                                                </MenuList>
                                            </MenuPopover>
                                        </Menu>
                                    )}
                                    {!kompakt && neuerVorgang}
                                    <div className={styles.toolbarSpacer} />
                                </>
                            );
                        }
                        // Nichts eindeutig erkannt: alle Chips zur Wahl, Kunden aus
                        // dem Betreff unter eigener Überschrift (T-265).
                        return (
                            <>
                                {kandidaten.some((c) => !istBetreffTreffer(c)) && (
                                    <span className={styles.candidateLead}>Zur Auswahl:</span>
                                )}
                                {buildStateBItems(kandidaten).map((item) => {
                                    if (item.type === "betreff-lead") {
                                        return (
                                            <span
                                                key="betreff-lead"
                                                className={styles.candidateLead}
                                                title="Diese Kunden stehen im Betreff — nichts ist vorausgewählt, bitte selbst wählen."
                                            >
                                                Aus dem Betreff:
                                            </span>
                                        );
                                    }
                                    if (item.type === "candidate" || item.type === "combined") {
                                        return renderChip(item);
                                    }
                                    return null;
                                })}
                                {!kompakt && neuerVorgang}
                                <div className={styles.toolbarSpacer} />
                            </>
                        );
                    })()}
                    {!kompakt && barReady && showAssignContactButton(commandState) && (
                        /* State C (B5/SP4): „Kontakt zuweisen" öffnet den Zuweisungs-
                           Flow (Suche + Minimal-Anlage Firma/AP/Person + „ohne
                           Kontakt"); danach direkt ins Light-Erstell-Popup. */
                        <>
                            <Button
                                icon={<PersonAdd20Regular />}
                                size="small"
                                onClick={() => openAssignFlowFromMail("zuweisen", effektiveZuweisungJetzt())}
                                style={{ backgroundColor: "#4d2051", color: "#ffffff", border: "1px solid #4d2051" }}
                            >
                                Kontakt zuweisen
                            </Button>
                            <div className={styles.toolbarSpacer} />
                        </>
                    )}
                    {/* PO 26.07.2026: Bei erkanntem Kontakt (State B) zeigt die Leiste
                        bereits „Neuer Vorgang" — das Zuordnen zu einem BESTEHENDEN
                        Vorgang fehlte dort ganz, weil das generische Menü in B
                        ausgeblendet ist. Dieser Button schließt die Lücke und öffnet
                        direkt die Vorgangsauswahl (mehrere möglich). */}
                    {!kompakt && commandState.kind === "B" && (
                        <Button
                            appearance="subtle"
                            icon={<TaskListSquareLtr20Regular />}
                            size="small"
                            onClick={() => oeffneVorgangPicker()}
                            title={
                                istSchadenPostfach
                                    ? "Diese Mail in einen bestehenden Schaden-Ordner des Kontakts legen"
                                    : "Diese Mail einem oder mehreren bestehenden Vorgängen zuordnen"
                            }
                        >
                            Zuweisen
                        </Button>
                    )}
                    {!kompakt && darfArchivieren && (
                        <Button appearance="subtle" icon={<Archive20Regular />} onClick={() => void finishRead()} size="small">
                            Archivieren
                        </Button>
                    )}
                    {/* A5 (Review 2026-07-06): die generische „Zuweisen"-Dropdown-Leiste
                       ist im State B („Erkannt") redundant (Kandidaten-Buttons + „Neuer
                       Vorgang" decken den Flow ab) → nur außerhalb von B anzeigen. */}
                    {!kompakt && showZuweisenMenu(commandState.kind) && (
                        /* PO 26.07.2026 (Punkt 11): aus „Zuweisen" wird „Vorgang"
                           mit GENAU ZWEI Optionen. Die drei früheren Einträge
                           „Neuen Vorgang zuweisen → Kontakt / Vertrag / Risiko"
                           landeten ohnehin alle im selben Zweig — `handleZuweisen`
                           verwarf den Parameter. Das To-do bleibt als eigener
                           Eintrag darunter erhalten. */
                        <Menu>
                            <MenuTrigger disableButtonEnhancement>
                                <Button appearance="subtle" icon={<ChevronDown20Regular />} iconPosition="after" size="small">
                                    {istSchadenPostfach ? "Schaden" : "Vorgang"}
                                </Button>
                            </MenuTrigger>
                            <MenuPopover>
                                <MenuList>
                                    <MenuItem onClick={() => handleZuweisen("KUNDE")}>
                                        {istSchadenPostfach ? "Neuer Schaden" : "Neuer Vorgang"}
                                    </MenuItem>
                                    <MenuItem onClick={() => handleZuweisen("Vorgang")}>
                                        {istSchadenPostfach ? "Einem bestehenden Schaden zuordnen" : "Bestehendem Vorgang zuordnen"}
                                    </MenuItem>
                                    {!istSchadenPostfach && <MenuDivider />}
                                    {!istSchadenPostfach && (
                                        <MenuItem onClick={() => handleZuweisen("Todo")}>An To-Do anhängen</MenuItem>
                                    )}
                                    {schadenZuordnenItem}
                                </MenuList>
                            </MenuPopover>
                        </Menu>
                    )}
                    {!kompakt && (barReady ? showNeutralVorgangButton(commandState) : commandState.kind !== "B") && (
                        /* Neutrale Ansicht: der bisherige „Neuer Vorgang"-Button.
                           Solange die Leiste noch lädt, gilt bewusst die ALTE
                           Bedingung (`kind !== "B"`) — damit ist das Verhalten
                           während des Ladens bitgleich zu vorher, es flackert
                           nichts. Ist sie scharf, entscheidet der reine Helfer:
                           in C und D steht „Kontakt zuweisen" an dieser Stelle,
                           beide Buttons nebeneinander wären doppelt. */
                        <Button
                            icon={<Add20Regular />}
                            onClick={handleNeuerVorgang}
                            size="small"
                            style={{ backgroundColor: "#4d2051", color: "#ffffff", border: "1px solid #4d2051" }}
                        >
                            {istSchadenPostfach ? "Neuer Schaden" : "Neuer Vorgang"}
                        </Button>
                    )}
                    {!kompakt && (
                    <>
                    <Menu>
                        <MenuTrigger disableButtonEnhancement>
                            <Tooltip content="Antworten / Allen antworten / Weiterleiten" relationship="label">
                                <Button
                                    appearance="subtle"
                                    icon={<ArrowReply20Regular />}
                                    size="small"
                                    aria-label="Antworten"
                                >
                                    Antworten
                                    <ChevronDown20Regular style={{ marginLeft: 2 }} />
                                </Button>
                            </Tooltip>
                        </MenuTrigger>
                        <MenuPopover>
                            <MenuList>
                                <MenuItem icon={<ArrowReply20Regular />} onClick={() => handleReply()}>
                                    Antworten
                                </MenuItem>
                                <MenuItem icon={<ArrowReplyAll20Regular />} onClick={() => handleReplyAll()}>
                                    Allen antworten
                                </MenuItem>
                                <MenuItem icon={<ArrowForward20Regular />} onClick={() => handleForward()}>
                                    Weiterleiten
                                </MenuItem>
                            </MenuList>
                        </MenuPopover>
                    </Menu>
                    {/* H5 — dieser Zweig hatte bisher KEIN Überlauf-Menü (das
                        einzige ⋯ liegt in Zweig A). Es wird immer gerendert,
                        damit „Regel erstellen …" in jedem Zustand erreichbar
                        ist. */}
                    <Menu>
                        <MenuTrigger disableButtonEnhancement>
                            <Tooltip content="Weitere Aktionen" relationship="label">
                                <Button
                                    appearance="subtle"
                                    size="small"
                                    icon={<MoreHorizontal20Regular />}
                                    aria-label="Weitere Aktionen"
                                />
                            </Tooltip>
                        </MenuTrigger>
                        <MenuPopover>
                            <MenuList>
                                {/* Im Zustand B fehlt das „Vorgang"-Menü — der
                                    Schaden-Weg steht dann hier (T-180). */}
                                {commandState.kind === "B" && schadenZuordnenItem}
                                {regelMenuItem}
                            </MenuList>
                        </MenuPopover>
                    </Menu>
                    </>
                    )}
                </div>
            ) : null}
            {/* „Absender wurde nicht erkannt" entfällt (User 03.11.2026) — Zuweisen
                steht ohnehin als Knopf da. Die erkannte Signatur bleibt als Angebot. */}
            {!kompakt &&
                barReady &&
                showUnrecognizedWarning(commandState) &&
                signaturHinweis(recognized?.signature ?? null, false) && (
                    /* T-36 — die erkannte Signatur sichtbar machen: ein Klick
                       öffnet „Kontakt zuweisen" mit genau dieser Vorbelegung. */
                    <div className={styles.domainBar} role="status">
                        <PersonAdd20Regular />
                        <span>{signaturHinweis(recognized?.signature ?? null, false)}</span>
                        <Button appearance="primary" size="small" onClick={() => openAssignFlowFromMail("zuweisen")}>
                            Als Kontakt anlegen
                        </Button>
                    </div>
                )}

            {barReady && commandState.kind === "D" && (
                /* A4 — „unbekannte Adresse an bekannter Firmendomäne". Der vom
                   PO benannte Kernfall: Die Domäne kennen wir, genau DIESE
                   Adresse noch nicht → es ist wahrscheinlich ein neuer
                   Ansprechpartner. Bewusst OHNE Kandidaten-Chip daneben: ein
                   Angebot, keine Zuordnung. Den Anschluss („neuen AP für Firma
                   X anlegen") baut Cluster ZUWEISEN auf genau diesem Zustand. */
                <div className={styles.domainBar} role="status">
                    <PersonAdd20Regular />
                    {commandState.ambiguous ? (
                        <span>
                            {`Diese Adresse gehört zu einer von ${commandState.companies.length + commandState.more} bekannten Firmen: `}
                            <span className={styles.domainCompany}>
                                {commandState.companies.map((c) => c.displayName).join(", ")}
                            </span>
                            {commandState.more > 0 ? ` + ${commandState.more} weitere` : ""}
                        </span>
                    ) : (
                        <span>
                            {"Unbekannte Adresse der Firma "}
                            <span className={styles.domainCompany}>
                                {commandState.companies[0]?.displayName ?? commandState.domain}
                            </span>
                        </span>
                    )}
                </div>
            )}

            {barReady && offeneKontaktdaten.length > 0 && (
                /* A6/C9 — in der Mail gefundene Kontaktdaten, die beim
                   Bezugskontakt noch fehlen. Das Übernehmen läuft NICHT mehr
                   blind auf den erkannten Kontakt, sondern über die
                   eingeschränkte Auswahl „Firma / Ansprechpartner / neuer AP"
                   (bei einem Privatkontakt: nur privat oder geschäftlich). */
                <div className={styles.hintBar} role="status">
                    {offeneKontaktdaten.map((entry) => (
                        <span key={`${entry.kind}-${entry.value}`}>
                            {`${MISSING_LABEL[entry.kind]}: `}
                            <span className={styles.hintValue}>{entry.display}</span>
                            {!entry.confident && <span className={styles.hintUnsure}> (unsicher)</span>}
                            <Button
                                appearance="subtle"
                                size="small"
                                onClick={() => oeffneNummerZuordnen(entry)}
                            >
                                Übernehmen
                            </Button>
                        </span>
                    ))}
                    <Button appearance="subtle" size="small" onClick={stelleKontaktdatenZurueck}>
                        Später
                    </Button>
                </div>
            )}

            {recognized && (rollup.contracts.length > 0 || rollup.risks.length > 0) && (
                <div className={styles.recognitionChipRow}>
                    {/* 01.11.2026: nicht „Erkannt:" — das steht schon EINMAL vor
                        dem Kontakt; hier geht es um Verträge/Risiken im Text. */}
                    <span className={styles.recognitionLabel}>In der Mail:</span>
                    {rollup.contracts.map((c) => (
                        <button
                            key={`contract-${c.id}`}
                            type="button"
                            className={styles.recognitionChip}
                            onClick={() => handleRecognizedClick(c)}
                            title={`Vorgang für Vertrag ${c.contractNbr} anlegen`}
                        >
                            <span style={{ fontWeight: 700 }}>Vertrag</span>
                            <span>{c.contractNbr}</span>
                        </button>
                    ))}
                    {rollup.risks.map((r) => (
                        <button
                            key={`risk-${r.id}`}
                            type="button"
                            className={styles.recognitionChip}
                            onClick={() => handleRecognizedClick(r)}
                            title={`Vorgang für Risiko ${r.name} anlegen`}
                        >
                            <span style={{ fontWeight: 700 }}>
                                {r.type === "kfz" ? "Kfz" : "Immobilie"}
                            </span>
                            <span>{r.type === "kfz" ? r.plate : r.adresse}</span>
                        </button>
                    ))}
                    {rollup.contactMismatch && (
                        <span className={styles.recognitionLabel} style={{ fontWeight: 400 }}>
                            — gehört zu einem anderen Kontakt als dem Absender
                        </span>
                    )}
                </div>
            )}

            {antwortEntwurf && (
                /* T-212 (Teil B) — wie Outlooks „[Entwurf]" an der Mail. */
                <div
                    role="status"
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "6px 12px",
                        fontSize: 13,
                        color: "#a4262c",
                        background: "#fdf3f4",
                        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
                    }}
                >
                    <span style={{ fontWeight: 700 }}>[Entwurf]</span>
                    <span style={{ flexGrow: 1, color: "#241522" }}>Zu dieser Mail gibt es einen Antwort-Entwurf.</span>
                    <Button size="small" appearance="secondary" onClick={entwurfFortsetzen}>
                        Entwurf fortsetzen
                    </Button>
                </div>
            )}
            {kompakt ? (
                /* Handy (Outlook iOS): Betreff, darunter Bild + Absender + Zeit,
                   „An …" einzeilig; ein Tipp zeigt die Details. Datum und
                   Drucken/Vollbild/Herunterladen klein in einer Zeile. */
                <div className="mm-kopf-mail">
                    {onNavBack && (
                        <Button
                            appearance="subtle"
                            size="small"
                            icon={<ArrowLeft20Regular />}
                            onClick={onNavBack}
                            aria-label="Zurück zur vorherigen Mail"
                            style={{ marginBottom: 4 }}
                        >
                            Zurück
                        </Button>
                    )}
                    <h2 className="mm-kopf-betreff">{view.subject || "Kein Betreff"}</h2>
                    <MailVorgangsChips
                        vorgaenge={zugeordneteVorgaenge}
                        rowClassName={styles.vorgangRow}
                        chipClassName={styles.vorgangChip}
                        onNavigieren={navigate}
                    />
                    {/* In der Unterhaltung trägt jede Nachricht ihren eigenen Kopf —
                        der Absender stünde hier sonst doppelt. */}
                    {!(convo && convo.length > 1) && (
                    <>
                    <button
                        type="button"
                        className="mm-absender"
                        aria-expanded={kopfDetails}
                        aria-label={`Von ${view.senderName} — Details ${kopfDetails ? "ausblenden" : "anzeigen"}`}
                        onClick={() => setKopfDetails((o) => !o)}
                    >
                        <SenderAvatar name={view.senderName} email={view.senderEmail} size={36} bg={view.senderColor} />
                        <span className="mm-absender-text">
                            <span className="mm-absender-name">{view.senderName || view.senderEmail}</span>
                            <span className="mm-absender-an">{empfaengerKurz(view.to)}</span>
                        </span>
                        <span className="mm-absender-zeit">{kurzeZeit(view.date)}</span>
                    </button>
                    {kopfDetails && (
                        <div className="mm-kopf-details">
                            <span>
                                <b>Von:</b> {view.senderName}
                                {view.senderEmail ? ` ‹${normalizeAddress(view.senderEmail)}›` : ""}
                                {senderContact && (
                                    <>
                                        {" · "}
                                        <button
                                            type="button"
                                            className={styles.senderLink}
                                            onClick={() => navigate(`/contacts/${senderContact.id}`)}
                                        >
                                            Kontakt öffnen
                                        </button>
                                    </>
                                )}
                            </span>
                            <span>
                                <b>An:</b> {formatRecipientTitle(view.to) || "—"}
                            </span>
                            {(view.cc?.length ?? 0) > 0 && (
                                <span>
                                    <b>Cc:</b> {formatRecipientTitle(view.cc ?? [])}
                                </span>
                            )}
                        </div>
                    )}
                    </>
                    )}
                    {/* Einladung: Termin-Karte unter dem Absender (Outlook iOS). */}
                    {(view.meetingMessageType ?? email.meetingMessageType) === "meetingRequest" && (
                        <TerminKarte
                            variante="karte"
                            mailbox={mailbox}
                            messageId={view.id}
                            onBeantwortet={einladungBeantwortet}
                        />
                    )}
                    <div className="mm-kopf-fuss" style={convo && convo.length > 1 ? { marginLeft: 0 } : undefined}>
                        <span>{formatHeaderDate(view.date)}</span>
                        <button type="button" aria-label="Drucken" title="Drucken" onClick={handleDrucken}>
                            <Print20Regular />
                        </button>
                        {onVollbild && (
                            <button type="button" aria-label="Als Vollbild öffnen" title="Vollbild" onClick={onVollbild}>
                                <ArrowMaximize20Regular />
                            </button>
                        )}
                        <button
                            type="button"
                            aria-label="Herunterladen"
                            title="Herunterladen (.eml)"
                            disabled={laedtHerunter}
                            onClick={handleHerunterladen}
                        >
                            <ArrowDownload20Regular />
                        </button>
                    </div>
                </div>
            ) : (
            <div className={styles.header}>
                {/* J1 — der Zurück-Weg der In-Fenster-Navigation darf nicht NUR
                    im Leisten-Overlay stehen, das der Nutzer gerade geschlossen
                    hat. Deshalb hier ein zweiter, immer sichtbarer Pfeil. */}
                {onNavBack && (
                    <Button
                        appearance="subtle"
                        size="small"
                        icon={<ArrowLeft20Regular />}
                        onClick={onNavBack}
                        title="Zurück zur vorherigen Mail"
                        aria-label="Zurück zur vorherigen Mail"
                        style={{ alignSelf: "flex-start", marginBottom: 2 }}
                    >
                        Zurück
                    </Button>
                )}
                <h2 className={styles.subject}>{view.subject}</h2>
                {/* P7 (PO 14.09.2026) — „in den Mails anzeigen, ob eine Mail zu
                    einem Vorgang gehört": derselbe Hinweis wie der Marker in der
                    Liste, hier mit Titel und Klickziel. Daten aus den
                    Konversations-Links, die das Fenster ohnehin lädt. */}
                <MailVorgangsChips
                    vorgaenge={zugeordneteVorgaenge}
                    rowClassName={styles.vorgangRow}
                    chipClassName={styles.vorgangChip}
                    onNavigieren={navigate}
                />
                {/* T-229: Einladung zusagen, mit Vorbehalt annehmen oder absagen. */}
                <TerminEinladungLeiste
                    mailbox={mailbox}
                    messageId={view.id}
                    meetingMessageType={view.meetingMessageType ?? email.meetingMessageType}
                    onBeantwortet={einladungBeantwortet}
                />
                <div className={styles.senderRow}>
                    {/* SP5: interne Absender zeigen ihr Entra-Foto (wie im Chat). */}
                    <SenderAvatar
                        name={view.senderName}
                        email={view.senderEmail}
                        size={40}
                        bg={view.senderColor}
                    />
                    <div className={styles.senderInfo}>
                        {/* SP3 (B3/B5): „Von: Name ‹adresse›" — Adresse lowercase-
                            normalisiert; mit matchedContact ist der Name klickbar. */}
                        <span className={styles.senderLine}>
                            <span className={styles.senderLabel}>Von:</span>
                            {senderContact ? (
                                <button
                                    type="button"
                                    className={styles.senderLink}
                                    onClick={() => navigate(`/contacts/${senderContact.id}`)}
                                    title={`KMVS-Kontakt öffnen: ${senderContact.displayName}`}
                                >
                                    {view.senderName}
                                </button>
                            ) : (
                                <span className={styles.senderName}>{view.senderName}</span>
                            )}
                            {view.senderEmail && (
                                <span className={styles.senderAddress}>
                                    ‹{normalizeAddress(view.senderEmail)}›
                                </span>
                            )}
                            {/* SP3 (B4): Präsenz-Punkt nur bei internen Absendern
                                (KMVS-Chat-Status; grün=online, gelb=abwesend). */}
                            {senderUserId && (
                                <PresenceDot status={senderPresence[senderUserId]} size={9} />
                            )}
                        </span>
                        {/* SP3 (B3) + PO Runde 3: „An: Name ‹adresse›" in derselben
                            Optik wie „Von:". Mehr Empfänger als die Zeile fasst,
                            stehen hinter „+n anzeigen"; der Tooltip führt sie
                            ohnehin alle auf. */}
                        <span className={styles.recipientRow}>
                            <span className={styles.senderLabel}>An:</span>
                            <span
                                className={mergeClasses(
                                    styles.recipientLine,
                                    toOpen && styles.recipientLineOffen,
                                )}
                                title={formatRecipientTitle(view.to) || undefined}
                            >
                                {toSichtbar.length === 0
                                    ? "—"
                                    : toSichtbar.map((e, i) => (
                                        <React.Fragment key={`${e.address}-${e.name}-${i}`}>
                                            {i > 0 ? ", " : null}
                                            {e.name ? (
                                                <span className={styles.recipientName}>
                                                    {e.name}
                                                </span>
                                            ) : null}
                                            {e.name && e.address ? " " : null}
                                            {e.address ? (
                                                <span className={styles.senderAddress}>
                                                    {e.name ? `‹${e.address}›` : e.address}
                                                </span>
                                            ) : null}
                                        </React.Fragment>
                                    ))}
                            </span>
                            {toKompakt.rest > 0 && (
                                <button
                                    type="button"
                                    className={mergeClasses(styles.ccToggle, styles.recipientToggle)}
                                    onClick={() => setToOpen((offen) => !offen)}
                                    aria-expanded={toOpen}
                                    aria-label={
                                        toOpen
                                            ? "Weitere Empfänger wieder einklappen"
                                            : `Weitere ${toKompakt.rest} Empfänger anzeigen`
                                    }
                                >
                                    {toOpen ? "(weniger)" : `+${toKompakt.rest} anzeigen`}
                                </button>
                            )}
                        </span>
                        {/* PO 26.07.2026: Die An-Adressen stehen offen da, Cc
                            kostet einen Klick — bei Rundmails schob die
                            Cc-Zeile sonst den halben Kopf voll. */}
                        {(view.cc?.length ?? 0) > 0 &&
                            (ccOpen ? (
                                <span className={styles.ccExpanded}>
                                    Cc:{" "}
                                    {(view.cc ?? [])
                                        .map((t) => t.name || t.address)
                                        .filter(Boolean)
                                        .join(", ")}{" "}
                                    <button
                                        type="button"
                                        className={styles.ccToggle}
                                        onClick={() => setCcOpen(false)}
                                    >
                                        (weniger)
                                    </button>
                                </span>
                            ) : (
                                <button
                                    type="button"
                                    className={styles.ccToggle}
                                    onClick={() => setCcOpen(true)}
                                    aria-expanded={false}
                                >
                                    Cc: {view.cc!.length} Empfänger anzeigen
                                </button>
                            ))}
                    </div>
                    {/* SP3 (B3): Tag + Datum + Uhrzeit rechtsbündig. */}
                    <span className={styles.headerDate}>{formatHeaderDate(view.date)}</span>
                    {/* T-195 — kleiner Drucker-Knopf, in jedem Zustand sichtbar. */}
                    <Tooltip content="Drucken" relationship="label">
                        <Button
                            appearance="subtle"
                            size="small"
                            icon={<Print20Regular />}
                            aria-label="Drucken"
                            title="Drucken"
                            onClick={handleDrucken}
                            style={{ flexShrink: 0, marginTop: -4 }}
                        />
                    </Tooltip>
                    <MailZiehGriff zug={mailSnapshot} />
                    <Tooltip content="Herunterladen (.eml)" relationship="label">
                        <Button
                            appearance="subtle"
                            size="small"
                            icon={<ArrowDownload20Regular />}
                            aria-label="Herunterladen"
                            title="Herunterladen (.eml)"
                            disabled={laedtHerunter}
                            onClick={handleHerunterladen}
                            style={{ flexShrink: 0, marginTop: -4 }}
                        />
                    </Tooltip>
                </div>
            </div>

            )}

            <div className={mergeClasses(styles.bodyScroll, kompakt && styles.bodyScrollMit)}>
                {/* Outlook Desktop: auch eine Unterhaltung steht auf EINEM Blatt —
                    die Nachrichten darin trennt eine Linie, nicht je eine Karte. */}
                <div className={mergeClasses(styles.bodyCard, kompakt && styles.bodyCardRandlos)}>
                    {/* T-369: Anhänge stehen direkt unter dem Kopf ÜBER dem Text
                        (wie in Outlook) — hinter einem langen Text bzw. allen
                        Abschnitten einer Konversation wurden sie übersehen. */}
                    {(attachmentsError || attachments.length > 0) && (
                        <div className={styles.anhangOben}>
                            <MailAnhangLeiste
                                attachments={attachments}
                                fehler={attachmentsError}
                                onRetry={() => setAttRetryTick((t) => t + 1)}
                                offen={attachmentsOpen}
                                onToggle={() => setAttachmentsOpen((o) => !o)}
                                saveTargetContact={saveTargetContact}
                                savedKeys={savedAttKeys}
                                savingKey={savingAttKey}
                                downloadingKey={downloadingAtt}
                                vcfLaedtKey={vcfLaedt}
                                saveMsg={attSaveMsg}
                                onOpen={handleOpenAttachment}
                                onDownload={handleDownloadAttachment}
                                onSave={handleSaveAttachment}
                                onSaveAll={handleSaveAllAttachments}
                                onVisitenkarte={(att) => void handleVisitenkarte(att)}
                                ziehMailbox={mailbox}
                                herkunft={anhangHerkunft}
                            />
                        </div>
                    )}
                    {/* Konversations-Thread nicht ladbar → statt still auf die
                        Einzelansicht zu degradieren, dezent darauf hinweisen. */}
                    {convoError && email.type === "conversation" && (
                        <div className={styles.loadingHint} role="status">
                            <span className={styles.loadErrorText}>
                                Konversation konnte nicht geladen werden — es wird nur diese Nachricht angezeigt.
                            </span>
                            <Button
                                appearance="secondary"
                                size="small"
                                onClick={() => setConvoRetryTick((t) => t + 1)}
                            >
                                Neu laden
                            </Button>
                        </div>
                    )}
                    {/* Voll-Body-Status (nur Einzelansicht — in der Konversations-
                        Ansicht zeigt die jeweilige Abschnitts-Karte ihren Status). */}
                    {!(convo && convo.length > 1) && bodyError && (
                        <div className={styles.loadErrorBox} role="alert">
                            <span className={styles.loadErrorText}>
                                {bodyUnavailable
                                    ? "Diese Mail ist für dich nicht verfügbar. Bitte an eine berechtigte Person teilen lassen."
                                    : bodyError404
                                        ? "Nachricht konnte nicht geladen werden."
                                        : "Nachricht konnte gerade nicht geladen werden. Bitte erneut versuchen."}
                            </span>
                            {!bodyUnavailable && (
                                <Button
                                    appearance="secondary"
                                    size="small"
                                    onClick={() => setRetryTick((t) => t + 1)}
                                >
                                    Neu laden
                                </Button>
                            )}
                        </div>
                    )}
                    {!(convo && convo.length > 1) && !bodyError && !full && bodyLoadingSlow && (
                        <div className={styles.loadingHint} role="status">
                            <Spinner size="tiny" />
                            <span>Nachricht wird geladen …</span>
                        </div>
                    )}
                    {/* Der Text steht schon, die Bilder noch nicht: ohne diesen Satz
                        wirken die Lücken wie eine kaputte Mail. */}
                    {bilderHinweis && (
                        <div className={styles.loadingHint} role="status">
                            <Spinner size="tiny" />
                            <span>{bilderHinweis}</span>
                        </div>
                    )}
                    {!showRemote && hasRemote && (
                        <div className={styles.remoteBanner} role="status">
                            <ImageOff20Regular />
                            <span className={styles.remoteBannerText}>
                                Externe Inhalte (z. B. Bilder) wurden zum Schutz vor Tracking blockiert.
                            </span>
                            <Button appearance="primary" size="small" onClick={() => setShowRemote(true)}>
                                Anzeigen
                            </Button>
                            {view.senderEmail && (
                                <Button
                                    appearance="subtle"
                                    size="small"
                                    onClick={() => { allowRemoteSender(view.senderEmail); setShowRemote(true); }}
                                    title={`Externe Inhalte von ${view.senderEmail} künftig automatisch laden`}
                                >
                                    Von diesem Absender immer laden
                                </Button>
                            )}
                        </div>
                    )}
                    {convo && convo.length > 1 ? (
                        /* Wie Outlook Desktop: neueste Nachricht OBEN, die Nachrichten
                           durch eine Linie getrennt (kein Kartenstapel älteste-zuerst
                           wie in Outlook Web). Jede bleibt auf-/zuklappbar (neueste
                           standardmäßig offen), Aktionen pro Nachricht. */
                        neuesteZuerst(convo).map((msg, index) => {
                            const isOpen = expandedIds.has(msg.id);
                            const fullMsg = bodies[msg.id];
                            const replyTarget = fullMsg ?? msg;
                            return (
                                <div
                                    key={msg.id}
                                    className={index > 0 ? styles.convoTrenner : undefined}
                                >
                                    {kompakt ? (
                                        /* Handy (Outlook iOS): je Nachricht Bild, Name,
                                           „An …" einzeilig, Zeit rechts und darunter „…". */
                                        <div
                                            className="mm-nachricht-kopf"
                                            role="button"
                                            tabIndex={0}
                                            aria-expanded={isOpen}
                                            onClick={() => toggleExpand(msg.id)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter" || e.key === " ") toggleExpand(msg.id);
                                            }}
                                        >
                                            <SenderAvatar name={msg.senderName} email={msg.senderEmail} size={36} bg={msg.senderColor} />
                                            <span className="mm-absender-text">
                                                <span className="mm-absender-name">{msg.senderName || msg.senderEmail}</span>
                                                <span className="mm-absender-an">
                                                    {isOpen ? empfaengerKurz(msg.to) : msg.preview}
                                                </span>
                                            </span>
                                            <span className="mm-nachricht-rechts">
                                                <span className="mm-absender-zeit">{kurzeZeit(msg.date)}</span>
                                                <button
                                                    type="button"
                                                    className="mm-nachricht-mehr"
                                                    aria-label="Aktionen für diese Nachricht"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setNachrichtMenue(replyTarget);
                                                    }}
                                                >
                                                    <MoreHorizontal20Regular />
                                                </button>
                                            </span>
                                        </div>
                                    ) : (
                                    <div
                                        className={styles.convoHead}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => toggleExpand(msg.id)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" || e.key === " ") toggleExpand(msg.id);
                                        }}
                                    >
                                        <SenderAvatar
                                            name={msg.senderName}
                                            email={msg.senderEmail}
                                            size={32}
                                            bg={msg.senderColor}
                                        />
                                        <div style={{ flexGrow: 1, minWidth: 0 }}>
                                            <div className={styles.convoFrom}>{msg.senderName}</div>
                                            {isOpen ? (
                                                <div className={styles.convoMeta}>
                                                    An: {(msg.to ?? []).map((t) => t.name || t.address).filter(Boolean).join(", ") || "—"}
                                                </div>
                                            ) : (
                                                <div className={styles.convoPreview}>{msg.preview}</div>
                                            )}
                                        </div>
                                        <div style={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
                                            {isOpen && (
                                                <>
                                                    <Button
                                                        appearance="subtle"
                                                        size="small"
                                                        icon={<ArrowReply20Regular />}
                                                        title="Auf diese Nachricht antworten"
                                                        onClick={(e) => { e.stopPropagation(); handleReply(replyTarget); }}
                                                    />
                                                    <Button
                                                        appearance="subtle"
                                                        size="small"
                                                        icon={<ArrowReplyAll20Regular />}
                                                        title="Allen antworten (diese Nachricht)"
                                                        onClick={(e) => { e.stopPropagation(); handleReplyAll(replyTarget); }}
                                                    />
                                                    <Button
                                                        appearance="subtle"
                                                        size="small"
                                                        icon={<ArrowForward20Regular />}
                                                        title="Diese Nachricht weiterleiten"
                                                        onClick={(e) => { e.stopPropagation(); handleForward(replyTarget); }}
                                                    />
                                                </>
                                            )}
                                            <span className={styles.convoDate}>{formatDate(msg.date)}</span>
                                            <span className={isOpen ? styles.convoChevronOpen : styles.convoChevron}>
                                                <ChevronDown20Regular />
                                            </span>
                                        </div>
                                    </div>
                                    )}
                                    {isOpen && (
                                        <div className={styles.convoBody}>
                                            {fullMsg ? (
                                                <SandboxedMailBody
                                                    className={styles.bodyText}
                                                    /* Handy/Tablet/schmales Panel: breite Mails umbrechen bzw.
                                                       verkleinern statt rechts abschneiden. */
                                                    anBreiteAnpassen
                                                    zitatAbtrennen={kompakt}
                                                    zoombar={kompakt}
                                                    html={fullMsg.body}
                                                    /* Die 400px des Standardwerts sind für EINE gelesene
                                                       Mail gedacht. Im Verlauf steht hier oft ein
                                                       Zweizeiler („Anbei, viele Grüße") — 400px daraus
                                                       zu machen, streckt den Thread ins Unlesbare. Klein,
                                                       aber nicht 0: misslingt die Höhenmessung, bliebe
                                                       die Nachricht sonst unsichtbar. */
                                                    minHeight={60}
                                                    blockRemote={!showRemote}
                                                    onRemotePresence={(present) => { if (present) setHasRemote(true); }}
                                                    onMarkierung={beiMarkierung}
                                                    onBild={(b) => beiBild(b, msg.id)}
                                                    onBilderLaden={(n) => meldeBilderLaden(msg.id, n)}
                                                />
                                            ) : failedBodyIds.has(msg.id) ? (
                                                /* Body-Fetch fehlgeschlagen → kompakte Fehlerzeile
                                                   statt Endlos-Spinner; Retry nur für DIESE Nachricht. */
                                                <div className={styles.loadErrorBox} role="alert" style={{ marginBottom: 0 }}>
                                                    <span className={styles.loadErrorText}>
                                                        Nachricht konnte nicht geladen werden.
                                                    </span>
                                                    <Button
                                                        appearance="secondary"
                                                        size="small"
                                                        onClick={(e) => { e.stopPropagation(); retryBody(msg.id); }}
                                                    >
                                                        Neu laden
                                                    </Button>
                                                </div>
                                            ) : (
                                                <Spinner size="tiny" label="Nachricht wird geladen …" />
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    ) : view.thread ? (
                        view.thread.map((msg, i) => (
                            <div key={i} className={styles.threadMsg}>
                                <div className={styles.threadFrom}>{msg.from}</div>
                                <div className={styles.threadDate}>{msg.date}</div>
                                <SandboxedMailBody
                                    className={styles.threadBody}
                                    /* Handy/Tablet/schmales Panel: breite Mails umbrechen bzw.
                                       verkleinern statt rechts abschneiden. */
                                    anBreiteAnpassen
                                    zitatAbtrennen={kompakt}
                                    zoombar={kompakt}
                                    html={msg.body}
                                    blockRemote={!showRemote}
                                    onRemotePresence={(present) => { if (present) setHasRemote(true); }}
                                    onMarkierung={beiMarkierung}
                                    onBild={(b) => beiBild(b, resolvedId)}
                                                    onBilderLaden={(n) => meldeBilderLaden(`thread-${i}`, n)}
                                />
                            </div>
                        ))
                    ) : !full && !bodyError && !view.body ? (
                        /* SP4 — Body lädt noch: SOFORT die Vorschau als Text zeigen
                           (kein leeres Fenster; der Spinner-Hinweis oben erscheint
                           erst nach 400 ms). Sobald `full` da ist, ersetzt der echte
                           Body diese Vorschau. */
                        <div className={styles.previewLoading}>{email.preview}</div>
                    ) : (
                        <SandboxedMailBody
                            className={styles.bodyText}
                            /* Handy/Tablet/schmales Panel: breite Mails umbrechen bzw.
                               verkleinern statt rechts abschneiden. */
                            anBreiteAnpassen
                            zitatAbtrennen={kompakt}
                            zoombar={kompakt}
                            html={view.body}
                            blockRemote={!showRemote}
                            onRemotePresence={(present) => { if (present) setHasRemote(true); }}
                            onMarkierung={beiMarkierung}
                            onBild={(b) => beiBild(b, resolvedId)}
                                                    onBilderLaden={(n) => meldeBilderLaden("haupt", n)}
                        />
                    )}

                </div>
            </div>
            </div>

            {/* Handy: Aktionen für GENAU diese Nachricht der Unterhaltung. */}
            {kompakt && nachrichtMenue && (
                <div className="cal-rd">
                    <BottomSheet
                        open
                        title={nachrichtMenue.senderName || "Diese Nachricht"}
                        onClose={() => setNachrichtMenue(null)}
                    >
                        {(
                            [
                                ["Antworten", <ArrowReply20Regular key="i" />, () => handleReply(nachrichtMenue)],
                                ["Allen antworten", <ArrowReplyAll20Regular key="i" />, () => handleReplyAll(nachrichtMenue)],
                                ["Weiterleiten", <ArrowForward20Regular key="i" />, () => handleForward(nachrichtMenue)],
                                [
                                    "Als ungelesen markieren",
                                    <MailUnread20Regular key="i" />,
                                    () =>
                                        void graphMailService.markAsUnread(nachrichtMenue.id, mailbox).catch((err: unknown) =>
                                            reportMailError({
                                                area: "mail",
                                                action: "mark-unread",
                                                error: err,
                                                fallbackMessage: "Der Lesestatus konnte nicht geändert werden.",
                                            }),
                                        ),
                                ],
                                ["Löschen", <Delete20Regular key="i" />, () => void loescheNachricht(nachrichtMenue)],
                                ["Unterhaltung drucken", <Print20Regular key="i" />, handleDrucken],
                            ] as const
                        ).map(([label, icon, aktion]) => (
                            <button
                                key={label}
                                type="button"
                                className="mm-sheet-zeile"
                                onClick={() => {
                                    setNachrichtMenue(null);
                                    aktion();
                                }}
                            >
                                {icon}
                                <span className="mm-label">{label}</span>
                            </button>
                        ))}
                    </BottomSheet>
                </div>
            )}

            {/* J1 — die Leiste gehört zur geöffneten Mail, nicht zum Lesepanel:
                sie erscheint im Panel UND im Detailfenster, egal woher die Mail
                geöffnet wurde. */}
            {sidebar && (
                <MailContactSidebar
                    ohneLeiste={leisteVerborgen}
                    /* J2 — nicht mehr die Adress-Annotation der Liste, sondern
                       die aufgelöste Zuweisung dieser Mail. */
                    contact={zuweisungJ2.kontakt}
                    contactQuelle={zuweisungJ2.quelle}
                    readableMailboxes={readableMailboxes}
                    mailAddresses={displayAddresses}
                    mailbox={mailbox}
                    email={view}
                    aktiverReiter={aktiverReiter}
                    onReiterWechsel={onReiterWechsel}
                    onOpenMail={onOpenMailFromSidebar}
                    onNavBack={onNavBack}
                    /* A11: Ein Klick auf einen Vorgang navigiert die Seite
                       DARUNTER — im Fenster-Modus wandert das Mail-Fenster
                       deshalb in die Taskbar statt den Vorgang zu verdecken.
                       Ein Klick holt es zurück. */
                    onNavigiertZuSeite={windowId ? () => minimizeWindow(windowId) : undefined}
                    /* Damit der Vollflächen-Kalender aus dem Reiter „Termine"
                       nicht hinter diesem Fenster verschwindet. */
                    fensterId={windowId ?? null}
                    /* J6 — die Anhänge kommen aus DIESEM State: der Reiter zeigt
                       dieselben Dateien wie der Block unter dem Mailtext, ohne
                       einen zweiten Graph-Aufruf und mit genau EINEM Schreibweg
                       für „Beim Kontakt speichern". */
                    anhaenge={attachments.map((a) => ({
                        messageId: a.messageId,
                        id: a.id,
                        name: a.name,
                        size: a.size,
                    }))}
                    gespeicherteAnhangKeys={savedAttKeys}
                    onOpenAnhang={(a) =>
                        handleOpenAttachment(attachments.find((x) => x.id === a.id) ?? (a as any))
                    }
                    onDownloadAnhang={(a) =>
                        void handleDownloadAttachment(attachments.find((x) => x.id === a.id) ?? (a as any))
                    }
                    onSaveAnhang={(a) => {
                        const att = attachments.find((x) => x.id === a.id);
                        if (att) void handleSaveAttachment(att);
                    }}
                />
            )}
        </div>
    );
};
